<template>
  <div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-12">
          <div class="d-flex flex-wrap flex-wrap align-items-center justify-content-center">
            <div class="text-center">
              <h4 class="mb-3">상품 경쟁력 분석</h4>
              <p class="mb-0">상품 링크를 입력하여 해당 상품의 트래픽 키워드를 수집하고,<br>
                경쟁 상품들과의 가격 경쟁력을 분석합니다.
              </p>
            </div>
          </div>
        </div>

        <div class="col-lg-12">
          <!-- 메인 버튼들 -->
          <div class="d-flex justify-content-center align-items-center mb-4" style="gap: 15px;">
            <!-- 상품그룹 선택 -->
            <b-dropdown
              id="product-group-dropdown"
              :text="'상품그룹: ' + activeProductGroup"
              class="mr-2"
              variant="outline-primary"
              :disabled="isAnalyzing"
            >
              <b-dropdown-item
                v-for="group in productGroups"
                :key="group"
                :active="group === activeProductGroup"
                @click="switchProductGroup(group)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>{{ group }}</div>
                  <b-badge variant="secondary" v-if="group !== 'Default'" @click.stop="deleteProductGroup(group)">Del</b-badge>
                </div>
              </b-dropdown-item>
              <b-dropdown-divider v-if="productGroups.length > 0"></b-dropdown-divider>
              <b-dropdown-item @click="createProductGroup">새 상품그룹</b-dropdown-item>
            </b-dropdown>

            <!-- 상품설정 버튼 -->
            <button type="button" class="btn btn-info" @click="openProductModal" :disabled="isAnalyzing">
              상품설정 ({{ productList.length }}개)
            </button>

            <!-- 초기화 버튼 -->
            <button
              type="button"
              class="btn btn-secondary"
              @click="resetData"
              :disabled="isAnalyzing"
            >
              초기화
            </button>

            <!-- 비교 범위 설정 -->
            <div class="d-flex align-items-center">
              <label class="mr-2 mb-0"> 근접 범위 설정 :</label>
              <input
                type="number"
                v-model="compareRange"
                class="form-control mr-2"
                style="width: 80px;"
                min="1"
                max="20"
                :disabled="isAnalyzing"
              />
              <small class="text-muted"> 앞뒤로 몇 개 상품까지 비교할지 설정</small>
            </div>


          </div>
        </div>
      </div>



      <!-- 진행 상태 -->
      <div class="row" v-if="isAnalyzing">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-1">{{ progressMessage }}</h6>
                  <div class="progress" style="height: 6px;">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="`width: ${progressPercent}%`"
                    ></div>
                  </div>
                </div>
                <div class="text-end">
                  <span class="badge bg-primary">{{ progressPercent.toFixed(0) }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 가격 조정 제안 섹션 (독립적으로 표시) -->
      <div v-if="competitivenessResults.length > 0" class="row mb-4">
        <div class="col-lg-12">
          <div class="card border-info">
            <div class="card-header bg-light">
              <h4 class="card-title mb-0">💡 가격 경쟁력 개선 제안</h4>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <label class="form-label">목표 경쟁력 순위 (%)</label>
                  <div class="input-group">
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="targetCompetitivenessPercent"
                      min="1"
                      max="100"
                      placeholder="30"
                      @input="onTargetPercentChange"
                    >
                    <span class="input-group-text">%</span>
                  </div>
                  <small class="text-muted">30% = 100개 상품 중 30순위의 가격 경쟁력</small>
                  <!-- 목표 백분위 위치 Bar 표시 -->
                  <div class="mt-3">
                    <div class="mb-2 small text-muted">목표 경쟁력 순위를 변경하면 아래 바에서 위치가 즉시 표시됩니다.</div>

                    <!-- (좌측) 바 영역 제거 – 우측으로 이동 -->
                  </div>

                </div>
                <div class="col-md-6">
                  <!-- 가격 제안 카드 제거 → 오른쪽에 바 2개 배치 -->
                  <div class="mt-2">
                    <!-- 전체범위 바 (오른쪽) -->
                    <div class="mb-3 pos-bar-block">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <div class="fw-semibold">전체범위</div>
                        <div class="small text-muted" v-if="barFull.n > 0">상품수: {{ barFull.n }} · 구간: {{ formatPrice(barFull.min) }}~{{ formatPrice(barFull.max) }}</div>
                        <div class="small text-muted" v-else>데이터 없음</div>
                      </div>
                      <div class="pos-bar" :class="{ 'pos-bar--empty': barFull.n === 0 }">
                        <div class="pos-bar__track"></div>
                        <div class="pos-bar__mark pos-bar__mark--tgt" :style="{ left: barFull.tgtPos + '%' }" v-if="barFull.n > 0" title="목표 위치"></div>
                        <div class="pos-bar__mark pos-bar__mark--my"  :style="{ left: barFull.myPos + '%' }" v-if="barFull.n > 0 && barFull.myPos !== null" title="현재가"></div>
                        <div class="pos-bar__mark pos-bar__mark--sug" :style="{ left: barFull.sugPos + '%' }" v-if="barFull.n > 0 && barFull.sugPos !== null" title="제안가" @mousedown.prevent="startDragSuggest('full', $event)"></div>
                        <div class="pos-bar__label pos-bar__label--my"  :style="{ left: barFull.myPos + '%' }" v-if="barFull.n > 0 && barFull.myPos !== null">판매가 {{ formatPrice(integratedPriceSuggestion.currentPrice) }}원 ({{ getPricePercentileInRange('fullRange', integratedPriceSuggestion.currentPrice) || '-' }}%)</div>
                        <div class="pos-bar__label pos-bar__label--sug" :style="{ left: barFull.sugPos + '%' }" v-if="barFull.n > 0 && barFull.sugPos !== null">제안가 {{ formatPrice((manualSuggestion.full != null ? manualSuggestion.full : integratedPriceSuggestion.fullRangePrice) || 0) }}원 ({{ getPricePercentileInRange('fullRange', (manualSuggestion.full != null ? manualSuggestion.full : integratedPriceSuggestion.fullRangePrice) || 0) || '-' }}%)</div>
                      </div>
                    </div>

                    <!-- 근접범위 바 (오른쪽) -->
                    <div class="mt-5 mb-2 pos-bar-block">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <div class="fw-semibold">근접범위</div>
                        <div class="small text-muted" v-if="barNear.n > 0">상품수: {{ barNear.n }} · 구간: {{ formatPrice(barNear.min) }}~{{ formatPrice(barNear.max) }}</div>
                        <div class="small text-muted" v-else>데이터 없음</div>
                      </div>
                      <div class="pos-bar" :class="{ 'pos-bar--empty': barNear.n === 0 }">
                        <div class="pos-bar__track"></div>
                        <div class="pos-bar__mark pos-bar__mark--tgt" :style="{ left: barNear.tgtPos + '%' }" v-if="barNear.n > 0" title="목표 위치"></div>
                        <div class="pos-bar__mark pos-bar__mark--my"  :style="{ left: barNear.myPos + '%' }" v-if="barNear.n > 0 && barNear.myPos !== null" title="현재가"></div>
                        <div class="pos-bar__mark pos-bar__mark--sug" :style="{ left: barNear.sugPos + '%' }" v-if="barNear.n > 0 && barNear.sugPos !== null" title="제안가" @mousedown.prevent="startDragSuggest('near', $event)"></div>
                        <div class="pos-bar__label pos-bar__label--my"  :style="{ left: barNear.myPos + '%' }" v-if="barNear.n > 0 && barNear.myPos !== null">판매가 {{ formatPrice(integratedPriceSuggestion.currentPrice) }}원 ({{ getPricePercentileInRange('nearbyRange', integratedPriceSuggestion.currentPrice) || '-' }}%)</div>
                        <div class="pos-bar__label pos-bar__label--sug" :style="{ left: barNear.sugPos + '%' }" v-if="barNear.n > 0 && barNear.sugPos !== null">제안가 {{ formatPrice((manualSuggestion.near != null ? manualSuggestion.near : integratedPriceSuggestion.nearbyRangePrice) || 0) }}원 ({{ getPricePercentileInRange('nearbyRange', (manualSuggestion.near != null ? manualSuggestion.near : integratedPriceSuggestion.nearbyRangePrice) || 0) || '-' }}%)</div>
                      </div>
                    </div>

                    <div class="small text-muted mt-2">표시: 파랑(현재가), 주황(제안가), 초록(목표)</div>
                  </div>
                </div>
              </div>

              <!-- 🔧 키워드별 가격 제안 테이블 제거 (통합 가격 제안으로 대체) -->
            </div>
          </div>
        </div>
      </div>

      <!-- 키워드별 가격 경쟁력 분석 결과 -->
      <div v-if="competitivenessResults && competitivenessResults.length > 0" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">💰 키워드별 가격 경쟁력 분석</h4>
              <p class="text-muted mb-0">노출수 기준 내림차순 정렬</p>
            </div>
            <div class="card-body">
              <!-- 가격 경쟁력 분석 테이블 -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="table-light">
                    <tr>
                      <th>키워드</th>
                      <th>검색순위</th>
                      <th>내 상품 가격</th>
                      <th>전체범위 가격순위</th>
                      <th>근접범위 가격순위</th>
                      <th>전체범위 경쟁력</th>
                      <th>근접범위 경쟁력</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(result, index) in sortedCompetitivenessResults" :key="`result-${index}-${result.keyword}`"
                        @click="openKeywordDetailModal(result)"
                        style="cursor: pointer;"
                        class="keyword-row">
                      <td>
                        <span class="fw-bold text-primary">{{ result.keyword }}</span>
                      </td>
                      <td>
                        <span v-if="result.searchRank" class="badge bg-info">{{ result.searchRank }}위</span>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                      <td>
                        <span v-if="result.price" class="fw-bold">{{ formatPrice(result.price) }}원</span>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                      <td>
                        <div v-if="result.fullRange && result.fullRange.myPriceRank">
                          <span class="badge" :class="getPriceRankBadgeClass(result.fullRange.myPriceRank, result.fullRange.totalProducts)">
                            {{ result.fullRange.myPriceRank }}/{{ result.fullRange.totalProducts }}위
                          </span>
                          <small class="text-muted d-block">
                            평균: {{ formatPrice(result.fullRange.avgPrice) }}원 | 최저: {{ formatPrice(result.fullRange.minPrice) }}원 | 최고: {{ formatPrice(result.fullRange.maxPrice) }}원
                          </small>
                        </div>
                        <div v-else-if="result.fullRange">
                          <span class="text-muted">내 상품 없음</span>
                          <small class="text-muted d-block">
                            평균: {{ formatPrice(result.fullRange.avgPrice) }}원 ({{ result.fullRange.totalProducts }}개 상품)
                          </small>
                        </div>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                      <td>
                        <div v-if="result.nearbyRange && result.nearbyRange.myPriceRank">
                          <span class="badge" :class="getPriceRankBadgeClass(result.nearbyRange.myPriceRank, result.nearbyRange.totalProducts)">
                            {{ result.nearbyRange.myPriceRank }}/{{ result.nearbyRange.totalProducts }}위
                          </span>
                          <small class="text-muted d-block">
                            평균: {{ formatPrice(result.nearbyRange.avgPrice) }}원 | 최저: {{ formatPrice(result.nearbyRange.minPrice) }}원 | 최고: {{ formatPrice(result.nearbyRange.maxPrice) }}원
                          </small>
                        </div>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                      <td>
                        <div v-if="result.fullRange && result.fullRange.myPriceRank && result.fullRange.totalProducts" class="d-flex align-items-center">
                          <div class="progress mr-2" style="width: 60px; height: 8px;">
                            <div class="progress-bar"
                                 :style="{
                                   width: getPricePercent(result.fullRange) + '%',
                                   ...getPercentBarStyle(getPricePercent(result.fullRange))
                                 }"></div>
                          </div>
                          <span class="fw-bold" :style="getPercentTextStyle(getPricePercent(result.fullRange))">
                            {{ getPricePercent(result.fullRange) }}%
                          </span>
                        </div>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                      <td>
                        <div v-if="result.nearbyRange && result.nearbyRange.myPriceRank && result.nearbyRange.totalProducts" class="d-flex align-items-center">
                          <div class="progress mr-2" style="width: 60px; height: 8px;">
                            <div class="progress-bar"
                                 :style="{
                                   width: getPricePercent(result.nearbyRange) + '%',
                                   ...getPercentBarStyle(getPricePercent(result.nearbyRange))
                                 }"></div>
                          </div>
                          <span class="fw-bold" :style="getPercentTextStyle(getPricePercent(result.nearbyRange))">
                            {{ getPricePercent(result.nearbyRange) }}%
                          </span>
                        </div>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>


            </div>
          </div>
        </div>
      </div>


      <!-- 키워드그룹 변경 중 로딩 메시지 -->
      <div v-if="isGroupSwitching" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body text-center py-5">
              <div class="spinner-border text-primary mb-3" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <h5 class="text-muted">키워드그룹 변경 중...</h5>
              <p class="text-muted small">데이터를 준비하고 있습니다. 잠시만 기다려주세요.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 데이터가 없을 때 안내 메시지 (shownomadv3가 true일 때만) -->
      <div v-if="analysisResults.length === 0 && !isGroupSwitching && !isAnalyzing && shownomadv3" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body text-center py-5">
              <i class="fas fa-search fa-3x text-muted mb-3"></i>
              <h5 class="text-muted">분석 데이터가 없습니다</h5>
              <p class="text-muted">키워드를 추가하고 "전체 수집 시작" 버튼을 클릭하여 데이터를 수집해주세요.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 상품설정 모달 -->
    <div class="modal fade" :class="{ show: showProductModal }" :style="{ display: showProductModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-xl" style="max-height: 90vh;">
        <div class="modal-content" style="max-height: 90vh; overflow-y: auto;">
          <div class="modal-header">
            <h5 class="modal-title">상품설정 - {{ activeProductGroup }}</h5>
            <button type="button" class="btn-close" @click="showProductModal = false"></button>
          </div>
          <div class="modal-body">
            <!-- 상품링크 입력 -->
            <div class="row mb-0">
              <div class="col-md-12">
                <div class="card">
                  <div class="card-body py-3">
                    <div class="row d-flex justify-content-between align-items-center">
                      <div class="d-flex justify-content-start align-items-center ml-2 w-100">
                        <div class="d-flex align-items-center w-75">
                          <label class="form-label small mb-1 w-25 ml-5">상품링크</label>
                          <input
                            ref="modalProductLinkInput"
                            type="text"
                            class="form-control w-75"
                            placeholder="쿠팡 상품링크 입력 후 Enter"
                            v-model="targetProductLink"
                            @keyup.enter="collectProductTrafficKeywords"
                            :disabled="isAnalyzing"
                          >
                        </div>
                        <div class="d-flex align-items-center w-25">
                          <button
                            type="button"
                            class="btn btn-primary btn-sm ml-2"
                            @click="collectProductTrafficKeywords"
                            :disabled="isAnalyzing || !targetProductLink"
                          >
                            키워드 수집
                          </button>
                        </div>
                      </div>
                      <!-- <div class="col-md-2">
                        <label class="form-label small mb-1">지연시간</label>
                        <div class="d-flex align-items-center">
                          <input
                            type="number"
                            class="form-control text-center"
                            v-model="delayTime"
                            :disabled="isAnalyzing"
                            min="100"
                            max="3000"
                            style="width: 70px;"
                          >
                          <span class="text-muted small ml-1">ms</span>
                        </div>
                      </div> -->
                      <!-- <div class="col-md-2">
                        <label class="form-label small mb-1">최대수집</label>
                        <div class="d-flex align-items-center">
                          <input
                            type="number"
                            class="form-control text-center"
                            v-model="maxProducts"
                            :disabled="isAnalyzing"
                            min="100"
                            max="2000"
                            step="100"
                            style="width: 70px;"
                          >
                          <span class="text-muted small ml-1">개</span>
                        </div>
                      </div> -->
                      <!-- <div class="col-md-2">
                        <div class="d-flex flex-column"> -->
                          <!-- <button type="button"
                                  class="btn"
                                  :class="showDataLabSection ? 'btn-primary' : 'btn-outline-primary'"
                                  @click="toggleDataLabSection">
                            <i class="fas fa-chart-line"></i>네이버데이터랩
                          </button> -->
                          <!-- <button type="button"
                                  class="btn"
                                  :class="showExcelUploadSection ? 'btn-primary' : 'btn-outline-primary'"
                                  @click="toggleExcelUploadSection">
                            <i class="fas fa-file-upload"></i>키워드 업로드
                          </button> -->
                        <!-- </div>
                      </div> -->
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 네이버데이터랩 섹션 -->
            <div v-if="showDataLabSection" class="row" style="z-index: 0;">
              <div class="col-md-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">네이버 데이터랩 키워드 수집</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-12 d-flex align-items-center justify-content-between">
                        <div style="font-size:85%">
                          <b>[수집할 키워드 순위설정]</b><br>
                          새탭에 네이버 데이터랩 쇼핑인사이트 Top500키워드가 나오도록 먼저 세팅해주세요
                        </div>
                        <div class="d-flex align-items-center justify-content-center">
                          <div class="d-flex align-items-center mr-2">
                            <label class="form-label mr-2">시작순위:</label>
                            <input type="number" :min="1" :max="500" class="form-control text-center"
                                   v-model="startRank" style="width: 80px;">
                          </div>
                          <div class="d-flex align-items-center">
                            <label class="form-label mr-2">~ 종료순위:</label>
                            <input type="number" :min="1" :max="500" class="form-control text-center"
                                   v-model="endRank" style="width: 80px;">
                          </div>
                          <button type="button" class="btn btn-success ml-3" @click="collectKeywordRank">
                            수집하기
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 키워드 업로드 섹션 -->
            <div v-if="showExcelUploadSection" class="row" style="z-index: 0;">
              <div class="col-md-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">키워드 업로드</h6>
                  </div>
                  <div class="card-body d-flex justify-content-between">
                    <div style="font-size:85%">
                      <b>[업로드 방법]</b><br>
                        1. 엑셀업로드 포맷 다운로드<br>
                        2. 엑셀 파일에서 키워드 부분(맨윗줄 제외) 업데이트<br>
                        3. 업데이트된 엑셀 파일 업로드 순으로 진행해 주세요.
                    </div>
                    <div class="d-flex justify-content-end align-items-center">
                      <div class="d-flex flex-column mr-4">
                        <label class="form-label mr-4">1. 엑셀업로드 포맷</label>
                        <div>
                          <button type="button" class="btn btn-primary" @click="downloadTemplate">Download</button>
                        </div>
                      </div>
                      <div class="d-flex flex-column mr-4">
                        <label class="form-label">2. 업데이트된 키워드 파일 업로드</label>
                        <div class="custom-file">
                          <input ref="bulkUpload" type="file" class="custom-file-input" @change="uploadBulkFile" accept=".xlsx,.xls" id="inputGroupFile02">
                          <label class="custom-file-label" for="inputGroupFile02">{{ uploadstatus }}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 키워드 목록 테이블 -->
            <div class="row" style="z-index:999">
              <div class="col-md-12">
                  <div v-if="productList.length === 0" class="text-center py-4 text-muted">
                    <i class="fas fa-list fa-3x mb-3 opacity-50"></i>
                    <p>등록된 키워드가 없습니다.<br>위에서 키워드를 추가해주세요.</p>
                  </div>
                  <CDataTable
                      storage-key="productCompetitiveAnalysis.1"
                    v-else
                    :key="productList.length"
                    :headers="productHeaders"
                    :items="productListWithIndex"
                    :itemsPerPage="10"
                    :search="true"
                    :pagination="true"
                    :itemKey="'index'"
                    class="table-responsive"
                  >
                    <template v-slot:index="{ item }">
                      <span>{{ item.index + 1 }}</span>
                    </template>

                    <template v-slot:keyword="{ item }">
                      <span class="fw-bold">{{ item.keyword }}</span>
                    </template>



                    <template v-slot:actions="{ item }">
                      <button class="btn btn-outline-secondary btn-sm"
                              @click="deleteProductFromList(item.index)"
                              title="키워드 삭제">
                        x
                      </button>
                    </template>
                  </CDataTable>
                </div>
            </div>


          </div>
          <div class="modal-footer bg-light">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="text-muted small">
                총 {{ productList.length }}개 키워드
              </div>
              <div>
                <button type="button" class="btn btn-secondary mr-2" @click="showProductModal = false">
                  <i class="fas fa-times mr-1"></i>닫기
                </button>
                <button type="button" class="btn btn-primary" @click="startCompetitiveAnalysis" :disabled="isAnalyzing || productList.length === 0">
                  <i class="fas fa-chart-line mr-1"></i>수집
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showProductModal }" v-if="showProductModal"></div>

    <!-- 키워드 상세 모달 -->
    <div class="modal fade" :class="{ show: showKeywordDetailModal }" :style="{ display: showKeywordDetailModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-xl" style="max-height: 90vh;">
        <div class="modal-content" style="max-height: 90vh; overflow-y: auto;">
          <div class="modal-header">
            <h5 class="modal-title">키워드 상세 분석 - {{ selectedKeywordResult?.keyword }}</h5>
            <button type="button" class="btn-close" @click="showKeywordDetailModal = false"></button>
          </div>
          <div class="modal-body">
            <!-- 근접 범위 상품 목록 -->
            <div v-if="keywordDetailProducts.length > 0">
              <h6 class="mb-3">근접 범위 상품 목록 ({{ keywordDetailProducts.length }}개)</h6>
              <CDataTable
                  storage-key="productCompetitiveAnalysis.2"
                :headers="keywordDetailHeaders"
                :items="keywordDetailProducts"
                :itemsPerPage="10"
                :search="true"
                :pagination="true"
                :itemKey="'vendorItemId'"
                class="table-responsive"
              >
                <template v-slot:image="{ item }">
                  <img :src="item.imageUrl" :alt="item.productName" style="width: 60px; height: 60px; object-fit: cover;" class="rounded">
                </template>

                <template v-slot:productName="{ item }">
                  <div>
                    <div class="fw-bold" style="font-size: 14px; line-height: 1.3;">{{ item.productName }}</div>
                    <small class="text-muted">{{ item.vendorItemId }}</small>
                  </div>
                </template>

                <template v-slot:price="{ item }">
                  <span class="fw-bold" :class="{ 'text-primary': item.isMyProduct }">
                    {{ formatPrice(item.price) }}원
                    <i v-if="item.isMyProduct" class="fas fa-star text-warning ml-1" title="내 상품"></i>
                  </span>
                </template>

                <template v-slot:reviewCount="{ item }">
                  <span>{{ item.reviewCount ? formatNumber(item.reviewCount) : '-' }}</span>
                </template>

                <template v-slot:reviewRating="{ item }">
                  <div v-if="item.reviewRating" class="d-flex align-items-center">
                    <span class="fw-bold mr-1">{{ item.reviewRating }}</span>
                    <div class="text-warning">
                      <i v-for="n in 5" :key="n" class="fas fa-star" :class="{ 'text-muted': n > Math.floor(item.reviewRating) }"></i>
                    </div>
                  </div>
                  <span v-else>-</span>
                </template>
              </CDataTable>
            </div>
            <div v-else class="text-center py-4 text-muted">
              <i class="fas fa-search fa-3x mb-3 opacity-50"></i>
              <p>근접 범위 상품 데이터가 없습니다.</p>
            </div>

            <!-- 키워드 기본 정보 (테이블 아래로 이동) -->
            <div v-if="selectedKeywordResult" class="mt-4">
              <div class="row">
                <div class="col-md-3">
                  <div class="card">
                    <div class="card-body text-center">
                      <h6 class="card-title">검색순위</h6>
                      <h4 class="text-primary">{{ selectedKeywordResult.searchRank || 'N/A' }}위</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card">
                    <div class="card-body text-center">
                      <h6 class="card-title">내 상품 가격</h6>
                      <h4 class="text-success">{{ selectedKeywordResult.price ? formatPrice(selectedKeywordResult.price) + '원' : 'N/A' }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card">
                    <div class="card-body text-center">
                      <h6 class="card-title">근접범위 순위</h6>
                      <h4 class="text-info">{{ selectedKeywordResult.nearbyRange?.myPriceRank || 'N/A' }}/{{ selectedKeywordResult.nearbyRange?.totalProducts || 'N/A' }}위</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card">
                    <div class="card-body text-center">
                      <h6 class="card-title">근접범위 경쟁력</h6>
                      <h4 class="text-warning">{{ selectedKeywordResult.nearbyRange ? getPricePercent(selectedKeywordResult.nearbyRange) + '%' : 'N/A' }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showKeywordDetailModal = false">
              <i class="fas fa-times mr-1"></i>닫기
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showKeywordDetailModal }" v-if="showKeywordDetailModal"></div>
  </div>
</template>

<style scoped>
.keyword-row:hover {
  background-color: #f8f9fa !important;
}

.keyword-row {
  transition: background-color 0.2s ease;
}
</style>

<script>
import { scopedDbName } from '@/services/scopedDB'
import CDataTable from '@/components/common/CDataTable.vue'
// import ApexChart from '@/components/charts/ApexChart.vue'

// import * as XLSX from 'xlsx';

export default {
  name: 'ProductCompetitiveAnalysis',
  components: {
    CDataTable,
    // ApexChart
  },
  data() {
    return {
      targetProductLink: '', // 상품링크 입력 필드
      myVendorItemId: null, // 내 상품의 vendorItemId
      defaultStartPage: 1, // 새로 추가되는 키워드의 기본 시작 페이지
      defaultEndPage: 1, // 새로 추가되는 키워드의 기본 끝 페이지
      isAnalyzing: false,
      progressPrefix: '', // 진행 상황 앞부분 (키워드/페이지 정보)
      progressSuffix: '', // 진행 상황 뒷부분 (Extension 메시지)
      progressPercent: 0,
      analysisResults: [],
      currentKeywordIndex: 0, // 현재 분석 중인 키워드 인덱스

      // 모든 계산이 완료된 데이터 (성능 최적화)
      enrichedAnalysisResults: [],   // 모든 계산값이 포함된 데이터

      // 미리 계산된 필터링된 데이터들 (각 분석에서 재사용)
      validProductsWithOutliers: [],    // 이상치포함 유효 상품들
      validProductsWithoutOutliers: [], // 이상치제외 유효 상품들
      top300ByPriceCvr: [],            // 판매가×CVR Top300 (이상치포함)
      top20ByImpressions: [],          // 총노출수 Top20 (이상치포함)
      top20ByRPI: [],                  // RPI Top20 (이상치제외)

      // 통합 키워드 분석 데이터
      summaryData: [],
      summaryHeaders: [
        { text: '지표', value: 'metric', sortable: false, width: '200px' },
        { text: '값', value: 'value', sortable: false, width: '150px' }
      ],

      // 상품그룹 관리
      productGroups: ['Default'],
      activeProductGroup: 'Default',
      showProductGroup: true,

      // 키워드 분석 기능용
      autoList: [], // 키워드 분석에서 사용
      originalAutoList: null, // 신규 수집 시 원본 백업용

      // 상품설정 (상품 경쟁력 분석 기능용)
      productList: [], // 상품 경쟁력 분석에서 사용
      originalProductList: null, // 신규 수집 시 원본 백업용

      // 수동 제안가(Bar 드래그)
      manualSuggestion: { full: null, near: null },
      dragging: { active: false, type: null, rect: null, prices: [], min: 0, max: 0 },

      currentProductInfo: null, // 현재 수집된 상품 정보 (원본 데이터 보관)
      delayTime: 300,

      // 경쟁력 분석 결과
      competitivenessResults: [], // 키워드별 경쟁력 분석 결과
      keywordSearchResults: {}, // 키워드별 검색 결과 저장

      // 가격 조정 제안
      targetCompetitivenessPercent: 30, // 기본값 30%
      priceSuggestions: [],

      // shownomadv3 권한 관리
      shownomadv3: false, // 기본값 false
      uploadstatus: 'Excel 파일 업로드',
      showProductModal: false, // showBulkModal 대신 showProductModal 사용

      // 비교 범위 설정
      compareRange: 5, // 기본값 5

      // 수집 제한 설정
      maxProducts: 3000, // 최대 수집 상품 수 제한



      // IndexedDB 관련
      currentGroupId: 'default', // 현재 키워드 그룹 ID
      existingVendorItemIds: [], // 중복 방지용 기존 vendorItemId 목록
      isLoadingFromDB: false, // DB에서 데이터 로딩 중 여부

      // 작업 큐 방식
      workQueue: [], // 작업 큐 [{keyword, keywordIndex, pageIndex, totalPages}]
      isProcessingQueue: false, // 큐 처리 중 여부

      // 쿠팡 API 차단 감지 (Preflight + 연속 실패 카운터)
      hasReceivedAnyCollectionSuccess: false, // 시작 이후 단 1건이라도 수집 성공했는지
      consecutivePage1Failures: 0, // 연속 page 1 throw 카운터 (성공 시 리셋)



      // 키워드설정 모달 섹션 표시 상태
      showDataLabSection: false,
      showExcelUploadSection: false, // 기본적으로 숨김

      // 연관+자동완성 키워드 수집 (개별 키워드용)
      relCycle: 50,

      // 네이버 데이터랩 키워드 수집
      startRank: 1,
      endRank: 50,

      // 상품설정 테이블 헤더 (키워드 분석용)
      bulkHeaders: [
        { text: '순번', value: 'index', align: 'center', width: 80, isSlot: true },
        { text: '키워드', value: 'keyword', align: 'center', width: 400, isSlot: true },
        { text: '삭제', value: 'actions', align: 'center', width: 80, isSlot: true }
      ],
      // 상품설정 테이블 헤더 (상품 경쟁력 분석용)
      productHeaders: [
        { text: '순번', value: 'index', align: 'center', width: 80, isSlot: true },
        { text: '키워드', value: 'keyword', align: 'center', width: 250, isSlot: true },
        { text: '노출수', value: 'impressions', align: 'center', width: 120 },
        { text: '클릭수', value: 'clicks', align: 'center', width: 120 },
        { text: 'CTR', value: 'ctr', align: 'center', width: 100 },
        { text: '삭제', value: 'actions', align: 'center', width: 80, isSlot: true }
      ],
      tableHeaders: [
        { text: '순위', value: 'rank', sortable: true, width: '80px' },
        { text: '상품명', value: 'productName', sortable: true, width: '250px' },
        { text: '판매가', value: 'price', sortable: true, width: '100px' },
        { text: 'CTR', value: 'ctr', sortable: true, width: '80px' },
        { text: 'CVR', value: 'cvr', sortable: true, width: '80px' },
        { text: 'RPI', value: 'overall', sortable: true, width: '80px' },
        { text: '노출수', value: 'impressions', sortable: true, width: '100px' },
        { text: '매출', value: 'salesLast28d', sortable: true, width: '120px' },
        { text: '광고', value: 'ad', sortable: true, width: '60px' },
        { text: '트래픽 키워드', value: 'trafficKeyword', sortable: false, width: '200px' }
      ],
      // popup.js와 동일한 분석 데이터
      summaryMetrics: {
        medianCTR: '0.00',
        medianCVR: '0.00',
        totalProducts: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalSales: 0,
        totalAdImpressions: 0,
        totalOrganicImpressions: 0
      },

      bestPriceRange: '데이터 없음',
      bestPriceRanges: [],
      optimizedCategory: '데이터 없음',
      optimizedCategoryInfo: null,
      categoryAnalysis: [],
      bestProducts: [],
      integratedKeywords: [],

      // 기본 헤더 (상세보기 OFF)
      basicIntegratedKeywordHeaders: [
        { text: '키워드', value: 'keyword', sortable: true, width: '120px', isSlot: true },
        { text: '총 노출수', value: 'totalVolume', sortable: true, width: '100px', isSlot: true },
        { text: '자연노출수', value: 'organicImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '광고노출수', value: 'adImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '총 클릭수', value: 'totalClicks', sortable: true, width: '100px', isSlot: true },
        { text: '노출 상품수', value: 'productCount', sortable: true, width: '100px', isSlot: true },
        { text: '주요 카테고리', value: 'mainCategory', sortable: true, width: '120px', isSlot: true },
        { text: '카테고리적합도', value: 'categoryFitness', sortable: true, width: '100px', isSlot: true },
        { text: '총 판매량', value: 'totalSalesCount', sortable: true, width: '100px', isSlot: true },
        { text: '평균매출', value: 'avgSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '중간매출', value: 'medianSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '일매출\n가이드라인', value: 'revenueShareBasedRevenueRange', sortable: true, width: '140px', isSlot: true },
        { text: '가격대\n(25%~75%)', value: 'priceRange25to75', sortable: true, width: '120px', isSlot: true },
        { text: '접근성', value: 'accessibilityScore', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n평균RPI', value: 'topRankAvgRpi', sortable: true, width: '100px', isSlot: true },
        { text: '평균 RPI', value: 'avgRpi', sortable: true, width: '80px', isSlot: true },
        { text: '중간 RPI', value: 'medianRpi', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CTR', value: 'avgCtr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CTR', value: 'medianCtr', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CVR', value: 'avgCvr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CVR', value: 'medianCvr', sortable: true, width: '80px', isSlot: true }
      ],

      // 상세 헤더 (상세보기 ON) - 현재 전체 데이터
      detailIntegratedKeywordHeaders: [
        { text: '키워드', value: 'keyword', sortable: true, width: '120px', isSlot: true },
        { text: '총 노출수', value: 'totalVolume', sortable: true, width: '100px', isSlot: true },
        { text: '자연노출수', value: 'organicImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '광고노출수', value: 'adImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '총 클릭수', value: 'totalClicks', sortable: true, width: '100px', isSlot: true },
        { text: '노출 상품수', value: 'productCount', sortable: true, width: '100px', isSlot: true },
        { text: '주요 카테고리', value: 'mainCategory', sortable: true, width: '120px', isSlot: true },
        { text: '카테고리적합도', value: 'categoryFitness', sortable: true, width: '100px', isSlot: true },
        { text: '총 판매량', value: 'totalSalesCount', sortable: true, width: '100px', isSlot: true },
        { text: '총매출', value: 'totalGmv', sortable: true, width: '120px', isSlot: true },
        { text: '최대매출', value: 'maxSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '평균매출', value: 'avgSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '중간매출', value: 'medianSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '매출점유율', value: 'revenueShare', sortable: true, width: '100px', isSlot: true },
        { text: '일매출\n가이드라인', value: 'revenueShareBasedRevenueRange', sortable: true, width: '140px', isSlot: true },
        { text: '가격대\n(25%~75%)', value: 'priceRange25to75', sortable: true, width: '120px', isSlot: true },
        { text: '접근성', value: 'accessibilityScore', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n최대RPI', value: 'topRankMaxRpi', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n평균RPI', value: 'topRankAvgRpi', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n중간RPI', value: 'topRankMedianRpi', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n최소RPI', value: 'topRankMinRpi', sortable: true, width: '100px', isSlot: true },
        { text: '최대 RPI', value: 'maxRpi', sortable: true, width: '80px', isSlot: true },
        { text: '평균 RPI', value: 'avgRpi', sortable: true, width: '80px', isSlot: true },
        { text: '중간 RPI', value: 'medianRpi', sortable: true, width: '80px', isSlot: true },
        { text: '최소 RPI', value: 'minRpi', sortable: true, width: '80px', isSlot: true },

        { text: '최대 CTR', value: 'maxCtr', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CTR', value: 'avgCtr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CTR', value: 'medianCtr', sortable: true, width: '80px', isSlot: true },
        { text: '최소 CTR', value: 'minCtr', sortable: true, width: '80px', isSlot: true },
        { text: '최대 CVR', value: 'maxCvr', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CVR', value: 'avgCvr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CVR', value: 'medianCvr', sortable: true, width: '80px', isSlot: true },
        { text: '최소 CVR', value: 'minCvr', sortable: true, width: '80px', isSlot: true }
      ],

      // 현재 사용 중인 헤더 (초기값: 빈 배열, mounted에서 설정됨)
      integratedKeywordHeaders: [],

      bestCTRProduct: null,
      bestCVRProduct: null,
      bestPriceCvrProduct: null,
      bestAttentionProduct: null,
      bestRPIProduct: null,



      // 사용자 임의 제외/복원 상태
      userExcludedVendorItemIds: [],
      userExcludedList: [],


      // 사용자 제외/복원 처리 스피너 상태
      isUserOutlierProcessing: false,
      processingVendorItemId: null,
      processingAllUserExcluded: false,







      // 다국어 검색어 생성 상태/결과
      isGeneratingMultilingualKeywords: false,
      multilingualKeywords: [], // [{ ko: {keyword, rationale}, zh: {keyword, rationale}, en: {keyword, rationale} }]

      // 키워드 SEO 분석
      keywordSEOAnalysis: {
        productNameKeywords: [],
        tagKeywords: [],
        suggestions: []
      },

      // 키워드 복사 기능
      generatedProductName: '',
      editableTagKeywords: [],

      // 통합키워드 vs 단일키워드 분석 타입 (기본: 단일키워드)
      keywordAnalysisType: 'single', // 'integrated' 또는 'single'

      // 최적 카테고리만 표시 여부 (기본: true)
      showOptimalCategoryOnly: true,

      // 상세보기 여부 (기본: false)
      showDetailView: false,

      // 페이지별 점진적 수집을 위한 변수들
      currentKeywordPageIndex: 1,
      currentKeywordTotalPages: 1,
      currentKeywordResults: [],

      // 키워드 분석 헤더 툴팁 (헤더의 value 값을 키로 사용)
      keywordHeaderTooltips: {
        'keyword': '분석 대상 키워드입니다',
        'totalVolume': '해당 키워드로 노출된 전체 상품들의 총 노출수입니다',
        'organicImpressions': '자연 검색으로 노출된 횟수입니다 (전체 노출수의 70% 추정)',
        'adImpressions': '광고로 노출된 횟수입니다 (전체 노출수의 30% 추정)',
        'totalClicks': '해당 키워드로 발생한 전체 클릭수입니다',
        'productCount': '해당 키워드에 노출된 상품의 개수입니다',
        'mainCategory': '해당 키워드에 노출된 상품들 중 가장 많은 상품이 속한 카테고리입니다 (최적카테고리는 총매출 기준으로 선정)',
        'categoryFitness': '주요 카테고리에 속한 상품의 비율(%)입니다',
        'totalSalesCount': '해당 키워드에 노출된 모든 상품의 실제 총 판매량입니다',
        'totalGmv': '키워드별 클릭수 × CVR × 가격으로 계산된 키워드 기여 매출입니다',
        'maxSalesAmount': '해당 키워드 총 노출수를 상위 20개 상품의 노출수 비율로 분배하여 각 상품 RPI와 곱한 예상 매출의 최대값입니다',
        'avgSalesAmount': '해당 키워드 총 노출수를 상위 20개 상품의 노출수 비율로 분배하여 각 상품 RPI와 곱한 예상 매출의 평균입니다',
        'medianSalesAmount': '해당 키워드 총 노출수를 상위 20개 상품의 노출수 비율로 분배하여 각 상품 RPI와 곱한 예상 매출의 중간값입니다',
        'revenueShare': '해당 키워드에 노출된 상품들의 전체 매출 중 해당 키워드가 실제로 기여한 매출의 비율(%)입니다',
        'revenueShareBasedRevenueRange': '해당 키워드에 노출된 상품 중 매출 기준 상위 20개의 2위, 10위 실제 매출(판매량×가격) 기준 일매출 가이드라인 범위입니다 (실제 월매출 ÷ 30)',
        'priceRange25to75': '해당 키워드 노출수 기준 상위 20개 상품의 가격 중 25% ~ 75% 구간에 해당하는 가격대입니다',
        'accessibilityScore': '매출점유율기준 중간일매출 ÷ 상위랭킹 평균RPI로 계산된 키워드 진입 용이성 지표입니다 (높을수록 진입하기 쉬움)',
        'maxRpi': '해당 키워드에 노출된 상품들 중 최대 RPI입니다',
        'avgRpi': '키워드별 클릭수 가중평균 RPI입니다',
        'medianRpi': '해당 키워드에 노출된 상품들의 RPI 중간값입니다',
        'minRpi': '해당 키워드에 노출된 상품들 중 최소 RPI입니다',
        'topRankMaxRpi': '키워드 노출수 기준 상위 10개 상품 중 최대 RPI입니다',
        'topRankAvgRpi': '키워드 노출수 기준 상위 10개 상품의 평균 RPI입니다',
        'topRankMedianRpi': '키워드 노출수 기준 상위 10개 상품의 중간 RPI입니다',
        'topRankMinRpi': '키워드 노출수 기준 상위 10개 상품 중 최소 RPI입니다',
        'maxCtr': '해당 키워드에 노출된 상품들 중 최대 CTR입니다',
        'avgCtr': '총 클릭수 / 총 노출수 × 100으로 계산된 키워드 전체 CTR입니다',
        'medianCtr': '해당 키워드에 노출된 상품들의 CTR 중간값입니다',
        'minCtr': '해당 키워드에 노출된 상품들 중 최소 CTR입니다',
        'maxCvr': '해당 키워드에 노출된 상품들 중 최대 CVR입니다',
        'avgCvr': '해당 키워드에 노출된 상품들의 CVR 평균값입니다',
        'medianCvr': '해당 키워드에 노출된 상품들의 CVR 중간값입니다',
        'minCvr': '해당 키워드에 노출된 상품들 중 최소 CVR입니다'
      },

      // 가격별 CVR 분석
      priceInput: '',
      expectedMinCVR: '-',
      priceVsCvrData: [],

      // 광고경쟁력 분석
      marginRate: 30,
      minROAS: 333,
      sortByImpression: false,
      adCompetitivenessData: [],



      // 차트 표시 상태
      showImpressionChart: false,
      showPriceVsCvrChart: false,
      showRpiCompetitivenessChart: false,
      showAdCompetitivenessChart: false,
      showPriceRangeSalesChart: false,
      showDailyTrafficChart: false,

      // 차트 옵션들
      impressionChartOptions: {},
      priceVsCvrChartOptions: {},
      rpiCompetitivenessChartOptions: {},
      adCompetitivenessChartOptions: {},
      priceRangeSalesChartOptions: {},
      dailyTrafficChartOptions: {},

      // RPI Top20 데이터 (다운로드용)
      rpiTop20Products: [],

      // 메모리 사용량 모니터링
      memoryUsage: {
        totalProducts: 0,
        estimatedSize: 0,
        lastUpdated: null
      },

      // 디버깅용 추적 vendorItemId
      debugTargetVendorId: '92021995662',

      // 키워드그룹 변경 로딩 상태
      isGroupSwitching: false,

      // 키워드 상세 모달
      showKeywordDetailModal: false,
      selectedKeywordResult: null,
      keywordDetailHeaders: [
        { text: '순위', value: 'rank', align: 'center', width: 80 },
        { text: '이미지', value: 'image', align: 'center', width: 100, isSlot: true },
        { text: '상품명', value: 'productName', align: 'left', width: 300, isSlot: true },
        { text: '판매가', value: 'price', align: 'center', width: 120, isSlot: true },
        { text: '리뷰수', value: 'reviewCount', align: 'center', width: 100, isSlot: true },
        { text: '리뷰평점', value: 'reviewRating', align: 'center', width: 100, isSlot: true }
      ]
    }
  },
  async created() {
    // 상품그룹 초기화 (IndexedDB에서 기존 그룹들 로드)
    await this.initializeProductGroups()
  },
  async mounted() {
    console.log('🚀 [상품 경쟁력 분석 컴포넌트 마운트] 시작')

    // Extension 메시지 리스너 등록
    window.addEventListener('message', this.handleExtensionMessage)

    // 기존 데이터 로드
    await this.loadFromIndexedDB()

    // IndexedDB 초기화 및 기존 데이터 로딩
    // console.log('🚀 [컴포넌트 마운트] IndexedDB 초기화 시작...')
    await this.initializeDB()


    // 초기 헤더 설정
    this.updateHeaders()

    // console.log('✅ [컴포넌트 마운트] 완료 - 키워드:', this.autoList.length, '개, 상품:', this.analysisResults.length, '개')
    // console.log('✅ [컴포넌트 마운트] 헤더 설정 완료:', this.integratedKeywordHeaders.length, '개 헤더')
  },
  beforeDestroy() {
    // 메시지 리스너 제거
    window.removeEventListener('message', this.handleExtensionMessage)
  },
  watch: {
    targetCompetitivenessPercent(newVal, oldVal) {
      console.log('🔄 [목표 경쟁력 % 변경]', { old: oldVal, new: newVal })
      // 목표 % 변경 시 수동 제안가 초기화 → 백분위 제안가가 양쪽 바에서 즉시 반영되도록
      this.manualSuggestion.full = null
      this.manualSuggestion.near = null
    }
  },
  // watch: {
  //   showDetailView(newVal, oldVal) {
      // console.log('🔄 상세보기 스위치 변경:', {
      //   이전값: oldVal,
      //   새값: newVal,
      //   기본헤더수: this.basicIntegratedKeywordHeaders.length,
      //   상세헤더수: this.detailIntegratedKeywordHeaders.length
      // })
  //   }
  // },
  computed: {

    // 현재 분석 데이터가 있는지 확인
    hasAnalysisData() {
      return this.competitivenessResults.length > 0 // 🔧 analysisResults → competitivenessResults
    },

    // 활성화된 경쟁력 분석 결과만 필터링
    enabledCompetitivenessResults() {
      return this.competitivenessResults.filter(result => result.enabled !== false)
    },

    // 노출수 기준 내림차순 정렬된 경쟁력 분석 결과
    sortedCompetitivenessResults() {
      // 🔍 디버깅: 키워드 수 불일치 확인
      console.log('🔍 [키워드 수 비교]')
      console.log('  - productList 키워드 수:', this.productList.length)
      console.log('  - competitivenessResults 수:', this.competitivenessResults.length)
      console.log('  - productList 키워드들:', this.productList.map(p => p.keyword))
      console.log('  - competitivenessResults 키워드들:', this.competitivenessResults.map(r => r.keyword))

      // 누락된 키워드 찾기
      const productKeywords = this.productList.map(p => p.keyword)
      const resultKeywords = this.competitivenessResults.map(r => r.keyword)
      const missingKeywords = productKeywords.filter(k => !resultKeywords.includes(k))
      const extraKeywords = resultKeywords.filter(k => !productKeywords.includes(k))

      if (missingKeywords.length > 0) {
        console.log('⚠️ [누락된 키워드]:', missingKeywords)
      }
      if (extraKeywords.length > 0) {
        console.log('⚠️ [추가된 키워드]:', extraKeywords)
      }

      return [...this.competitivenessResults].sort((a, b) => {
        // 키워드에 해당하는 productList 항목 찾기
        const aKeyword = this.productList.find(p => p.keyword === a.keyword)
        const bKeyword = this.productList.find(p => p.keyword === b.keyword)

        const aImpressions = aKeyword?.impressions || 0
        const bImpressions = bKeyword?.impressions || 0

        return bImpressions - aImpressions // 내림차순
      })
    },

    // 목표 백분위 위치 표시용 Bar 데이터(전체범위)
    barFull() {
      try {
        const prices = this.collectPriceList('fullRange')
        const n = prices.length
        if (!n) return { n: 0, min: 0, max: 0, tgtPos: 0, myPos: null, sugPos: null }
        const sorted = [...prices].sort((a,b)=>a-b)
        const min = sorted[0]
        const max = sorted[sorted.length - 1]

        // 순위 기반 위치(등간격)로 계산
        const toPos = (i) => (n <= 1) ? 0 : Math.min(100, Math.max(0, Math.round((i / (n - 1)) * 100)))
        const findIdxGE = (list, v) => {
          const i = list.findIndex(p => p >= v)
          return i >= 0 ? i : (list.length - 1)
        }

        const tp = this.targetCompetitivenessPercent || 1
        const idx = Math.max(0, Math.ceil((tp/100)*n) - 1)

        const current = this.integratedPriceSuggestion.currentPrice || null
        const suggested = (this.manualSuggestion.full != null ? this.manualSuggestion.full : this.integratedPriceSuggestion.fullRangePrice) || null

        return {
          n,
          min,
          max,
          tgtPos: toPos(Math.min(idx, n - 1)),
          myPos: current!=null ? toPos(findIdxGE(sorted, current)) : null,
          sugPos: suggested!=null ? toPos(findIdxGE(sorted, suggested)) : null
        }
      } catch (e) {
        console.warn('⚠️ [barFull] 계산 실패:', e)
        return { n: 0, min: 0, max: 0, tgtPos: 0, myPos: null, sugPos: null }
      }
    },

    // 목표 백분위 위치 표시용 Bar 데이터(근접범위)
    barNear() {
      try {
        const prices = this.collectPriceList('nearbyRange')
        const n = prices.length
        if (!n) return { n: 0, min: 0, max: 0, tgtPos: 0, myPos: null, sugPos: null }
        const sorted = [...prices].sort((a,b)=>a-b)
        const min = sorted[0]
        const max = sorted[sorted.length - 1]

        // 순위 기반 위치(등간격)로 계산
        const toPos = (i) => (n <= 1) ? 0 : Math.min(100, Math.max(0, Math.round((i / (n - 1)) * 100)))
        const findIdxGE = (list, v) => {
          const i = list.findIndex(p => p >= v)
          return i >= 0 ? i : (list.length - 1)
        }

        const tp = this.targetCompetitivenessPercent || 1
        const idx = Math.max(0, Math.ceil((tp/100)*n) - 1)

        const current = this.integratedPriceSuggestion.currentPrice || null
        const suggested = (this.manualSuggestion.near != null ? this.manualSuggestion.near : this.integratedPriceSuggestion.nearbyRangePrice) || null

        return {
          n,
          min,
          max,
          tgtPos: toPos(Math.min(idx, n - 1)),
          myPos: current!=null ? toPos(findIdxGE(sorted, current)) : null,
          sugPos: suggested!=null ? toPos(findIdxGE(sorted, suggested)) : null
        }
      } catch (e) {
        console.warn('⚠️ [barNear] 계산 실패:', e)
        return { n: 0, min: 0, max: 0, tgtPos: 0, myPos: null, sugPos: null }
      }
    },

    // 🔧 통합 가격 제안 계산 (전체범위/근접범위 통합)
    integratedPriceSuggestion() {
      if (this.competitivenessResults.length === 0 || !this.targetCompetitivenessPercent) {
        return { fullRangePrice: null, nearbyRangePrice: null, currentPrice: null }
      }
      const currentPrice = this.competitivenessResults[0]?.price || 0
      const tp = this.targetCompetitivenessPercent

      // 백분위 기반 제안가(1%→1등, 100%→꼴찌)
      const fullPrices = this.collectPriceList('fullRange')
      const nearPrices = this.collectPriceList('nearbyRange')

      let fullRangePrice = null
      let nearbyRangePrice = null

      if (fullPrices.length > 0) fullRangePrice = this.percentilePriceSuggest(fullPrices, tp)
      if (nearPrices.length > 0) nearbyRangePrice = this.percentilePriceSuggest(nearPrices, tp)

      // priceList가 없으면 기존 휴리스틱으로 폴백
      if (fullRangePrice === null || nearbyRangePrice === null) {
        const aggFull = this.aggregateRangeData('fullRange')
        const aggNear = this.aggregateRangeData('nearbyRange')
        if (fullRangePrice === null) fullRangePrice = this.calculateIntegratedPrice(aggFull, currentPrice)
        if (nearbyRangePrice === null) nearbyRangePrice = this.calculateIntegratedPrice(aggNear, currentPrice)
        console.log('🛈 [통합제안][fallback] priceList 없음 → 휴리스틱 사용')
      }

      console.log('\ud83e\udd0e [\ud1b5\ud569\uc81c\uc548] %', tp, '| full:', fullRangePrice, '| near:', nearbyRangePrice, '| current:', currentPrice, '| fullN:', fullPrices.length, '| nearN:', nearPrices.length)
      return { fullRangePrice, nearbyRangePrice, currentPrice }
    },

    // 키워드설정 관련 computed
    autoListWithIndex() {
      return this.autoList.map((item, index) => {
        // 기존 데이터에 startPage, endPage가 없는 경우 처리
        let startPage = item.startPage || 1
        let endPage = item.endPage || item.pageRange || 1

        // pageRange가 문자열인 경우 파싱
        if (typeof item.pageRange === 'string' && item.pageRange.includes('-')) {
          const [start, end] = item.pageRange.split('-').map(Number)
          startPage = start || 1
          endPage = end || start || 1
        } else if (typeof item.pageRange === 'number') {
          startPage = endPage = item.pageRange
        }

        return {
          ...item,
          index,
          startPage,
          endPage
        }
      })
    },

    // 상품설정 관련 computed (노출수 기준 내림차순 정렬)
    productListWithIndex() {
      return this.productList
        .slice() // 원본 배열 보호
        .sort((a, b) => (b.impressions || 0) - (a.impressions || 0)) // 노출수 기준 내림차순 정렬
        .map((item, index) => {
          return {
            ...item,
            index
          }
        })
    },

    newKeywordsCount() {
      return this.autoList.filter(item => !item.analysisData || !item.collectedAt).length
    },

    // 진행 상황 메시지 (앞부분 + 뒷부분 조합)
    progressMessage() {
      return this.progressPrefix + this.progressSuffix
    },

    // 키워드 상세 모달용 근접 범위 상품 목록
    keywordDetailProducts() {
      if (!this.selectedKeywordResult) return []

      const keyword = this.selectedKeywordResult.keyword
      const searchResults = this.keywordSearchResults[keyword]

      console.log('🔍 [keywordDetailProducts] 키워드:', keyword)
      console.log('🔍 [keywordDetailProducts] searchResults 타입:', typeof searchResults)
      console.log('🔍 [keywordDetailProducts] searchResults 존재:', !!searchResults)

      if (!searchResults) return []

      // 실제 데이터 구조에 맞게 처리: 배열 형태로 저장되어 있음
      let products = []
      if (Array.isArray(searchResults)) {
        products = searchResults
      } else if (searchResults.products && Array.isArray(searchResults.products)) {
        products = searchResults.products
      } else {
        console.log('🔍 [keywordDetailProducts] 예상치 못한 데이터 구조:', searchResults)
        return []
      }

      console.log('🔍 [keywordDetailProducts] products 길이:', products.length)

      // 내 상품 위치 찾기 (배열을 직접 전달)
      const myProductIndex = this.findMyProductInResults(products)
      console.log('🔍 [keywordDetailProducts] myProductIndex:', myProductIndex)

      if (myProductIndex === -1) return []

      // 근접 범위 계산
      const range = this.compareRange
      const startIndex = Math.max(0, myProductIndex - range)
      const endIndex = Math.min(products.length - 1, myProductIndex + range)

      console.log('🔍 [keywordDetailProducts] 근접범위:', { range, startIndex, endIndex })

      // 근접 범위 상품들 추출 및 가공
      const nearbyProducts = products.slice(startIndex, endIndex + 1)

      console.log('🔍 [keywordDetailProducts] nearbyProducts 길이:', nearbyProducts.length)
      console.log('🔍 [keywordDetailProducts] 첫 번째 상품 샘플:', nearbyProducts[0])

      return nearbyProducts.map((product, index) => ({
        rank: startIndex + index + 1,
        vendorItemId: product.vendorItemId,
        productName: product.productName || product.name || '상품명 없음',
        price: parseInt(product.price) || 0,
        imageUrl: product.imgUrl || product.imageUrl || product.image || '/placeholder-image.png', // imgUrl 필드명 수정
        reviewCount: product.ratingcount || product.reviewCount || 0, // ratingcount 필드명 수정
        reviewRating: product.rating || product.reviewRating || 0, // rating 필드명 수정
        isMyProduct: product.vendorItemId === this.myVendorItemId
      }))
    },

  },
  methods: {

    // 키워드 상세 모달 열기
    openKeywordDetailModal(result) {
      console.log('🔍 [키워드 상세 모달] 열기:', result.keyword)
      this.selectedKeywordResult = result
      this.showKeywordDetailModal = true
    },



    // IndexedDB 초기화 (상품경쟁력분석 전용 DB만 사용 - 별도 초기화 불필요)
    async initializeDB() {
      // No-op: productCompetitiveAnalysis DB는 각 호출 시 open으로 생성/사용
    },

    // 상품그룹 초기화 (별도 IndexedDB에서 기존 그룹들 로드)
    async initializeProductGroups() {
      try {
        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'

        // IndexedDB 열기
        const request = indexedDB.open(scopedDbName(dbName), 1)

        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'groupName' })
          }
        }

        const db = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })

        const transaction = db.transaction([storeName], 'readonly')
        const store = transaction.objectStore(storeName)
        const getAllRequest = store.getAll()

        const allGroups = await new Promise((resolve, reject) => {
          getAllRequest.onsuccess = () => resolve(getAllRequest.result)
          getAllRequest.onerror = () => reject(getAllRequest.error)
        })

        db.close()

        if (allGroups && allGroups.length > 0) {
          const groupNames = allGroups.map(group => group.groupName).filter(name => name)

          // Default 그룹이 없으면 추가
          if (!groupNames.includes('Default')) {
            groupNames.unshift('Default')
          }

          this.productGroups = [...new Set(groupNames)] // 중복 제거

          console.log('✅ 상품그룹 초기화 완료:', this.productGroups)
        } else {
          // 기존 그룹이 없으면 Default만 설정
          this.productGroups = ['Default']
          console.log('✅ 기본 상품그룹 초기화:', this.productGroups)
        }
      } catch (error) {
        console.error('❌ 상품그룹 초기화 실패:', error)
        // 오류 시 기본값으로 설정
        this.productGroups = ['Default']
      }
    },

    // 키워드를 autoList에 추가
    addKeywordToList() {
      if (!this.targetKeyword.trim()) {
        alert('키워드를 입력해주세요.')
        return
      }

      // 페이지 범위 유효성 검사
      if (this.defaultStartPage > this.defaultEndPage) {
        alert('시작페이지가 끝페이지보다 클 수 없습니다.')
        return
      }
      if (this.defaultEndPage > 30) {
        alert('최대 30페이지까지만 수집 가능합니다.')
        this.defaultEndPage = 30
        return
      }

      const keyword = this.targetKeyword.trim()

      // 중복 체크
      if (this.autoList.some(item => item.keyword === keyword)) {
        alert('이미 추가된 키워드입니다.')
        return
      }

      // 페이지 범위 계산
      const pageRange = this.defaultStartPage === this.defaultEndPage ?
        this.defaultStartPage :
        `${this.defaultStartPage}-${this.defaultEndPage}`

      // autoList에 추가
      this.autoList.push({
        keyword: keyword,
        pageRange: pageRange,
        startPage: this.defaultStartPage,
        endPage: this.defaultEndPage,
        completedPages: [], // 수집 완료된 페이지들 (새로 추가)
        source: '직접입력',
        analysisData: null,
        collectedAt: null
      })

      // 입력란 초기화
      this.targetKeyword = ''

      // 키워드 추가 즉시 IndexedDB에 저장
      this.saveCurrentGroupKeywords()
      //console.log('현재 autoList:', this.autoList)
      //console.log('autoListWithIndex:', this.autoListWithIndex)

      // Vue 반응성 강제 업데이트
      this.$forceUpdate()

      // 입력창에 다시 포커스
      this.$nextTick(() => {
        if (this.$refs.modalKeywordInput) {
          this.$refs.modalKeywordInput.focus()
        }
      })
    },

    // 페이지 범위 업데이트
    async updatePageRange(item) {
      // autoList에서 해당 키워드의 실제 인덱스 찾기
      const keywordIndex = this.autoList.findIndex(kw =>
        (kw.keyword || kw.targetKeyword) === (item.keyword || item.targetKeyword)
      )

      if (keywordIndex === -1) {
        console.error('❌ [페이지 범위 업데이트] 키워드를 찾을 수 없음:', item.keyword || item.targetKeyword)
        return
      }

      // autoList의 실제 항목 참조
      const actualItem = this.autoList[keywordIndex]

      // 숫자형으로 변환 (문자열 입력 방지)
      const MAX_PAGE = 30
      const newStartPage = Math.min(parseInt(item.startPage) || 1, MAX_PAGE)
      const newEndPage = Math.min(parseInt(item.endPage) || newStartPage, MAX_PAGE)

      // UI 값도 cap 반영
      if ((parseInt(item.startPage) || 1) > MAX_PAGE) item.startPage = MAX_PAGE
      if ((parseInt(item.endPage) || 1) > MAX_PAGE) item.endPage = MAX_PAGE

      if (newStartPage > newEndPage) {
        item.endPage = newStartPage
        return // 잘못된 범위이므로 다시 입력하도록 함
      }

      const newPageRange = newStartPage === newEndPage ?
        newStartPage.toString() :
        `${newStartPage}-${newEndPage}`

      // console.log(`🔧 [페이지 범위 업데이트] ${actualItem.keyword}: ${actualItem.pageRange} → ${newPageRange}`)
      // console.log(`🔧 [페이지 범위 상세] startPage: ${newStartPage} (${typeof newStartPage}), endPage: ${newEndPage} (${typeof newEndPage})`)

      // 페이지 범위가 변경되었는지 확인
      const pageRangeChanged = actualItem.pageRange !== newPageRange

      if (pageRangeChanged) {
        // 새로운 페이지 범위 계산
        const newPages = []
        for (let i = newStartPage; i <= newEndPage; i++) {
          newPages.push(i)
        }

        // 기존 완료된 페이지 중 새 범위에 없는 페이지들 확인
        const completedPages = actualItem.completedPages || []
        const pagesToRemove = completedPages.filter(page => !newPages.includes(page))

        // 범위 축소로 인해 완료된 페이지가 제거되는 경우 확인
        if (pagesToRemove.length > 0) {
          const keywordName = actualItem.keyword || actualItem.targetKeyword
          if (!confirm(`"${keywordName}" 키워드의 페이지 범위를 축소하면 완료된 페이지 [${pagesToRemove.join(', ')}]의 데이터가 삭제됩니다.\n\n변경하시겠습니까?\n\n기존: ${actualItem.pageRange} → 변경: ${newPageRange}`)) {
            // 취소 시 원래 값으로 되돌리기
            const originalRange = this.parsePageRange(actualItem.pageRange)
            item.startPage = originalRange.startPage
            item.endPage = originalRange.endPage
            return
          }

          // 확인 시 해당 페이지의 데이터 제거
          this.removePageDataFromAnalysisResults(actualItem.keyword, pagesToRemove)
        }

        // autoList의 실제 항목을 직접 업데이트 (Vue 반응성 보장)
        this.$set(this.autoList, keywordIndex, {
          ...actualItem,
          pageRange: newPageRange,
          startPage: newStartPage,
          endPage: newEndPage,
          completedPages: completedPages.filter(page => newPages.includes(page))
        })

        // console.log(`✅ [페이지 범위 업데이트 완료] ${actualItem.keyword}:`, {
        //   pageRange: newPageRange,
        //   startPage: newStartPage,
        //   endPage: newEndPage,
        //   startPageType: typeof newStartPage,
        //   endPageType: typeof newEndPage,
        //   completedPages: this.autoList[keywordIndex].completedPages
        // })

        // Vue의 반응성 시스템이 완전히 처리된 후 저장
        await this.$nextTick()
        this.saveCurrentGroupKeywords()
      }
    },



    startAnalysis() {
      if (this.productList.length === 0) {
        alert('상품설정에서 분석할 키워드를 추가해주세요.')
        return
      }

      if (!this.compareRange || this.compareRange < 1) {
        alert('비교 범위를 1 이상으로 설정해주세요.')
        return
      }

      this.startCompetitiveAnalysis()
    },

    // 쿠팡 API 차단 에러 분류 (Preflight + 연속 실패 감지)
    classifyCollectionError(errorData, pageNum) {
      if (!errorData || !errorData.blocked) return { action: 'other' }
      if (!this.hasReceivedAnyCollectionSuccess) {
        return { action: 'hard', message: '쿠팡 API 접근이 차단되었습니다.\n\nCLI가 정상 실행 중인지, 쿠팡 계정이 로그인되어 있는지 확인해주세요.' }
      }
      if (pageNum === 1) {
        this.consecutivePage1Failures++
        if (this.consecutivePage1Failures >= 3) {
          return { action: 'hard', message: '수집 중 쿠팡 API 차단이 감지되었습니다 (3개 키워드 연속 실패).\n\n지금까지 수집된 데이터는 유지됩니다. 잠시 후 다시 시도해주세요.' }
        }
        return { action: 'soft-keyword' }
      }
      this.consecutivePage1Failures = 0
      return { action: 'soft-page' }
    },
    markCollectionSuccess() {
      this.hasReceivedAnyCollectionSuccess = true
      this.consecutivePage1Failures = 0
    },

    // 상품 경쟁력 분석 시작
    async startCompetitiveAnalysis() {
      this.isAnalyzing = true
      this.progressPercent = 0
      this.progressPrefix = ''
      this.progressSuffix = '경쟁력 분석을 시작합니다...'

      // Preflight + 연속 실패 카운터 초기화
      this.hasReceivedAnyCollectionSuccess = false
      this.consecutivePage1Failures = 0

      // 상품설정 모달 닫기
      this.showProductModal = false

      console.log('🚀 [상품 경쟁력 분석] 시작')
      console.log('📊 [분석 대상] 키워드:', this.productList.length, '개')
      console.log('📏 [비교 범위]:', this.compareRange)

      // 분석 결과 초기화
      this.analysisResults = []
      this.summaryData = []
      this.keywordSearchResults = {} // 키워드별 검색 결과 저장

      try {
        // 각 키워드별로 경쟁 상품 수집 및 분석
        for (let i = 0; i < this.productList.length; i++) {
          const keyword = this.productList[i]

          this.progressPercent = Math.floor((i / this.productList.length) * 100)
          this.progressPrefix = ''
          this.progressSuffix = `키워드 "${keyword.keyword}" 분석 중... (${i + 1}/${this.productList.length})`

          console.log(`🔍 [키워드 ${i + 1}/${this.productList.length}] "${keyword.keyword}" 분석 시작`)

          // 키워드별 경쟁 상품 수집 및 분석
          await this.analyzeKeywordCompetitiveness(keyword)

          // 키워드별 분석 완료 후 중간 저장 (5개마다)
          if ((i + 1) % 5 === 0 || i === this.productList.length - 1) {
            try {
              await this.saveToIndexedDB()
              console.log(`💾 [중간 저장] ${i + 1}/${this.productList.length} 키워드 분석 완료 후 저장`)
            } catch (saveError) {
              console.error('❌ [중간 저장] 실패:', saveError)
            }
          }

          // 잠시 대기 (API 부하 방지)
          await new Promise(resolve => setTimeout(resolve, 1000))
        }

        this.progressPercent = 100
        this.progressPrefix = ''
        this.progressSuffix = '경쟁력 분석이 완료되었습니다!'

        console.log('✅ [상품 경쟁력 분석] 완료')

        // 분석 완료 후 자동 저장
        await this.saveToIndexedDB()
        console.log('💾 [자동 저장] 분석 결과 저장 완료')

        alert('경쟁력 분석이 완료되었습니다!')

      } catch (error) {
        console.error('❌ [상품 경쟁력 분석] 실패:', error)

        // 오류 발생 시에도 부분 결과 저장
        if (this.competitivenessResults.length > 0) {
          try {
            await this.saveToIndexedDB()
            console.log('💾 [오류 시 저장] 부분 분석 결과 저장 완료')
          } catch (saveError) {
            console.error('❌ [오류 시 저장] 실패:', saveError)
          }
        }

        alert('경쟁력 분석 중 오류가 발생했습니다: ' + error.message)
      } finally {
        this.isAnalyzing = false
      }
    },

    // 키워드별 경쟁력 분석
    async analyzeKeywordCompetitiveness(keyword) {
      console.log(`🔍 [키워드 분석] "${keyword.keyword}" 시작`)

      // 1단계: 키워드 검색으로 내 상품 위치 파악
      const searchResults = await this.searchKeywordProducts(keyword.keyword)
      if (!searchResults || searchResults.length === 0) {
        console.log(`⚠️ [키워드 분석] "${keyword.keyword}" 검색 결과 없음`)
        return
      }

      // 검색 결과 저장 (가격 분석에서 활용)
      this.keywordSearchResults[keyword.keyword] = searchResults

      // 2단계: 내 상품 찾기 (vendorItemId 매칭)
      const myProductIndex = this.findMyProductInResults(searchResults)
      if (myProductIndex === -1) {
        console.log(`⚠️ [키워드 분석] "${keyword.keyword}"에서 내 상품을 찾을 수 없음 - 전체범위만 분석`)
        // 내 상품이 없어도 전체범위 분석은 진행
        // searchResults는 직접 배열이므로 .products 제거
        const fullRangeAnalysis = this.performFullRangeAnalysis(keyword.keyword, searchResults)

        if (fullRangeAnalysis) {
          this.savePriceAnalysisResultWithoutNearby(keyword.keyword, fullRangeAnalysis)
        }
        return
      }

      console.log(`📍 [내 상품 위치] "${keyword.keyword}"에서 ${myProductIndex + 1}위`)

      // 3단계: 비교 대상 상품 선정
      const competitorProducts = this.selectCompetitorProducts(searchResults, myProductIndex)
      console.log(`🎯 [비교 대상] ${competitorProducts.length}개 상품 선정`)

      // 조건부 분석: shownomadv3가 true인 경우에만 상세 분석
      if (this.shownomadv3) {
        console.log(`🔬 [종합 분석] "${keyword.keyword}" 상세 정보 수집 시작`)

        // 4단계: 선정된 상품들의 상세 정보 수집
        const detailedProducts = await this.collectDetailedProductInfo(competitorProducts)
        console.log(`📊 [상세 정보] ${detailedProducts.length}개 상품 수집 완료`)

        // 5단계: 경쟁력 분석 수행
        const competitivenessAnalysis = this.performCompetitivenessAnalysis(detailedProducts)

        // 6단계: 결과 저장
        this.saveAnalysisResult(keyword.keyword, competitivenessAnalysis)
      } else {
        console.log(`💰 [가격 분석] "${keyword.keyword}" 가격 경쟁력만 분석`)

        // 간단한 가격 경쟁력 분석
        const priceAnalysis = await this.performPriceCompetitivenessAnalysis(keyword.keyword, competitorProducts, myProductIndex)

        // 가격 분석 결과 저장
        if (priceAnalysis) {
          this.savePriceAnalysisResult(keyword.keyword, priceAnalysis)
        } else {
          console.warn(`⚠️ [키워드 분석] "${keyword.keyword}" 가격 분석 실패`)
        }
      }

      console.log(`✅ [키워드 분석] "${keyword.keyword}" 완료`)
    },

    // 키워드 검색으로 상품 목록 가져오기
    async searchKeywordProducts(keyword) {
      return new Promise((resolve, reject) => {
        console.log(`🔍 [키워드 검색] "${keyword}" 검색 시작`)

        // 확장 프로그램으로 키워드 검색 요청 (효율적 수집)
        window.postMessage({
          type: 'searchKeywordProducts', // greeting 대신 type 사용
          keyword: keyword,
          myVendorItemId: this.myVendorItemId,
          range: this.compareRange // 내 상품 위치 + range까지만 수집
        }, '*')

        // 응답 대기
        const timeout = setTimeout(() => {
          reject(new Error(`키워드 "${keyword}" 검색 시간 초과`))
        }, 30000) // 30초 타임아웃

        // 임시로 응답 처리 (실제로는 handleExtensionMessage에서 처리)
        this.keywordSearchPromises = this.keywordSearchPromises || {}
        this.keywordSearchPromises[keyword] = { resolve, reject, timeout }
      })
    },

    // 검색 결과에서 내 상품 찾기
    findMyProductInResults(searchResults) {
      if (!this.myVendorItemId) {
        console.error('❌ [내 상품 찾기] myVendorItemId가 설정되지 않음')
        return -1
      }

      // vendorItemId로 내 상품 찾기
      for (let i = 0; i < searchResults.length; i++) {
        const product = searchResults[i]
        if (product.vendorItemId && product.vendorItemId.toString() === this.myVendorItemId.toString()) {
          console.log(`✅ [내 상품 발견] ${i + 1}위 - vendorItemId: ${product.vendorItemId}`)
          return i
        }
      }

      console.warn(`⚠️ [내 상품 찾기] vendorItemId ${this.myVendorItemId}를 찾을 수 없음`)
      console.log('🔍 [디버깅] 검색된 상품들의 vendorItemId:', searchResults.slice(0, 10).map(p => ({
        rank: searchResults.indexOf(p) + 1,
        vendorItemId: p.vendorItemId,
        productName: p.productName?.substring(0, 30)
      })))

      return -1
    },

    // 비교 대상 상품 선정
    selectCompetitorProducts(searchResults, myProductIndex) {
      const competitors = []
      const range = this.compareRange

      // 내 상품부터 뒤로 range만큼 (공통 로직)
      const startIndex = myProductIndex
      const endIndex = Math.min(searchResults.length - 1, myProductIndex + range)

      console.log(`🎯 [비교 범위] ${startIndex + 1}위 ~ ${endIndex + 1}위 (내 상품: ${myProductIndex + 1}위, range: ${range})`)

      // 선정된 범위의 상품들 추가
      for (let i = startIndex; i <= endIndex; i++) {
        competitors.push({
          ...searchResults[i],
          isMyProduct: i === myProductIndex,
          rank: i + 1
        })
      }

      console.log(`📊 [상품 선정] ${competitors.length}개 상품 선정 완료`)

      return competitors
    },

    // 가격 경쟁력 분석 (개선된 버전)
    async performPriceCompetitivenessAnalysis(keyword, competitorProducts, myProductIndex) {
      const myProduct = competitorProducts.find(p => p.isMyProduct === true)
      if (!myProduct) {
        console.warn(`⚠️ [가격 분석] 내 상품을 찾을 수 없음`)
        console.log(`🔍 [디버깅] 경쟁 상품들:`, competitorProducts.map(p => ({
          rank: p.rank,
          vendorItemId: p.vendorItemId,
          isMyProduct: p.isMyProduct,
          productName: p.productName?.substring(0, 30)
        })))
        return null
      }

      const myPrice = parseInt(myProduct.price) || 0

      // 1. 전체 범위 분석 (1위~내상품뒤 range까지)
      const fullRangeEndIndex = myProductIndex + this.compareRange // 1위~내상품위치+근접범위까지
      const fullRangeProducts = await this.getFullRangeProducts(keyword, fullRangeEndIndex + 1)

      // 2. 근접 범위 분석 (내상품 앞뒤로 range만큼) - 키워드 전체 검색결과 기준으로 산정
      const nearbyRangeProducts = this.getNearbyRangeProducts(keyword, myProductIndex)

      // 전체 범위 가격 분석
      const fullRangeAnalysis = this.calculatePriceRanking(fullRangeProducts, myPrice)

      // 근접 범위 가격 분석
      const nearbyRangeAnalysis = this.calculatePriceRanking(nearbyRangeProducts, myPrice)

      return {
        keyword,
        myProduct: {
          ...myProduct,
          searchRank: myProductIndex + 1,
          price: myPrice
        },
        fullRange: {
          ...fullRangeAnalysis,
          description: `1위~${fullRangeEndIndex + 1}위 전체 범위`
        },
        nearbyRange: {
          ...nearbyRangeAnalysis,
          description: `내상품 앞뒤 ${this.compareRange}개 범위`
        }
      }
    },

    // 전체 범위 상품 가져오기 (이미 검색된 결과 활용)
    async getFullRangeProducts(keyword, endIndex) {
      // 이미 검색된 결과에서 필요한 범위만 추출
      const searchResults = this.keywordSearchResults[keyword] || []
      return searchResults.slice(0, endIndex)
    },

    // 근접 범위 상품 선정 (키워드 전체 검색결과 기준)
    getNearbyRangeProducts(keyword, myProductIndex) {
      const list = this.normalizeSearchResultList(this.keywordSearchResults[keyword])
      const range = this.compareRange
      const startIndex = Math.max(0, myProductIndex - range)
      const endIndex = Math.min(list.length - 1, myProductIndex + range)
      const sliced = list.slice(startIndex, endIndex + 1)
      console.log('🔎 [근접범위 산정]', JSON.stringify({ keyword, myIndex: myProductIndex, listLength: list.length, startIndex, endIndex, slicedCount: sliced.length }))
      return sliced
    },

    // 검색결과 normalize: 배열 또는 {products: []} 모두 지원
    normalizeSearchResultList(sr) {
      if (!sr) return []
      if (Array.isArray(sr)) return sr
      if (sr.products && Array.isArray(sr.products)) return sr.products
      return []
    },

    // 가격 순위 계산
    calculatePriceRanking(products, myPrice) {
      const prices = products.map(p => parseInt(p.price) || 0).filter(price => price > 0)
      const avgPrice = prices.length > 0 ? Math.round(prices.reduce((sum, price) => sum + price, 0) / prices.length) : 0
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 0

      // 내 상품 가격 순위 계산 (저렴한 순)
      const sortedPrices = [...prices].sort((a, b) => a - b)

      // 🔍 디버깅: 가격 순위 계산 과정 로깅
      console.log('🔍 [가격순위 계산] myPrice:', myPrice)
      console.log('🔍 [가격순위 계산] sortedPrices:', sortedPrices)
      console.log('🔍 [가격순위 계산] products 수:', products.length)
      console.log('🔍 [가격순위 계산] 유효한 prices 수:', prices.length)

      // 더 정확한 순위 계산: 내 가격보다 저렴한 상품 수 + 1
      const cheaperCount = sortedPrices.filter(price => price < myPrice).length
      const myPriceRank = cheaperCount + 1

      console.log('🔍 [가격순위 계산] 내 가격보다 저렴한 상품 수:', cheaperCount)
      console.log('🔍 [가격순위 계산] 계산된 순위:', myPriceRank)

      // 가격 경쟁력 점수 계산 (1-100점)
      let competitivenessScore = 50
      if (avgPrice > 0) {
        if (myPrice < avgPrice) {
          competitivenessScore = Math.min(100, 50 + ((avgPrice - myPrice) / avgPrice) * 50)
        } else {
          competitivenessScore = Math.max(0, 50 - ((myPrice - avgPrice) / avgPrice) * 50)
        }
      }

      return {
        totalProducts: products.length,
        avgPrice,
        minPrice,
        maxPrice,
        myPriceRank,
        competitivenessScore: Math.round(competitivenessScore),
        priceList: sortedPrices,
        priceDistribution: this.calculatePriceDistribution(prices, myPrice)
      }
    },

    // 가격 분포 계산
    calculatePriceDistribution(prices, myPrice) {
      const cheaper = prices.filter(price => price < myPrice).length
      const same = prices.filter(price => price === myPrice).length
      const expensive = prices.filter(price => price > myPrice).length

      return {
        cheaper,
        same,
        expensive,
        cheaperPercent: Math.round((cheaper / prices.length) * 100),
        expensivePercent: Math.round((expensive / prices.length) * 100)
      }
    },

    // 가격 분석 결과 저장
    savePriceAnalysisResult(keyword, priceAnalysis) {
      if (!priceAnalysis) {
        console.warn(`⚠️ [가격 분석 저장] "${keyword}" - 분석 결과가 null입니다`)
        return
      }

      // 가격 경쟁력 분석 결과를 competitivenessResults에 저장
      this.competitivenessResults.push({
        keyword: keyword,
        searchRank: priceAnalysis.myProduct.searchRank,
        price: priceAnalysis.myProduct.price,

        // 전체 범위 분석 결과
        fullRange: priceAnalysis.fullRange,

        // 근접 범위 분석 결과
        nearbyRange: priceAnalysis.nearbyRange,

        // 메타 정보
        analysisType: 'price',
        timestamp: new Date().toISOString()
      })

      console.log(`💰 [가격 분석 저장] "${keyword}" - 검색순위: ${priceAnalysis.myProduct.searchRank}위`)
      console.log(`📊 [전체범위] 가격순위: ${priceAnalysis.fullRange.myPriceRank}/${priceAnalysis.fullRange.totalProducts}위, 경쟁력: ${priceAnalysis.fullRange.competitivenessScore}점`)
      console.log(`📊 [근접범위] 가격순위: ${priceAnalysis.nearbyRange.myPriceRank}/${priceAnalysis.nearbyRange.totalProducts}위, 경쟁력: ${priceAnalysis.nearbyRange.competitivenessScore}점`)
    },

    // 선정된 상품들의 상세 정보 수집
    async collectDetailedProductInfo(competitorProducts) {
      const detailedProducts = []

      for (let i = 0; i < competitorProducts.length; i++) {
        const product = competitorProducts[i]

        try {
          // getSnPvInfoDetail 호출
          const detailInfo = await this.getProductDetail(product.vendorItemId)
          if (!detailInfo) {
            console.log(`⚠️ [상세 정보] ${product.vendorItemId} 상세 정보 없음 - 건너뛰기`)
            continue
          }

          // getTrafficKeyword 호출
          const trafficInfo = await this.getProductTrafficInfo(detailInfo)

          detailedProducts.push({
            ...product,
            detailInfo,
            trafficInfo
          })

        } catch (error) {
          console.error(`❌ [상세 정보] ${product.vendorItemId} 수집 실패:`, error)
          // 에러가 있어도 계속 진행
        }

        // API 부하 방지를 위한 대기
        await new Promise(resolve => setTimeout(resolve, 500))
      }

      return detailedProducts
    },

    // 상품 상세 정보 가져오기
    async getProductDetail(vendorItemId) {
      return new Promise((resolve, reject) => {
        console.log(`📊 [상세 정보] ${vendorItemId} 수집 시작`)

        // 확장 프로그램으로 상세 정보 요청
        window.postMessage({
          greeting: 'getProductDetail',
          vendorItemId: vendorItemId
        }, '*')

        // 응답 대기
        const timeout = setTimeout(() => {
          resolve(null) // 타임아웃 시 null 반환 (에러로 처리하지 않음)
        }, 15000) // 15초 타임아웃

        // 임시로 응답 처리
        this.productDetailPromises = this.productDetailPromises || {}
        this.productDetailPromises[vendorItemId] = { resolve, reject, timeout }
      })
    },

    // 상품 트래픽 정보 가져오기
    async getProductTrafficInfo(detailInfo) {
      if (!detailInfo.itemId || !detailInfo.displayCategoryInfo || !detailInfo.displayCategoryInfo[0]) {
        return null
      }

      return new Promise((resolve, reject) => {
        console.log(`📈 [트래픽 정보] ${detailInfo.itemId} 수집 시작`)

        // 확장 프로그램으로 트래픽 정보 요청
        window.postMessage({
          greeting: 'getProductTrafficInfo',
          itemId: detailInfo.itemId,
          leafCategoryCode: detailInfo.displayCategoryInfo[0].leafCategoryCode
        }, '*')

        // 응답 대기
        const timeout = setTimeout(() => {
          resolve(null) // 타임아웃 시 null 반환
        }, 15000) // 15초 타임아웃

        // 임시로 응답 처리
        this.trafficInfoPromises = this.trafficInfoPromises || {}
        this.trafficInfoPromises[detailInfo.itemId] = { resolve, reject, timeout }
      })
    },

    // 경쟁력 분석 수행
    performCompetitivenessAnalysis(detailedProducts) {
      console.log(`🏆 [경쟁력 분석] ${detailedProducts.length}개 상품 분석 시작`)

      const myProduct = detailedProducts.find(p => p.isMyProduct)
      if (!myProduct) {
        console.error('❌ [경쟁력 분석] 내 상품을 찾을 수 없음')
        return null
      }

      // 5가지 지표별 순위 계산
      const analysis = {
        keyword: myProduct.keyword || '',
        myRank: myProduct.rank,
        totalProducts: detailedProducts.length,
        competitiveness: {
          rpi: this.calculateRPIRank(detailedProducts, myProduct),
          advertising: this.calculateAdvertisingRank(detailedProducts, myProduct),
          ctr: this.calculateCTRRank(detailedProducts, myProduct),
          cvr: this.calculateCVRRank(detailedProducts, myProduct),
          price: this.calculatePriceRank(detailedProducts, myProduct)
        },
        products: detailedProducts,
        analyzedAt: new Date()
      }

      console.log(`🏆 [경쟁력 분석] 완료:`, analysis.competitiveness)

      return analysis
    },

    // RPI 경쟁력 순위 계산
    calculateRPIRank(products, myProduct) {
      // RPI = Revenue Per Impression
      const productsWithRPI = products.map(p => ({
        ...p,
        rpi: this.calculateRPI(p)
      })).filter(p => p.rpi !== null)

      // RPI 기준 내림차순 정렬
      productsWithRPI.sort((a, b) => b.rpi - a.rpi)

      const myRank = productsWithRPI.findIndex(p => p.isMyProduct) + 1

      return {
        rank: myRank,
        total: productsWithRPI.length,
        value: myProduct.rpi || 0,
        percentage: myRank > 0 ? Math.round((1 - (myRank - 1) / productsWithRPI.length) * 100) : 0
      }
    },

    // 광고 경쟁력 순위 계산
    calculateAdvertisingRank(products, myProduct) {
      // 광고 관련 지표 (광고비 대비 매출 등)
      const productsWithAd = products.map(p => ({
        ...p,
        adScore: this.calculateAdScore(p)
      })).filter(p => p.adScore !== null)

      productsWithAd.sort((a, b) => b.adScore - a.adScore)

      const myRank = productsWithAd.findIndex(p => p.isMyProduct) + 1

      return {
        rank: myRank,
        total: productsWithAd.length,
        value: myProduct.adScore || 0,
        percentage: myRank > 0 ? Math.round((1 - (myRank - 1) / productsWithAd.length) * 100) : 0
      }
    },

    // CTR 경쟁력 순위 계산
    calculateCTRRank(products, myProduct) {
      // CTR = Click Through Rate
      const productsWithCTR = products.map(p => ({
        ...p,
        ctr: this.calculateCTR(p)
      })).filter(p => p.ctr !== null)

      productsWithCTR.sort((a, b) => b.ctr - a.ctr)

      const myRank = productsWithCTR.findIndex(p => p.isMyProduct) + 1

      return {
        rank: myRank,
        total: productsWithCTR.length,
        value: myProduct.ctr || 0,
        percentage: myRank > 0 ? Math.round((1 - (myRank - 1) / productsWithCTR.length) * 100) : 0
      }
    },

    // CVR 경쟁력 순위 계산
    calculateCVRRank(products, myProduct) {
      // CVR = Conversion Rate
      const productsWithCVR = products.map(p => ({
        ...p,
        cvr: this.calculateCVR(p)
      })).filter(p => p.cvr !== null)

      productsWithCVR.sort((a, b) => b.cvr - a.cvr)

      const myRank = productsWithCVR.findIndex(p => p.isMyProduct) + 1

      return {
        rank: myRank,
        total: productsWithCVR.length,
        value: myProduct.cvr || 0,
        percentage: myRank > 0 ? Math.round((1 - (myRank - 1) / productsWithCVR.length) * 100) : 0
      }
    },

    // 가격 경쟁력 순위 계산
    calculatePriceRank(products, myProduct) {
      const productsWithPrice = products.map(p => ({
        ...p,
        price: this.getProductPrice(p)
      })).filter(p => p.price !== null)

      // 가격은 낮을수록 좋으므로 오름차순 정렬
      productsWithPrice.sort((a, b) => a.price - b.price)

      const myRank = productsWithPrice.findIndex(p => p.isMyProduct) + 1

      return {
        rank: myRank,
        total: productsWithPrice.length,
        value: myProduct.price || 0,
        percentage: myRank > 0 ? Math.round((1 - (myRank - 1) / productsWithPrice.length) * 100) : 0
      }
    },

    // RPI 계산 (Revenue Per Impression)
    calculateRPI(product) {
      if (!product.detailInfo || !product.trafficInfo) return null

      const revenue = product.detailInfo.salesLast28d || 0
      const impressions = product.trafficInfo.totalImpressions || 0

      return impressions > 0 ? revenue / impressions : 0
    },

    // 광고 점수 계산
    calculateAdScore(product) {
      if (!product.detailInfo || !product.trafficInfo) return null

      // 광고 관련 지표들을 종합한 점수
      const adRevenue = product.trafficInfo.adRevenue || 0
      const adCost = product.trafficInfo.adCost || 0
      const adROAS = adCost > 0 ? adRevenue / adCost : 0

      return adROAS
    },

    // CTR 계산 (Click Through Rate)
    calculateCTR(product) {
      if (!product.detailInfo || !product.trafficInfo) return null

      const clicks = product.trafficInfo.totalClicks || 0
      const impressions = product.trafficInfo.totalImpressions || 0

      return impressions > 0 ? (clicks / impressions) * 100 : 0
    },

    // CVR 계산 (Conversion Rate)
    calculateCVR(product) {
      if (!product.detailInfo) return null

      const sales = product.detailInfo.salesLast28d || 0
      const pv = product.detailInfo.pvLast28Day || 0

      return pv > 0 ? (sales / pv) * 100 : 0
    },

    // 상품 가격 가져오기
    getProductPrice(product) {
      if (!product.detailInfo) return null

      return product.detailInfo.salePrice || product.detailInfo.originalPrice || 0
    },



    // IndexedDB에 저장
    async saveToIndexedDB() {
      try {
        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'

        // 현재 상품그룹의 데이터 저장
        const groupData = {
          groupName: this.activeProductGroup,
          productList: this.productList,
          competitivenessResults: this.competitivenessResults,
          keywordSearchResults: this.keywordSearchResults, // 🔧 키워드 검색 결과 저장 추가
          compareRange: this.compareRange,
          myVendorItemId: this.myVendorItemId,
          targetCompetitivenessPercent: this.targetCompetitivenessPercent, // 목표 경쟁력 % 저장
          updatedAt: new Date()
        }

        // IndexedDB 저장 로직 (기존 keywordDataCollect와 유사)
        const request = indexedDB.open(scopedDbName(dbName), 1)

        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'groupName' })
          }
        }

        request.onsuccess = (event) => {
          const db = event.target.result
          const transaction = db.transaction([storeName], 'readwrite')
          const store = transaction.objectStore(storeName)

          const getReq = store.get(this.activeProductGroup)
          getReq.onsuccess = () => {
            const prev = getReq.result || { groupName: this.activeProductGroup }
            const merged = { ...prev, ...groupData } // autoList/maxProducts 등 기존 필드 보존
            const putReq = store.put(merged)
            putReq.onsuccess = () => {
              console.log('💾 [PCA] 저장 완료', { db: dbName, store: storeName, group: this.activeProductGroup })
            }
            putReq.onerror = (error) => {
              console.error('❌ [PCA] 저장 실패:', error)
            }
          }
          getReq.onerror = (error) => {
            console.error('❌ [PCA] 기존 레코드 조회 실패:', error)
          }
        }

        request.onerror = (error) => {
          console.error('❌ [IndexedDB] 연결 실패:', error)
        }

      } catch (error) {
        console.error('❌ [IndexedDB] 저장 중 오류:', error)
      }
    },

    // 키워드와 페이지 범위 재검증 및 정리
    async validateAndCleanupKeywords() {
      // console.log('🔍 [키워드 검증] 시작 - 총', this.autoList.length, '개 키워드')

      for (let i = 0; i < this.autoList.length; i++) {
        const keyword = this.autoList[i]
        // console.log(`🔍 [키워드 검증] ${i + 1}/${this.autoList.length}: ${keyword.keyword}`)
        // console.log(`🔍 [키워드 상태] ${keyword.keyword}:`, {
        //   pageRange: keyword.pageRange,
        //   startPage: keyword.startPage,
        //   endPage: keyword.endPage,
        //   startPageType: typeof keyword.startPage,
        //   endPageType: typeof keyword.endPage
        // })

        // 1. 기존 pageRange에서 startPage, endPage 파싱 (IndexedDB 로딩 시 누락될 수 있음)
        if (!keyword.startPage || !keyword.endPage) {
          const parsedRange = this.parsePageRange(keyword.pageRange)
          keyword.startPage = parsedRange.startPage
          keyword.endPage = parsedRange.endPage
          // console.log(`🔧 [페이지 범위 파싱] ${keyword.keyword}: pageRange "${keyword.pageRange}" → startPage: ${keyword.startPage}, endPage: ${keyword.endPage}`)
        } else {
          // console.log(`✅ [페이지 범위 확인] ${keyword.keyword}: startPage: ${keyword.startPage}, endPage: ${keyword.endPage} (파싱 불필요)`)
        }

        // 2. 페이지 범위 정규화
        const currentStartPage = parseInt(keyword.startPage) || 1
        const currentEndPage = parseInt(keyword.endPage) || 1
        const normalizedPageRange = currentStartPage === currentEndPage ?
          currentStartPage.toString() :
          `${currentStartPage}-${currentEndPage}`

        // console.log(`🔍 [페이지 범위 검증] ${keyword.keyword}: 기존 "${keyword.pageRange}" → 현재 "${normalizedPageRange}"`)

        // 3. 페이지 범위가 변경된 경우 처리
        if (keyword.pageRange !== normalizedPageRange) {
          // console.log(`🔧 [페이지 범위 변경] ${keyword.keyword}: ${keyword.pageRange} → ${normalizedPageRange}`)

          // 현재 페이지 범위 생성
          const newPages = []
          for (let page = currentStartPage; page <= currentEndPage; page++) {
            newPages.push(page)
          }

          // 기존 완료된 페이지 확인
          const completedPages = keyword.completedPages || []
          const pagesToRemove = completedPages.filter(page => !newPages.includes(page))

          // 범위 축소로 인한 데이터 삭제
          if (pagesToRemove.length > 0) {
            // console.log(`🗑️ [데이터 정리] ${keyword.keyword}: 페이지 [${pagesToRemove.join(', ')}] 데이터 삭제`)
            this.removePageDataFromAnalysisResults(keyword.keyword, pagesToRemove)
          }

          // 키워드 정보 업데이트
          keyword.pageRange = normalizedPageRange
          keyword.startPage = currentStartPage
          keyword.endPage = currentEndPage
          keyword.completedPages = completedPages.filter(page => newPages.includes(page))

          // console.log(`✅ [키워드 업데이트] ${keyword.keyword}: 페이지 범위 ${normalizedPageRange}, 완료된 페이지 [${keyword.completedPages.join(', ')}]`)
        }
      }

      // 4. 모든 변경사항을 IndexedDB에 저장
      // console.log('💾 [키워드 검증] IndexedDB 저장 시작...')
      await this.saveCurrentGroupKeywords()
      // console.log('✅ [키워드 검증] 완료 및 저장됨')
    },

    // 작업 큐 생성
    createWorkQueue() {
      this.workQueue = []

      this.autoList.forEach((keyword, keywordIndex) => {
        // console.log(`📋 [작업 큐 생성] 키워드 ${keywordIndex + 1}: ${keyword.keyword}`)
        // console.log(`📋 [작업 큐 생성] 키워드 상태:`, {
        //   pageRange: keyword.pageRange,
        //   startPage: keyword.startPage,
        //   endPage: keyword.endPage,
        //   completedPages: keyword.completedPages
        // })

        const pageRange = this.parsePageRange(keyword.pageRange)
        const completedPages = keyword.completedPages || []

        // console.log(`📋 [작업 큐] ${keyword.keyword}: 페이지 범위 ${pageRange.startPage}-${pageRange.endPage}, 완료된 페이지 [${completedPages.join(',')}]`)

        // 페이지 범위 내에서 완료되지 않은 페이지만 큐에 추가
        for (let pageIndex = pageRange.startPage; pageIndex <= pageRange.endPage; pageIndex++) {
          if (!completedPages.includes(pageIndex)) {
            // console.log(`📋 [작업 큐 추가] ${keyword.keyword} 페이지 ${pageIndex} 추가`)
            this.workQueue.push({
              keyword: keyword.keyword || keyword.targetKeyword,
              keywordIndex: keywordIndex,
              pageIndex: pageIndex,
              totalPages: pageRange.endPage
            })
          } else {
            console.log(`📋 [작업 큐 건너뛰기] ${keyword.keyword} 페이지 ${pageIndex} (이미 완료됨)`)
          }
        }
      })

      // console.log(`📋 [작업 큐] 생성 완료: ${this.workQueue.length}개 작업 (미완료 페이지만)`)
    },

    // 작업 큐 처리
    async processWorkQueue() {
      if (this.isProcessingQueue || this.workQueue.length === 0) {
        return
      }

      this.isProcessingQueue = true

      while (this.workQueue.length > 0 && this.isAnalyzing) {
        const work = this.workQueue.shift()

        // 진행률 업데이트
        const totalWork = this.autoList.reduce((sum, kw) => {
          const range = this.parsePageRange(kw.pageRange)
          return sum + (range.endPage - range.startPage + 1)
        }, 0)
        const completedWork = totalWork - this.workQueue.length
        this.progressPercent = (completedWork / totalWork) * 100

        // 진행 메시지 분리 설정 (상세 정보 포함)
        this.progressPrefix = `키워드 ${work.keywordIndex + 1}/${this.autoList.length}: ${work.keyword} [${work.pageIndex}p/${work.totalPages}p] `

        // 첫 번째 작업이거나 키워드가 바뀔 때만 progressSuffix 초기화
        if (!this.progressSuffix || this.progressSuffix === '키워드 분석을 시작합니다...') {
          this.progressSuffix = '상품 수집 중...'
        }
        // console.log('🔧 작업 큐에서 progressMessage 설정:', this.progressMessage)

        // console.log(`🚀 작업 처리: ${work.keyword} ${work.pageIndex}페이지`)

        // 작업 실행
        await this.executeWork(work)

        // 딜레이
        await new Promise(resolve => setTimeout(resolve, 500))
      }

      this.isProcessingQueue = false

      // 모든 작업 완료
      if (this.workQueue.length === 0) {
        this.completeAnalysis()
      }
    },

    // 개별 작업 실행
    executeWork(work) {
      return new Promise((resolve) => {
        // 현재 작업 정보 설정
        this.currentKeywordIndex = work.keywordIndex
        this.currentKeywordPageIndex = work.pageIndex
        this.currentKeywordTotalPages = work.totalPages

        // 완료 핸들러 등록
        const handleComplete = (event) => {
          if (event.data.res === 'keywordSeoAnalysisComplete') {
            window.removeEventListener('message', handleComplete)

            // 결과 처리
            this.processWorkResult(work, event.data.data || [])

            resolve()
          }
        }

        window.addEventListener('message', handleComplete)

        // Extension에 요청 전송
        window.postMessage({
          greeting: 'startKeywordSeoAnalysis',
          keyword: work.keyword,
          pageRange: work.pageIndex,
          existingVendorItemIds: this.existingVendorItemIds,
          isProgressiveCollection: true
        }, "*")
      })
    },

    // 작업 결과 처리
    async processWorkResult(work, rawResults) {

      const processedResults = this.processCollectedData(rawResults)

      // 키워드 정보 추가 (더 체계적으로)
      processedResults.forEach(result => {
        result.sourceKeyword = work.keyword
        result.sourcePageRange = `${work.pageIndex}`
        result.sourcePage = work.pageIndex // 숫자형 페이지 번호
        result.collectionKey = `${work.keyword}:${work.pageIndex}` // 고유 수집 키
        result.keywordIndex = work.keywordIndex // 키워드 인덱스

        // 특정 vendorItemId 추적
        if (result.vendorItemId === this.debugTargetVendorId) {
          console.log(`🔍 [추적] ${this.debugTargetVendorId} 수집됨:`, {
            keyword: work.keyword,
            page: work.pageIndex,
            productName: result.productName,
            price: result.price
          })
        }
      })

      // 중복 제거 후 전체 결과에 추가
      const existingVendorIds = new Set(this.analysisResults.map(item => item.vendorItemId))
      const newResults = processedResults.filter(result => !existingVendorIds.has(result.vendorItemId))

      // 특정 vendorItemId 중복 체크 추적
      if (processedResults.some(r => r.vendorItemId === this.debugTargetVendorId)) {
        const isExisting = existingVendorIds.has(this.debugTargetVendorId)
        const isFiltered = !newResults.some(r => r.vendorItemId === this.debugTargetVendorId)
        console.log(`🔍 [추적] ${this.debugTargetVendorId} 중복체크:`, {
          기존존재: isExisting,
          필터됨: isFiltered,
          기존analysisResults수: this.analysisResults.length,
          existingVendorItemIds포함: this.existingVendorItemIds.includes(this.debugTargetVendorId)
        })
      }

      // 모든 신규 상품을 추가 (최대 제한을 약간 초과해도 마지막 페이지 데이터는 모두 포함)
      this.analysisResults = this.analysisResults.concat(newResults)

      // 특정 vendorItemId analysisResults 추가 추적
      if (newResults.some(r => r.vendorItemId === this.debugTargetVendorId)) {
        console.log(`🔍 [추적] ${this.debugTargetVendorId} analysisResults에 추가됨, 전체 수:`, this.analysisResults.length)
      }

      // 최대 수집 제한 체크 (추가 후)
      const finalProductCount = this.analysisResults.length

      // 최대 제한을 초과했으면 수집 중단
      if (finalProductCount > this.maxProducts) {
        console.warn(`⚠️ [점진적 수집 완료] ${finalProductCount}개 상품 수집 완료 (${this.maxProducts}개 제한 초과로 수집 중단)`)

        // 수집 중단
        this.isAnalyzing = false
        this.progressMessage = `상품 수집 완료 (${finalProductCount}개, ${this.maxProducts}개 제한 초과)`
        this.progressPercent = 100

        // 사용자에게 알림
        if (this.$toast) {
          this.$toast.success(`상품 수집이 완료되었습니다 (${finalProductCount}개)`)
        }

        // 현재까지의 결과로 분석 완료 처리
        this.completeAnalysis()
        return
      }

      // existingVendorItemIds 업데이트 (실제 추가된 상품만)
      newResults.forEach(result => {
        if (result.vendorItemId && !this.existingVendorItemIds.includes(result.vendorItemId)) {
          this.existingVendorItemIds.push(result.vendorItemId)

          // 특정 vendorItemId existingVendorItemIds 추가 추적
          if (result.vendorItemId === this.debugTargetVendorId) {
            console.log(`🔍 [추적] ${this.debugTargetVendorId} existingVendorItemIds에 추가됨, 총 수:`, this.existingVendorItemIds.length)
          }
        }
      })

      // console.log(`✅ [작업 완료] ${work.keyword} ${work.pageIndex}p: ${processedResults.length}개 중 ${newResults.length}개 신규 (전체: ${this.analysisResults.length}개)`)

      // 페이지 완료 처리 (페이지 뱃지 시스템)
      this.markPageAsCompleted(work.keywordIndex, work.pageIndex)

      // 실시간 분석 업데이트
      this.performComprehensiveAnalysis()

      // 페이지별 즉시 저장 (신규 데이터만 증분 저장)
      await this.saveNewDataToIndexedDB(newResults)
    },

    // 다음 키워드 분석
    analyzeNextKeyword() {
      if (this.currentKeywordIndex >= this.autoList.length) {
        // 모든 키워드 분석 완료
        this.completeAnalysis()
        return
      }

      const currentKeyword = this.autoList[this.currentKeywordIndex]
      if (!currentKeyword) {
        console.error('❌ 유효하지 않은 키워드:', this.currentKeywordIndex)
        this.completeAnalysis()
        return
      }

      this.progressPercent = (this.currentKeywordIndex / this.autoList.length) * 100

      // 키워드 정보 표시 개선
      const keywordName = currentKeyword.keyword || currentKeyword.targetKeyword
      const keywordInfo = this.autoList.length > 1 ? `키워드 (${this.currentKeywordIndex + 1}/${this.autoList.length}) ` : '키워드 '
      this.progressPrefix = `${keywordInfo}: ${keywordName} `
      this.progressSuffix = '분석 시작...'

      // 현재 키워드의 페이지별 수집 초기화
      this.currentKeywordPageIndex = 1
      this.currentKeywordTotalPages = this.parsePageRange(currentKeyword.pageRange).endPage
      this.currentKeywordResults = []

      // 첫 번째 페이지부터 점진적 수집 시작
      this.analyzeKeywordPage(currentKeyword)
    },

    // 페이지 범위 파싱 유틸리티
    parsePageRange(pageRange) {
      // console.log(`🔧 [parsePageRange] 입력:`, pageRange, `(타입: ${typeof pageRange})`)

      let startPage = 1, endPage = 1
      if (typeof pageRange === 'string' && pageRange.includes('-')) {
        const [start, end] = pageRange.split('-').map(Number)
        startPage = start || 1
        endPage = end || start || 1
        // console.log(`🔧 [parsePageRange] 범위 파싱: "${pageRange}" → startPage: ${startPage}, endPage: ${endPage}`)
      } else {
        startPage = endPage = parseInt(pageRange) || 1
        // console.log(`🔧 [parsePageRange] 단일 페이지: "${pageRange}" → ${startPage}`)
      }

      const result = { startPage, endPage }
      // console.log(`🔧 [parsePageRange] 결과:`, result)
      return result
    },

    // 키워드의 특정 페이지 분석
    analyzeKeywordPage(currentKeyword) {
      // 분석 중이 아니면 중단
      if (!this.isAnalyzing) {
        // console.log('⚠️ 분석이 중단되어 페이지 분석을 건너뜁니다.')
        return
      }

      // 중복 실행 방지
      const requestId = `${currentKeyword.keyword}-${this.currentKeywordPageIndex}`
      if (this.currentRequestId === requestId) {
        // console.log('⚠️ 중복 요청 방지:', requestId)
        return
      }
      this.currentRequestId = requestId

      const keywordInfo = this.autoList.length > 1 ? `키워드 (${this.currentKeywordIndex + 1}/${this.autoList.length}) ` : '키워드 '
      const pageInfo = this.currentKeywordTotalPages > 1 ? ` [${this.currentKeywordPageIndex}/${this.currentKeywordTotalPages}페이지]` : ''

      this.progressPrefix = `${keywordInfo}: ${currentKeyword.keyword}${pageInfo} `
      this.progressSuffix = '분석 중...'

      // console.log(`🚀 [페이지 분석 시작] ${currentKeyword.keyword} ${this.currentKeywordPageIndex}페이지`)

      // Extension에 단일 페이지 분석 요청 전송
      window.postMessage({
        greeting: 'startKeywordSeoAnalysis',
        keyword: currentKeyword.keyword,
        pageRange: this.currentKeywordPageIndex, // 현재 페이지만
        existingVendorItemIds: this.existingVendorItemIds,
        isProgressiveCollection: true // 점진적 수집 플래그
      }, "*")
    },

    // 현재 키워드 완료 처리
    completeCurrentKeyword(currentKeyword) {
      // 인덱스 유효성 검사
      if (this.currentKeywordIndex >= this.autoList.length || !this.autoList[this.currentKeywordIndex]) {
        console.error('❌ 유효하지 않은 키워드 인덱스:', this.currentKeywordIndex, '전체 키워드 수:', this.autoList.length)
        return
      }

      // autoList의 해당 키워드에 분석 완료 표시
      this.autoList[this.currentKeywordIndex].analysisData = this.currentKeywordResults
      this.autoList[this.currentKeywordIndex].newAnalysisData = this.currentKeywordResults.filter(result => {
        const existingVendorIds = new Set(this.analysisResults.map(item => item.vendorItemId))
        return !existingVendorIds.has(result.vendorItemId)
      })
      this.autoList[this.currentKeywordIndex].collectedAt = new Date()

      console.log(`✅ [키워드 완료] ${currentKeyword.keyword}: 총 ${this.currentKeywordResults.length}개 상품 수집 완료`)

      // 다음 키워드로 진행 또는 전체 완료
      this.currentKeywordIndex++
      if (this.currentKeywordIndex < this.autoList.length) {
        // 다음 키워드 분석 시작
        setTimeout(() => {
          // 분석이 중단되었으면 실행하지 않음
          if (this.isAnalyzing) {
            this.analyzeNextKeyword()
          }
        }, 1000)
      } else {
        // 모든 키워드 분석 완료
        this.completeAnalysis()
      }
    },

    // 전체 분석 완료 처리
    completeAnalysis() {
      this.isAnalyzing = false
      this.isProcessingQueue = false
      this.progressPercent = 100

      // 완료 메시지
      const keywordCount = this.autoList.length
      const productCount = this.analysisResults.length
      const keywordInfo = keywordCount > 1 ? `${keywordCount}개 키워드` : '키워드'
      this.progressPrefix = ''
      this.progressSuffix = `${keywordInfo} 분석 완료! (총 ${productCount}개 상품 수집)`

      // console.log('🎉 [전체 분석 완료] 총', this.analysisResults.length, '개 상품 수집 완료')

      // 신규 키워드 수집인 경우 원래 autoList 복원
      if (this.originalAutoList) {
        // 분석 결과를 원래 autoList에 반영
        this.autoList.forEach(analyzedItem => {
          if (analyzedItem.originalIndex !== undefined) {
            this.originalAutoList[analyzedItem.originalIndex] = {
              ...analyzedItem,
              originalIndex: undefined // 임시 속성 제거
            }
          }
        })
        this.autoList = this.originalAutoList
        this.originalAutoList = null
      }

      // 분석 결과 기반으로 키워드별 페이지 현황 동적 업데이트
      this.updatePageStatusFromAnalysisResults()

      // 통합 분석 결과 생성
      this.generateIntegratedAnalysis()

      // 전체 분석 결과를 IndexedDB에 저장
      this.saveAllAnalysisResults()
    },

    // 통합 분석 결과 생성
    generateIntegratedAnalysis() {
      // console.log('📊 [통합 분석] 시작 - 상품 수:', this.analysisResults.length)

      if (this.analysisResults.length === 0) {
        // console.log('ℹ️ [통합 분석] 분석 결과가 없어 통합 분석을 건너뜁니다.')
        return
      }

      // console.log('📊 [통합 분석] 상품 데이터 샘플:', this.analysisResults.slice(0, 2))


      // 2단계: popup.js와 동일한 종합 분석 수행 (미리 계산된 데이터 사용)
      this.performComprehensiveAnalysis()

      // console.log('✅ [통합 분석] 완료')
    },


    async resetData() {
      // 확인 창 표시
      if (!confirm('현재 키워드그룹의 모든 데이터를 완전히 초기화하시겠습니까?\n\n⚠️ 다음 데이터가 모두 삭제됩니다:\n- 수집된 모든 상품 데이터\n- 상품링크로 수집한 키워드 목록\n- 가격 경쟁력 분석 결과\n- 모든 분석 차트 및 통계\n- IndexedDB의 관련 데이터\n\n※ 키워드 설정은 유지되지만 모두 대기 상태로 변경됩니다')) {
        return
      }

      try {
        console.log('🔄 [완전 초기화] 시작...')

        // 1. IndexedDB에서 현재 그룹의 모든 데이터 삭제
        await this.clearIndexedDBData()
        console.log('✅ [완전 초기화] IndexedDB 데이터 삭제 완료')

        // 2. 상품 분석 관련 데이터 초기화
        this.analysisResults = []
        this.summaryData = []
        this.progressPercent = 0
        this.progressPrefix = ''
        this.progressSuffix = ''
        this.currentKeywordIndex = 0

        // 3. 상품링크로 수집한 키워드 목록 완전 삭제
        this.productList = []
        this.targetProductLink = ''
        this.myVendorItemId = null
        console.log('✅ [완전 초기화] 상품링크 키워드 목록 삭제 완료')

        // 4. 가격 경쟁력 분석 결과 완전 삭제
        this.competitivenessResults = []
        this.priceSuggestions = []
        console.log('✅ [완전 초기화] 가격 경쟁력 분석 결과 삭제 완료')

        // 5. autoList의 분석 데이터 초기화 (키워드는 유지, 상태만 대기로 변경)
        this.autoList.forEach(item => {
          item.analysisData = null
          item.newAnalysisData = null
          item.collectedAt = null
          // Vue 반응성 보장을 위해 $set 사용
          this.$set(item, 'completedPages', [])
          // 키워드와 페이지 범위는 유지
        })

        // 6. 분석 데이터 초기화
        this.resetAnalysisData()

        // 7. 차트 옵션 완전 초기화
        this.rpiCompetitivenessChartOptions = {}
        this.adCompetitivenessChartOptions = {}
        this.priceRangeSalesChartOptions = {}
        this.dailyTrafficChartOptions = {}
        this.impressionChartOptions = {}
        this.priceVsCvrChartOptions = {}

        // 8. 키워드 설정 저장 (completedPages 초기화 반영)
        await this.saveCurrentGroupKeywords()

        // 9. IndexedDB에서 가격 경쟁력 분석 관련 데이터도 삭제
        await this.clearCompetitivenessAnalysisData()

        console.log('✅ [완전 초기화] 모든 데이터 초기화 완료')
        alert('모든 데이터가 성공적으로 초기화되었습니다.\n\n- 상품 분석 데이터 삭제됨\n- 상품링크 키워드 목록 삭제됨\n- 가격 경쟁력 분석 결과 삭제됨\n- 관련 IndexedDB 데이터 삭제됨')
      } catch (error) {
        console.error('❌ [완전 초기화] 실패:', error)
        alert('데이터 초기화 중 오류가 발생했습니다.')
      }
    },

    // 가격 경쟁력 분석 관련 IndexedDB 데이터 삭제
    async clearCompetitivenessAnalysisData() {
      try {
        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'

        // IndexedDB 열기
        const request = indexedDB.open(scopedDbName(dbName), 1)

        return new Promise((resolve) => {
          request.onerror = () => {
            console.log('IndexedDB 열기 실패 (DB가 없을 수 있음)')
            resolve() // 에러가 아닌 정상 처리로 간주
          }

          request.onsuccess = (event) => {
            const db = event.target.result

            try {
              const transaction = db.transaction([storeName], 'readwrite')
              const store = transaction.objectStore(storeName)

              // 현재 그룹의 가격 경쟁력 분석 데이터 삭제
              const deleteRequest = store.delete(this.activeProductGroup)

              deleteRequest.onsuccess = () => {
                console.log('✅ [IndexedDB] 가격 경쟁력 분석 데이터 삭제 완료:', this.activeProductGroup)
                db.close()
                resolve()
              }

              deleteRequest.onerror = () => {
                console.log('⚠️ [IndexedDB] 가격 경쟁력 분석 데이터 삭제 실패 (데이터가 없을 수 있음)')
                db.close()
                resolve() // 에러가 아닌 정상 처리로 간주
              }

            } catch (error) {
              console.log('⚠️ [IndexedDB] 트랜잭션 생성 실패:', error)
              db.close()
              resolve() // 에러가 아닌 정상 처리로 간주
            }
          }

          request.onupgradeneeded = (event) => {
            // DB가 없어서 새로 생성되는 경우 - 삭제할 데이터가 없으므로 정상 처리
            const db = event.target.result
            db.close()
            resolve()
          }
        })

      } catch (error) {
        console.error('❌ [IndexedDB] 가격 경쟁력 분석 데이터 삭제 중 오류:', error)
        // 에러가 발생해도 초기화는 계속 진행
      }
    },


    // IndexedDB 내 현재 그룹 데이터 비움 (이 화면의 저장소만 대상으로 함)
    async clearIndexedDBData() {
      try {
        // productCompetitiveAnalysis DB에서 현재 활성 그룹 데이터만 삭제
        await this.clearCompetitivenessAnalysisData()
        console.log('✅ [IndexedDB] 현재 그룹 분석 데이터 초기화 완료:', this.activeProductGroup)
      } catch (e) {
        console.error('❌ [IndexedDB] 현재 그룹 데이터 초기화 실패:', e)
      }
    },

    // 분석 관련 메모리/상태를 초기값으로 되돌림 (UI 반응 포함)
    resetAnalysisData() {
      try {
        // 핵심 배열/요약 초기화
        this.enrichedAnalysisResults = []
        this.validProductsWithOutliers = []
        this.validProductsWithoutOutliers = []
        this.top300ByPriceCvr = []
        this.top20ByImpressions = []
        this.top20ByRPI = []
        this.summaryData = []
        this.summaryMetrics = {
          medianCTR: '0.00',
          medianCVR: '0.00',
          totalProducts: 0,
          totalImpressions: 0,
          totalClicks: 0,
          totalSales: 0,
          totalAdImpressions: 0,
          totalOrganicImpressions: 0
        }

        // 추천/카테고리/키워드 집계 초기화
        this.categoryAnalysis = []
        this.bestProducts = []
        this.integratedKeywords = []
        this.bestCTRProduct = null
        this.bestCVRProduct = null
        this.bestPriceCvrProduct = null
        this.bestAttentionProduct = null
        this.bestRPIProduct = null

        // 가격 제안/드래그 상태 초기화
        this.manualSuggestion = { full: null, near: null }
        this.dragging = { active: false, type: null, rect: null, prices: [], min: 0, max: 0 }

        // 광고/가격-전환 분석 데이터 초기화
        this.adCompetitivenessData = []
        this.priceVsCvrData = []
        this.rpiTop20Products = []

        // 진행 상태/작업큐 초기화
        this.progressPercent = 0
        this.progressPrefix = ''
        this.progressSuffix = ''
        this.currentKeywordIndex = 0
        this.currentKeywordPageIndex = 1
        this.currentKeywordTotalPages = 1
        this.currentKeywordResults = []
        this.workQueue = []

        // 메모리 및 차트 옵션/표시 상태 초기화
        this.cleanupMemory && this.cleanupMemory()

        // 부가 상태 초기화
        this.memoryUsage = { totalProducts: 0, estimatedSize: 0, lastUpdated: null }
        console.log('✅ [분석 상태 초기화] 완료')
      } catch (e) {
        console.warn('⚠️ [분석 상태 초기화] 중 경고:', e)
      }
    },

    // autoList 저장 전 필드 정리 (무거운 필드 제거, 범위/완료페이지 정규화)
    cleanAutoListFields() {
      try {
        if (!Array.isArray(this.autoList)) {
          this.autoList = []
          return
        }
        this.autoList = this.autoList.map((raw) => {
          const keyword = raw.keyword || raw.targetKeyword || ''
          // pageRange 우선, 없으면 start-end 생성
          let start = parseInt(raw.startPage) || 1
          let end = parseInt(raw.endPage) || start
          if (typeof raw.pageRange === 'string' && raw.pageRange.includes('-')) {
            const parsed = this.parsePageRange(raw.pageRange)
            start = parsed.startPage
            end = parsed.endPage
          }
          const pageRange = `${start}-${end}`
          const completedPages = Array.isArray(raw.completedPages)
            ? [...new Set(raw.completedPages.filter(n => Number.isFinite(n)))].sort((a,b) => a-b)
            : []
          return {
            keyword,
            pageRange,
            startPage: start,
            endPage: end,
            completedPages,
            source: raw.source || '직접입력',
            collectedAt: raw.collectedAt || null,
            enabled: raw.enabled !== false
          }
        })
      } catch (e) {
        console.warn('⚠️ [autoList 정리] 중 경고:', e)
      }
    },

    // 상품설정 모달 열기
    openProductModal() {
      this.showProductModal = true
      this.focusModalInput()
    },

    // 상품링크에서 vendorItemId 추출
    extractVendorItemId(productLink) {
      try {
        // 쿠팡 상품링크에서 vendorItemId 추출
        // 예: https://www.coupang.com/vp/products/123456789?vendorItemId=987654321
        const url = new URL(productLink)
        const vendorItemId = url.searchParams.get('vendorItemId')

        if (!vendorItemId) {
          // URL 파라미터에 없으면 경로에서 추출 시도
          const pathMatch = productLink.match(/vendorItemId[=](\d+)/)
          if (pathMatch) {
            return pathMatch[1]
          }

          console.warn('⚠️ vendorItemId를 찾을 수 없습니다:', productLink)
          return null
        }

        return vendorItemId
      } catch (error) {
        console.error('❌ vendorItemId 추출 실패:', error)
        return null
      }
    },

    // 상품링크에서 트래픽 키워드 수집
    async collectProductTrafficKeywords() {
      if (!this.targetProductLink.trim()) {
        alert('상품링크를 입력해주세요.')
        return
      }

      // 쿠팡 상품링크 유효성 검사
      if (!this.targetProductLink.includes('coupang.com/vp/products/')) {
        alert('올바른 쿠팡 상품링크를 입력해주세요.')
        return
      }

      // vendorItemId 추출 및 저장
      this.myVendorItemId = this.extractVendorItemId(this.targetProductLink)
      if (!this.myVendorItemId) {
        alert('상품링크에서 vendorItemId를 찾을 수 없습니다. 올바른 쿠팡 상품링크를 입력해주세요.')
        return
      }

      console.log('✅ 내 상품 vendorItemId:', this.myVendorItemId)

      try {
        this.isAnalyzing = true
        this.progressPrefix = ''
        this.progressSuffix = '상품의 트래픽 키워드를 수집 중입니다...'
        this.progressPercent = 10

        // 확장 프로그램에 트래픽 키워드 수집 요청
        window.postMessage({
          greeting: 'getProductTrafficKeywords',
          productLink: this.targetProductLink,
          days: 30, // 30일 데이터
          limit: 10 // 10개 키워드 제한
        }, "*")

      } catch (error) {
        console.error('❌ 트래픽 키워드 수집 실패:', error)
        alert('트래픽 키워드 수집 중 오류가 발생했습니다.')
        this.isAnalyzing = false
      }
    },

    // 네이버데이터랩 섹션 토글
    toggleDataLabSection() {
      if (this.showDataLabSection) {
        // 이미 열려있으면 닫기
        this.showDataLabSection = false
      } else {
        // 닫혀있으면 열고 다른 섹션은 닫기
        this.showDataLabSection = true
        this.showExcelUploadSection = false
      }
    },

    // 키워드 업로드 섹션 토글
    toggleExcelUploadSection() {
      if (this.showExcelUploadSection) {
        // 이미 열려있으면 닫기
        this.showExcelUploadSection = false
      } else {
        // 닫혀있으면 열고 다른 섹션은 닫기
        this.showExcelUploadSection = true
        this.showDataLabSection = false
      }
    },

    // 키워드설정 모달이 열릴 때 입력 필드에 포커스
    focusModalInput() {
      this.$nextTick(() => {
        if (this.$refs.modalKeywordInput) {
          this.$refs.modalKeywordInput.focus()
        }
      })
    },



    isPageCompleted(item, page) {
      // 특정 키워드 아이템의 특정 페이지가 수집 완료되었는지 확인
      return item.completedPages && item.completedPages.includes(page)
    },

    // 키워드 아이템의 페이지 번호 배열 생성
    getPageNumbers(item) {
      if (!item.startPage || !item.endPage) return []

      const pages = []
      for (let i = item.startPage; i <= item.endPage; i++) {
        pages.push(i)
      }
      return pages
    },

    // 키워드 아이템의 모든 페이지가 완료되었는지 확인
    isAllPagesCompleted(item) {
      const allPages = this.getPageNumbers(item)
      if (allPages.length === 0) return false

      const completedPages = item.completedPages || []
      return allPages.every(page => completedPages.includes(page))
    },

    // 페이지를 완료로 표시
    markPageAsCompleted(keywordIndex, pageIndex) {
      if (keywordIndex >= 0 && keywordIndex < this.autoList.length) {
        const keyword = this.autoList[keywordIndex]
        if (!keyword.completedPages) {
          keyword.completedPages = []
        }
        if (!keyword.completedPages.includes(pageIndex)) {
          keyword.completedPages.push(pageIndex)
          keyword.completedPages.sort((a, b) => a - b)
          // console.log(`✅ [페이지 완료] ${keyword.keyword} ${pageIndex}페이지 완료 표시`)
        }
      }
    },

    // 특정 키워드의 특정 페이지 데이터를 analysisResults에서 제거 (개선된 버전)
    removePageDataFromAnalysisResults(keyword, pagesToRemove) {
      // const initialCount = this.analysisResults.length

      // 제거할 collectionKey 목록 생성
      const keysToRemove = pagesToRemove.map(page => `${keyword}:${page}`)

      this.analysisResults = this.analysisResults.filter(result => {
        // collectionKey를 우선 사용하고, 없으면 기존 방식 사용 (하위 호환성)
        if (result.collectionKey) {
          return !keysToRemove.includes(result.collectionKey)
        } else {
          // 기존 방식 (하위 호환성)
          if (result.sourceKeyword === keyword && pagesToRemove.includes(parseInt(result.sourcePageRange))) {
            return false // 제거
          }
          return true // 유지
        }
      })

      // const removedCount = initialCount - this.analysisResults.length
      // console.log(`🗑️ [페이지 데이터 제거] ${keyword} 키워드의 페이지 [${pagesToRemove.join(', ')}] 데이터 ${removedCount}개 제거`)
      // console.log(`🔑 [제거된 키] ${keysToRemove.join(', ')}`)

      // IndexedDB에서도 해당 데이터 제거
      this.removeDataFromIndexedDB(keysToRemove)


      // 분석 데이터 재계산
      this.performComprehensiveAnalysis()
    },

    // IndexedDB에서 특정 collectionKey 데이터 제거 (상품경쟁력분석 전용 DB에만 반영)
    async removeDataFromIndexedDB(keysToRemove) {
      try {
        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'
        const request = indexedDB.open(scopedDbName(dbName), 1)
        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'groupName' })
          }
        }
        const db = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })
        const tx = db.transaction([storeName], 'readwrite')
        const store = tx.objectStore(storeName)
        const prev = await new Promise((resolve, reject) => {
          const getReq = store.get(this.activeProductGroup)
          getReq.onsuccess = () => resolve(getReq.result || { groupName: this.activeProductGroup })
          getReq.onerror = () => reject(getReq.error)
        })
        // 메모리에서 이미 제거된 this.analysisResults를 그대로 반영
        const updated = { ...prev, analysisResults: this.analysisResults }
        await new Promise((resolve, reject) => {
          const putReq = store.put(updated)
          putReq.onsuccess = () => resolve()
          putReq.onerror = () => reject(putReq.error)
        })
        tx.oncomplete = () => db.close()
        console.log('💾 [PCA] 부분 삭제 반영', { db: dbName, store: storeName, group: this.activeProductGroup, removedKeys: (keysToRemove||[]).length })
      } catch (error) {
        console.error('IndexedDB 데이터 제거 실패:', error)
      }
    },

    // IndexedDB에서 특정 키워드의 모든 데이터 제거 (상품경쟁력분석 전용 DB에만 반영)
    async removeKeywordDataFromIndexedDB(keywordName) {
      try {
        // 메모리상 analysisResults 필터링
        const filtered = (this.analysisResults || []).filter(result => {
          if (result.collectionKey) return !result.collectionKey.startsWith(`${keywordName}:`)
          return result.sourceKeyword !== keywordName
        })
        this.analysisResults = filtered

        // 저장소 반영
        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'
        const request = indexedDB.open(scopedDbName(dbName), 1)
        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'groupName' })
          }
        }
        const db = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })
        const tx = db.transaction([storeName], 'readwrite')
        const store = tx.objectStore(storeName)
        const prev = await new Promise((resolve, reject) => {
          const getReq = store.get(this.activeProductGroup)
          getReq.onsuccess = () => resolve(getReq.result || { groupName: this.activeProductGroup })
          getReq.onerror = () => reject(getReq.error)
        })
        const updated = { ...prev, analysisResults: this.analysisResults }
        await new Promise((resolve, reject) => {
          const putReq = store.put(updated)
          putReq.onsuccess = () => resolve()
          putReq.onerror = () => reject(putReq.error)
        })
        tx.oncomplete = () => db.close()
        console.log('💾 [PCA] 키워드 데이터 삭제 반영', { db: dbName, store: storeName, group: this.activeProductGroup, keyword: keywordName })
      } catch (error) {
        console.error('IndexedDB 키워드 데이터 제거 실패:', error)
      }
    },

    // 상품 페이지 열기 (popup.js와 동일)
    openProductPage(product) {
      if (product.productId && product.itemId && product.vendorItemId) {
        const productUrl = `https://www.coupang.com/vp/products/${product.productId}?itemId=${product.itemId}&vendorItemId=${product.vendorItemId}`
        window.open(productUrl, '_blank')
      } else {
        console.warn('상품 링크 정보가 부족합니다:', product)
      }
    },

    // TOP3 임의 제외/복원
    async excludeTopProduct(product) {
      try {
        const id = product && product.vendorItemId
        if (!id) return
        if (!Array.isArray(this.userExcludedVendorItemIds)) this.userExcludedVendorItemIds = []
        if (!Array.isArray(this.userExcludedList)) this.userExcludedList = []
        if (!this.userExcludedVendorItemIds.includes(id)) {
          this.userExcludedVendorItemIds.push(id)
        }
        if (!this.userExcludedList.find(x => x.vendorItemId === id)) {
          this.userExcludedList.push({
            vendorItemId: id,
            productName: product.productName || '-',
            price: product.price || product.salePrice || 0,
            imgUrl: product.imgUrl || product.imagePath || '',
            rpi: Number(product.overall || 0)
          })
        }
        console.log('[UserOutlier] exclude', id, product.productName)
        // 스피너 on 후 렌더링 기회를 준 뒤 재계산
        this.isUserOutlierProcessing = true
        this.processingVendorItemId = id
        this.$nextTick(() => { setTimeout(() => {
        try {
          this.performComprehensiveAnalysis()
        } finally {
          this.isUserOutlierProcessing = false
          this.processingVendorItemId = null
          this.processingAllUserExcluded = false
        }
        }, 0) })
      } catch (e) {
        console.error('[UserOutlier] exclude error', e)
        this.isUserOutlierProcessing = false
        this.processingVendorItemId = null
        this.processingAllUserExcluded = false
      }
    },

    async restoreExcludedProduct(vendorItemId) {
      try {
        if (!Array.isArray(this.userExcludedVendorItemIds)) this.userExcludedVendorItemIds = []
        if (!Array.isArray(this.userExcludedList)) this.userExcludedList = []
        this.userExcludedVendorItemIds = this.userExcludedVendorItemIds.filter(id => id !== vendorItemId)
        this.userExcludedList = this.userExcludedList.filter(x => x.vendorItemId !== vendorItemId)
        console.log('[UserOutlier] restore', vendorItemId)
        this.isUserOutlierProcessing = true
        this.processingVendorItemId = vendorItemId
        this.$nextTick(() => { setTimeout(() => {
        try {
          this.performComprehensiveAnalysis()
        } finally {
          this.isUserOutlierProcessing = false
          this.processingVendorItemId = null
          this.processingAllUserExcluded = false
        }
        }, 0) })
      } catch (e) {
        console.error('[UserOutlier] restore error', e)
        this.isUserOutlierProcessing = false
        this.processingVendorItemId = null
        this.processingAllUserExcluded = false
      }
    },

    async restoreAllExcluded() {
      try {
        this.userExcludedVendorItemIds = []
        this.userExcludedList = []
        console.log('[UserOutlier] restore all')
        this.isUserOutlierProcessing = true
        this.processingAllUserExcluded = true
        this.processingVendorItemId = null
        this.$nextTick(() => { setTimeout(() => {
        try {
          this.performComprehensiveAnalysis()
        } finally {
          this.isUserOutlierProcessing = false
          this.processingAllUserExcluded = false
          this.processingVendorItemId = null
        }
        }, 0) })
      } catch (e) {
        console.error('[UserOutlier] restoreAll error', e)
        this.isUserOutlierProcessing = false
        this.processingAllUserExcluded = false
        this.processingVendorItemId = null
      }
    },


    // 수집된 원본 데이터를 popup 로직처럼 처리 (필드명 그대로 사용)


    processCollectedData(rawData) {


      if (!rawData || !Array.isArray(rawData)) {
        return []
      }

      // vendorItemId가 없는 상품 필터링
      const validData = rawData.filter(item => {
        if (!item || !item.vendorItemId) {
          console.warn('⚠️ vendorItemId가 없는 상품 데이터 필터링:', item)
          return false
        }
        return true
      })

      return validData.map(item => {
        // 원본 데이터 구조 그대로 사용하되, popup.js와 동일한 계산 로직 적용
        const processedItem = {
          ...item, // 원본 데이터 모든 필드 유지

          // popup.js와 동일한 계산 필드들 추가
          ctr: this.calculateCTRFromRawData(item),
          cvr: this.calculateCVRFromRawData(item),
          overall: this.calculateRPIFromRawData(item),

          // 노출수/클릭수 계산 (trafficKeyword에서)
          impressions: this.calculateImpressionsFromRawData(item),
          clicks: this.calculateClicksFromRawData(item),

          // 광고 비율 계산
          adRatio: this.calculateAdRatioFromRawData(item),

          // 경쟁도/어필도/주목도 계산
          competitiveness: this.calculateCompetitivenessFromRawData(item),
          appeal: this.calculateAppealFromRawData(item),
          attentionScore: this.calculateAttentionScoreFromRawData(item)
        }

        return processedItem
      })
    },

    // 원본 데이터에서 노출수 계산
    calculateImpressionsFromRawData(item) {
      let totalImpressions = 0
      if (item.trafficKeyword && item.trafficKeyword.length > 0) {
        item.trafficKeyword.forEach(tk => {
          totalImpressions += tk.keywordMetric?.impression || 0
        })
      }
      return totalImpressions
    },

    // 원본 데이터에서 클릭수 계산
    calculateClicksFromRawData(item) {
      let totalClicks = 0
      if (item.trafficKeyword && item.trafficKeyword.length > 0) {
        item.trafficKeyword.forEach(tk => {
          totalClicks += tk.keywordMetric?.srpClick || 0
        })
      }
      return totalClicks
    },

    // 원본 데이터에서 CTR 계산 (popup.js와 정확히 동일한 로직)
    calculateCTRFromRawData(item) {
      const totalImpressions = this.calculateImpressionsFromRawData(item)
      const totalClicks = this.calculateClicksFromRawData(item)

      // popup.js: productCTR = productImpressions > 0 ? (productClicks / productImpressions * 100) : 0
      const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0
      return parseFloat(ctr.toFixed(2))
    },

    // 원본 데이터에서 CVR 계산 (popup.js와 정확히 동일한 로직)
    calculateCVRFromRawData(item) {
      // popup.js: productCVR = pvLast28Day > 0 ? (salesLast28d / pvLast28Day * 100) : 0
      const salesLast28d = item.salesLast28d || 0
      const pvLast28Day = item.pvLast28Day || 0

      const cvr = pvLast28Day > 0 ? (salesLast28d / pvLast28Day * 100) : 0
      return parseFloat(cvr.toFixed(2))
    },

    // 원본 데이터에서 RPI 계산 (popup.js와 정확히 동일한 로직)
    calculateRPIFromRawData(item) {
      const ctr = this.calculateCTRFromRawData(item)
      const cvr = this.calculateCVRFromRawData(item)
      const price = item.price || 0

      // popup.js와 동일: RPI = CTR(%) × CVR(%) × 판매가 / 10000
      const rpi = ctr * cvr * price / 10000
      return parseFloat(rpi.toFixed(2))
    },

    // 원본 데이터에서 광고 비율 계산
    calculateAdRatioFromRawData(item) {
      let totalAdImpression = 0
      let totalKeywordImpression = 0

      if (item.trafficKeyword && item.trafficKeyword.length > 0) {
        item.trafficKeyword.forEach(tk => {
          totalAdImpression += tk.keywordMetric?.adImpression || 0
          totalKeywordImpression += tk.keywordMetric?.impression || 0
        })
      }

      return totalKeywordImpression > 0 ?
        parseFloat(((totalAdImpression / totalKeywordImpression) * 100).toFixed(2)) : 0
    },

    // 원본 데이터에서 경쟁도 계산
    calculateCompetitivenessFromRawData(item) {
      const impressions = this.calculateImpressionsFromRawData(item)
      const adRatio = this.calculateAdRatioFromRawData(item)

      // popup.js 로직: 노출수가 높고 광고 비율이 높을수록 경쟁도 높음
      let competitiveness = 0
      if (impressions > 10000) competitiveness += 30
      else if (impressions > 5000) competitiveness += 20
      else if (impressions > 1000) competitiveness += 10

      if (adRatio > 50) competitiveness += 30
      else if (adRatio > 30) competitiveness += 20
      else if (adRatio > 10) competitiveness += 10

      return Math.min(competitiveness, 100)
    },

    // 원본 데이터에서 어필도 계산
    calculateAppealFromRawData(item) {
      const ctr = this.calculateCTRFromRawData(item)
      const rating = parseFloat(item.rating) || 0
      const ratingCount = parseInt(item.ratingcount) || 0

      // popup.js 로직: CTR, 평점, 리뷰수 기반
      let appeal = 0
      if (ctr > 5) appeal += 30
      else if (ctr > 3) appeal += 20
      else if (ctr > 1) appeal += 10

      if (rating >= 4.5) appeal += 25
      else if (rating >= 4.0) appeal += 15
      else if (rating >= 3.5) appeal += 10

      if (ratingCount > 1000) appeal += 25
      else if (ratingCount > 500) appeal += 15
      else if (ratingCount > 100) appeal += 10

      return Math.min(appeal, 100)
    },

    // 원본 데이터에서 시선집중도 계산 (popup.js와 정확히 동일)
    calculateAttentionScoreFromRawData(item) {
      // popup.js: 시선집중도 계산 (dailyTraffic에서 glanceView/impression 비율)
      let attentionScore = 0

      if (item.dailyTraffic && typeof item.dailyTraffic === 'object') {
        let totalGlanceView = 0
        let totalDailyImpression = 0

        Object.entries(item.dailyTraffic).forEach(([, dailyDataArray]) => {
          if (Array.isArray(dailyDataArray) && dailyDataArray.length > 0) {
            const dailyData = dailyDataArray[0] // 첫 번째 요소 사용
            const impression = dailyData.impression || 0
            const glanceView = dailyData.glanceView || 0

            totalGlanceView += glanceView
            totalDailyImpression += impression
          }
        })

        if (totalDailyImpression > 0) {
          attentionScore = (totalGlanceView / totalDailyImpression) * 100
        }
      }

      return attentionScore ? attentionScore.toFixed(2) : 0
    },



    // 경쟁력 계산 (원본 데이터 함수 사용)
    calculateCompetitiveness(item) {
      return this.calculateCompetitivenessFromRawData(item)
    },

    // 매력도 계산 (원본 데이터 함수 사용)
    calculateAppeal(item) {
      return this.calculateAppealFromRawData(item)
    },

    // 주목도 계산 (원본 데이터 함수 사용)
    calculateAttentionScore(item) {
      return this.calculateAttentionScoreFromRawData(item)
    },

    // 클릭수 계산 (원본 데이터 함수 사용)
    calculateClicks(item) {
      return this.calculateClicksFromRawData(item)
    },

    // 노출수 계산 (원본 데이터 함수 사용)
    calculateImpressions(item) {
      return this.calculateImpressionsFromRawData(item)
    },

    // 광고 비율 계산 (원본 데이터 함수 사용)
    calculateAdRatio(item) {
      return this.calculateAdRatioFromRawData(item)
    },

    async handleExtensionMessage(event) {
      const message = event.data
      // console.log('📨 [handleExtensionMessage] 메시지 수신:', message)

      // 큐 시스템 사용 중이면 Progress 메시지와 에러 메시지만 허용
      if (this.isProcessingQueue && message.res !== 'keywordSeoAnalysisProgress' && message.res !== 'keywordSeoAnalysisError') {
        // console.log('⚠️ [handleExtensionMessage] 큐 처리 중이므로 메시지를 무시합니다:', message.res)
        return
      }

      // 연관키워드, 네이버데이터랩 수집은 분석 상태와 관계없이 허용
      // 에러 메시지도 항상 허용해야 함
      const allowedMessages = [
        'adkeywords',
        'keywordRankComplete',
        'keywordSeoAnalysisError',
        'productTrafficKeywordsComplete',
        'productTrafficKeywordsError',
        'searchKeywordProductsComplete',
        'searchKeywordProductsError',
        'getProductDetailComplete',
        'getProductDetailError',
        'getProductTrafficInfoComplete',
        'getProductTrafficInfoError'
      ]

      // 분석이 진행 중이 아니면 허용된 메시지 외에는 무시
      if (!this.isAnalyzing && !allowedMessages.includes(message.res)) {
        // console.log('⚠️ [handleExtensionMessage] 분석이 진행 중이 아니므로 메시지를 무시합니다:', message.res)
        return
      }

      // Extension에서 오는 모든 메시지 로그
      // console.log('🔔 Extension 메시지:', message)

      // console.log('📨 Extension 메시지:', message.res, message.message)

      if (message.res === 'keywordSeoAnalysisProgress') {
        // 개별 키워드 분석 진행률은 전체 진행률에 반영
        const keywordProgress = message.percent || 0
        const baseProgress = (this.currentKeywordIndex / this.autoList.length) * 100
        const currentKeywordWeight = 100 / this.autoList.length
        this.progressPercent = baseProgress + (keywordProgress * currentKeywordWeight / 100)

        // Extension 메시지로 progressSuffix만 교체
        this.progressSuffix = message.message || '상품 분석 중...'
      }

      if (message.res === 'keywordSeoAnalysisError') {
        console.error('키워드 분석 오류:', message.error)
        // console.log('🔍 [디버그] 받은 메시지:', message)

        // 카테고리 에러인 경우 특별 처리
        if (message.error === 'CATEGORY_NOT_UNDER_SELLER') {
          // console.log('🚨 [카테고리 에러] 수집 중단 및 알림 표시')
          // 수집 중단
          this.isAnalyzing = false
          // 큐 초기화
          this.workQueue = []
          // 알림 표시
          alert('현재 수집하는 카테고리는 판매자 상품에 등록되지 않은 카테고리 입니다.')
          return
        }

        // 쿠팡 API 차단 분류 (Preflight + 연속 실패 감지)
        const classification = this.classifyCollectionError(message, this.currentKeywordPageIndex || 1)
        if (classification.action === 'hard') {
          console.error('🚨 [쿠팡 API 차단]', classification.message)
          this.isAnalyzing = false
          this.workQueue = []
          alert(classification.message)
          return
        }

        // 일반 에러의 경우 현재 키워드를 실패로 표시하고 다음으로 진행
        if (this.currentKeywordIndex < this.autoList.length) {
          this.autoList[this.currentKeywordIndex].analysisData = 'error'
          this.autoList[this.currentKeywordIndex].collectedAt = new Date()
        }

        this.currentKeywordIndex++

        // 다음 키워드로 진행 (오류가 있어도 계속)
        setTimeout(() => {
          // 분석이 중단되었으면 실행하지 않음
          if (this.isAnalyzing) {
            this.analyzeNextKeyword()
          }
        }, 1000)
        return
      }

      if (message.res === 'keywordSeoAnalysisComplete') {
        // 수집 1건 이상 성공 표시 (Preflight 플래그 해제 + 연속 실패 카운터 리셋)
        this.markCollectionSuccess()

        // 중복 메시지 방지 (키워드 인덱스 + 페이지 인덱스로 고유 ID 생성)
        const messageId = `${this.currentKeywordIndex}-${this.currentKeywordPageIndex}`
        if (this.processedMessageIds.has(messageId)) {
          // console.log('⚠️ 중복 메시지 무시:', messageId)
          return
        }
        this.processedMessageIds.add(messageId)

        // 현재 요청 ID 초기화 (다음 요청 허용)
        this.currentRequestId = null

        // 인덱스 유효성 검사
        if (this.currentKeywordIndex >= this.autoList.length) {
          console.error('❌ 유효하지 않은 키워드 인덱스:', this.currentKeywordIndex, '전체 키워드 수:', this.autoList.length)
          return
        }

        // 현재 키워드의 분석 결과를 저장
        const currentKeyword = this.autoList[this.currentKeywordIndex]
        if (!currentKeyword) {
          console.error('❌ 현재 키워드가 존재하지 않습니다:', this.currentKeywordIndex)
          return
        }

        const rawKeywordResults = message.data || [] // 원본 데이터

        // 원본 데이터를 popup 로직으로 처리
        const processedKeywordResults = this.processCollectedData(rawKeywordResults)

        // 현재 키워드 정보를 결과에 추가 (더 체계적으로)
        const keywordName = currentKeyword.keyword || currentKeyword.targetKeyword || ''
        processedKeywordResults.forEach(result => {
          result.sourceKeyword = keywordName
          result.sourcePageRange = `${this.currentKeywordPageIndex}` // 현재 페이지 정보
          result.sourcePage = this.currentKeywordPageIndex // 숫자형 페이지 번호
          result.collectionKey = `${keywordName}:${this.currentKeywordPageIndex}` // 고유 수집 키
          result.keywordIndex = this.currentKeywordIndex // 키워드 인덱스
        })

        // 현재 키워드의 페이지별 결과를 누적
        this.currentKeywordResults = this.currentKeywordResults.concat(processedKeywordResults)

        // 강화된 중복 제거 로직 (vendorItemId 기준)
        const existingVendorIds = new Set(this.analysisResults.map(item => item.vendorItemId).filter(id => id))
        const newResults = processedKeywordResults.filter(result => {
          // vendorItemId가 없는 상품은 제외
          if (!result.vendorItemId) {
            console.warn('⚠️ vendorItemId가 없는 상품 제외:', result.productName)
            return false
          }
          // 이미 존재하는 vendorItemId는 제외
          return !existingVendorIds.has(result.vendorItemId)
        })

        // console.log(`🔍 [페이지별 수집] ${this.currentKeywordPageIndex}/${this.currentKeywordTotalPages}페이지: ${rawKeywordResults.length}개 중 ${newResults.length}개 신규 상품 추가`)

        // 모든 신규 상품을 추가 (최대 제한을 약간 초과해도 마지막 페이지 데이터는 모두 포함)
        this.analysisResults = this.analysisResults.concat(newResults)

        // 최대 수집 제한 체크 (추가 후)
        const finalProductCount = this.analysisResults.length

        // 최대 제한을 초과했으면 수집 중단
        if (finalProductCount > this.maxProducts) {
          console.warn(`⚠️ [상품 수집 완료] ${finalProductCount}개 상품 수집 완료 (${this.maxProducts}개 제한 초과로 수집 중단)`)

          // 수집 중단
          this.isAnalyzing = false
          this.progressMessage = `상품 수집 완료 (${finalProductCount}개, ${this.maxProducts}개 제한 초과)`
          this.progressPercent = 100

          // 사용자에게 알림
          if (this.$toast) {
            this.$toast.success(`상품 수집이 완료되었습니다 (${finalProductCount}개)`)
          }

          // 현재까지의 결과로 분석 완료 처리
          this.completeAnalysis()
          return
        }


        // 다음 페이지가 있으면 계속 수집, 없으면 키워드 완료 처리
        if (this.currentKeywordPageIndex < this.currentKeywordTotalPages) {
          this.currentKeywordPageIndex++
          // 500ms 딜레이 후 다음 페이지 수집
          setTimeout(() => {
            // 분석이 중단되었으면 실행하지 않음
            if (this.isAnalyzing) {
              this.analyzeKeywordPage(currentKeyword)
            }
          }, 500)
        } else {
          // 현재 키워드의 모든 페이지 수집 완료
          this.completeCurrentKeyword(currentKeyword)
        }

        // console.log(`키워드 "${currentKeyword.keyword}" 분석 완료. 수집: ${processedKeywordResults.length}개, 신규: ${newResults.length}개`)

        // 실시간으로 IndexedDB에 저장 (중복 제거된 신규 데이터만)
        for (const result of newResults) {
          await this.saveAnalysisResult(currentKeyword.keyword, result)
        }

        // 실시간으로 통합 분석 업데이트 (페이지별로 업데이트)
        this.generateIntegratedAnalysis()
      }

      if (message.res === 'keywordSeoAnalysisError') {
        console.error('키워드 분석 오류:', message.error)
        // console.log('🔍 [디버그] 받은 메시지:', message)

        // 카테고리 에러인 경우 특별 처리
        if (message.error === 'CATEGORY_NOT_UNDER_SELLER') {
          // console.log('🚨 [카테고리 에러] 수집 중단 및 알림 표시')
          // 수집 중단
          this.isAnalyzing = false
          // 큐 초기화
          this.workQueue = []
          // 알림 표시
          alert('현재 수집하는 카테고리는 판매자 상품에 등록되지 않은 카테고리 입니다.')
          return
        }

        // 일반 에러의 경우 현재 키워드를 실패로 표시하고 다음으로 진행
        if (this.currentKeywordIndex < this.autoList.length) {
          this.autoList[this.currentKeywordIndex].analysisData = 'error'
          this.autoList[this.currentKeywordIndex].collectedAt = new Date()
        }

        this.currentKeywordIndex++

        // 다음 키워드로 진행 (오류가 있어도 계속)
        setTimeout(() => {
          // 분석이 중단되었으면 실행하지 않음
          if (this.isAnalyzing) {
            this.analyzeNextKeyword()
          }
        }, this.delayTime)
      }

      // 연관+자동완성 키워드 수집 결과 처리
      if (message.res === 'adkeywords') {
        // console.log('🔍 연관키워드 수집 결과:', message.relatedkeywords)
        const keywords = message.relatedkeywords || []
        this.addKeywordsToList(keywords, '연관+자동완성')
      }

      // 네이버 데이터랩 키워드 수집 결과 처리
      if (message.res === 'keywordRankComplete') {
        const keywords = message.data || []
        this.addKeywordsToList(keywords, '네이버데이터랩')
      }

      // 상품 트래픽 키워드 수집 완료 처리
      if (message.res === 'productTrafficKeywordsComplete') {
        try {
          const productInfo = message.productInfo || {}
          const trafficKeyword = productInfo.trafficKeyword || []

          console.log('✅ 상품 정보 수집 완료:', productInfo)
          console.log('✅ 트래픽 키워드 원본 데이터:', trafficKeyword)
          console.log('✅ 키워드 목록:', trafficKeyword.length, '개')

          // 기존 키워드 목록 초기화
          this.productList = []

          // 수집된 키워드들을 productList에 추가 (keywordDataCollect.vue와 동일한 구조)
          trafficKeyword.forEach((item, index) => {
            const keywordDetail = item.keywordDetail || {}
            const keywordMetric = item.keywordMetric || {}

            this.productList.push({
              index: index,
              keyword: keywordDetail.keyword || '',
              source: '트래픽키워드',
              impressions: keywordMetric.impression || 0,
              clicks: keywordMetric.srpClick || 0,
              ctr: keywordMetric.ctr || 0,
              // 추가 정보
              searchVolume: keywordMetric.searchVolume || 0,
              adImpression: keywordMetric.adImpression || 0,
              organicImpression: keywordMetric.organicImpression || 0,
              // 원본 데이터도 보관 (필요시 활용)
              originalData: item
            })
          })

          // 상품 정보도 저장 (필요시 활용)
          this.currentProductInfo = productInfo

          this.progressPercent = 100
          this.progressPrefix = ''
          this.progressSuffix = `트래픽 키워드 ${trafficKeyword.length}개 수집 완료`

          // 상품링크 입력 필드 초기화
          this.targetProductLink = ''

          alert(`트래픽 키워드 ${trafficKeyword.length}개를 수집했습니다.`)

        } catch (error) {
          console.error('❌ 트래픽 키워드 처리 실패:', error)
          alert('트래픽 키워드 처리 중 오류가 발생했습니다.')
        } finally {
          this.isAnalyzing = false
        }
      }

      // 상품 트래픽 키워드 수집 에러 처리
      if (message.res === 'productTrafficKeywordsError') {
        console.error('❌ 트래픽 키워드 수집 실패:', message.message)
        alert('트래픽 키워드 수집 실패: ' + (message.message || '알 수 없는 오류'))
        this.isAnalyzing = false
        this.progressPercent = 0
        this.progressPrefix = ''
        this.progressSuffix = ''
      }

      // 키워드 검색 완료 처리
      if (message.res === 'searchKeywordProductsComplete') {
        const keyword = message.keyword
        const products = message.products || []

        const efficiencyInfo = message.collectionEfficient
          ? ` (효율적 수집: ${message.myProductRank}위까지 ${message.totalPages}페이지)`
          : ` (${message.totalPages}페이지)`

        console.log(`✅ [키워드 검색] "${keyword}" 완료: ${products.length}개 상품${efficiencyInfo}`)

        if (this.keywordSearchPromises && this.keywordSearchPromises[keyword]) {
          const promise = this.keywordSearchPromises[keyword]
          clearTimeout(promise.timeout)
          promise.resolve(products)
          delete this.keywordSearchPromises[keyword]
        }
      }

      // 키워드 검색 에러 처리
      if (message.res === 'searchKeywordProductsError') {
        const keyword = message.keyword
        const error = message.error

        console.error(`❌ [키워드 검색] "${keyword}" 실패:`, error)

        if (this.keywordSearchPromises && this.keywordSearchPromises[keyword]) {
          const promise = this.keywordSearchPromises[keyword]
          clearTimeout(promise.timeout)
          promise.reject(new Error(error))
          delete this.keywordSearchPromises[keyword]
        }
      }

      // 상품 상세 정보 완료 처리
      if (message.res === 'getProductDetailComplete') {
        const vendorItemId = message.vendorItemId
        const detailInfo = message.detailInfo

        console.log(`✅ [상품 상세 정보] ${vendorItemId} 완료`)

        if (this.productDetailPromises && this.productDetailPromises[vendorItemId]) {
          const promise = this.productDetailPromises[vendorItemId]
          clearTimeout(promise.timeout)
          promise.resolve(detailInfo)
          delete this.productDetailPromises[vendorItemId]
        }
      }

      // 상품 상세 정보 에러 처리
      if (message.res === 'getProductDetailError') {
        const vendorItemId = message.vendorItemId
        const error = message.error

        console.error(`❌ [상품 상세 정보] ${vendorItemId} 실패:`, error)

        if (this.productDetailPromises && this.productDetailPromises[vendorItemId]) {
          const promise = this.productDetailPromises[vendorItemId]
          clearTimeout(promise.timeout)
          promise.resolve(null) // 에러 시 null 반환
          delete this.productDetailPromises[vendorItemId]
        }
      }

      // 상품 트래픽 정보 완료 처리
      if (message.res === 'getProductTrafficInfoComplete') {
        const itemId = message.itemId
        const trafficInfo = message.trafficInfo

        console.log(`✅ [상품 트래픽 정보] ${itemId} 완료`)

        if (this.trafficInfoPromises && this.trafficInfoPromises[itemId]) {
          const promise = this.trafficInfoPromises[itemId]
          clearTimeout(promise.timeout)
          promise.resolve(trafficInfo)
          delete this.trafficInfoPromises[itemId]
        }
      }

      // 상품 트래픽 정보 에러 처리
      if (message.res === 'getProductTrafficInfoError') {
        const itemId = message.itemId
        const error = message.error

        console.error(`❌ [상품 트래픽 정보] ${itemId} 실패:`, error)

        if (this.trafficInfoPromises && this.trafficInfoPromises[itemId]) {
          const promise = this.trafficInfoPromises[itemId]
          clearTimeout(promise.timeout)
          promise.resolve(null) // 에러 시 null 반환
          delete this.trafficInfoPromises[itemId]
        }
      }
    },

    formatPrice(price) {
      if (!price) return '0'
      return parseInt(price).toLocaleString()
    },

    formatNumber(number) {
      if (!number || isNaN(number)) return '0'
      return parseInt(number).toLocaleString()
    },

    // popup.js와 동일한 종합 분석 수행
    performComprehensiveAnalysis() {
      if (!this.analysisResults || this.analysisResults.length === 0) return

      // console.log('종합 분석 시작:', this.analysisResults.length, '개 상품')

      // 1단계: 모든 계산이 완료된 데이터 생성 (최신 데이터 반영)
      this.createEnrichedAnalysisData()

      // 2단계: 종합 지표 계산
      this.calculateSummaryMetrics()

      // 베스트 상품 분석
      this.analyzeBestProducts()

      // 소싱 전략 분석
      this.analyzeSourcingStrategy()

      // 통합 키워드 분석 (먼저 실행)
      this.analyzeIntegratedKeywords()

      // 키워드 SEO 분석 (통합 키워드 분석 후 실행)
      this.analyzeKeywordSEO()

      // 차트 생성 (일별 트래픽은 Top20으로 최적화됨)
      this.$nextTick(() => {
        this.createCharts()
      })
    },

    // 종합 지표 계산 (popup.js의 updateSummaryMetrics와 유사)
    calculateSummaryMetrics() {
      const products = this.analysisResults

      // CTR, CVR 배열 생성 (실시간 계산)
      const ctrs = products.map(p => parseFloat(this.calculateCTR(p))).filter(ctr => ctr > 0)
      const cvrs = products.map(p => parseFloat(this.calculateCVR(p))).filter(cvr => cvr > 0)

      // 중앙값 계산
      this.summaryMetrics.medianCTR = this.calculateMedian(ctrs).toFixed(2)
      this.summaryMetrics.medianCVR = this.calculateMedian(cvrs).toFixed(2)

      // 총합 계산 (실시간 계산)
      this.summaryMetrics.totalProducts = products.length
      this.summaryMetrics.totalImpressions = products.reduce((sum, p) => sum + this.calculateImpressions(p), 0)
      this.summaryMetrics.totalClicks = products.reduce((sum, p) => sum + this.calculateClicks(p), 0)
      this.summaryMetrics.totalSales = products.reduce((sum, p) => sum + (parseInt(p.salesLast28d) || 0), 0)

      // 매출액 계산 (판매개수 × 가격)
      this.summaryMetrics.totalSalesAmount = products.reduce((sum, p) => {
        const salesCount = parseInt(p.salesLast28d) || 0
        const price = parseInt(p.price) || 0
        return sum + (salesCount * price)
      }, 0)

      // 평균 판매가격 계산
      const validPriceProducts = products.filter(p => p.price > 0)
      this.summaryMetrics.avgSalePrice = validPriceProducts.length > 0
        ? validPriceProducts.reduce((sum, p) => sum + (parseInt(p.price) || 0), 0) / validPriceProducts.length
        : 0

      // 평균 RPI 계산
      const validRPIProducts = products.filter(p => {
        const rpi = parseFloat(this.calculateRPI(p))
        return rpi > 0
      })
      this.summaryMetrics.avgRPI = validRPIProducts.length > 0
        ? (validRPIProducts.reduce((sum, p) => sum + parseFloat(this.calculateRPI(p)), 0) / validRPIProducts.length).toFixed(2)
        : '0.00'

      // popup.js와 동일: 광고/자연 노출수 계산
      let totalAdImpressions = 0
      let totalOrganicImpressions = 0

      products.forEach(product => {
        if (product.trafficKeyword && product.trafficKeyword.length > 0) {
          product.trafficKeyword.forEach(tk => {
            const adImpression = tk.keywordMetric?.adImpression || 0
            const impression = tk.keywordMetric?.impression || 0
            const organicImpression = Math.max(0, impression - adImpression) // 음수 방지

            totalAdImpressions += adImpression
            totalOrganicImpressions += organicImpression
          })
        }
      })

      this.summaryMetrics.totalAdImpressions = totalAdImpressions
      this.summaryMetrics.totalOrganicImpressions = totalOrganicImpressions

      // 실제 광고 노출수 데이터가 있는지 확인
      const hasRealAdData = this.analysisResults.some(product =>
        product.trafficKeyword?.some(tk =>
          tk.keywordMetric?.adImpression && tk.keywordMetric.adImpression > 0
        )
      )

      // 실제 데이터가 없는 경우에만 추정값 사용
      if (!hasRealAdData && totalAdImpressions === 0 && totalOrganicImpressions === 0 && this.summaryMetrics.totalImpressions > 0) {
        // 실제 데이터가 없다는 것을 명시적으로 표시
        // console.log('실제 광고 노출수 데이터가 없어 차트를 표시하지 않습니다.')
        this.summaryMetrics.totalAdImpressions = 0
        this.summaryMetrics.totalOrganicImpressions = 0
      } else if (hasRealAdData) {
        // console.log('실제 광고 노출수 데이터 발견:', {
        //   totalAdImpressions: this.summaryMetrics.totalAdImpressions,
        //   totalOrganicImpressions: this.summaryMetrics.totalOrganicImpressions
        // })
      }

      // console.log('노출수 계산 완료:', {
      //   totalAdImpressions: this.summaryMetrics.totalAdImpressions,
      //   totalOrganicImpressions: this.summaryMetrics.totalOrganicImpressions,
      //   total: this.summaryMetrics.totalAdImpressions + this.summaryMetrics.totalOrganicImpressions
      // })

      // console.log('종합 지표 계산 완료:', this.summaryMetrics)
      // console.log('- 중앙값 CTR:', this.summaryMetrics.medianCTR)
      // console.log('- 중앙값 CVR:', this.summaryMetrics.medianCVR)
      // console.log('- 총 노출수:', this.summaryMetrics.totalImpressions)
      // console.log('- 광고 노출수:', this.summaryMetrics.totalAdImpressions)
      // console.log('- 자연 노출수:', this.summaryMetrics.totalOrganicImpressions)
    },

    // 중앙값 계산 함수 (popup.js와 동일)
    calculateMedian(arr) {
      if (arr.length === 0) return 0
      const sorted = [...arr].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
    },

    // 베스트 상품 분석 (미리 계산된 데이터 사용)
    analyzeBestProducts() {
      // 미리 계산된 이상치제외 유효 상품들 사용
      let validProducts = this.validProductsWithoutOutliers.filter(p => {
        // popup.js와 동일한 추가 필터링 조건
        const hasMinimumClicks = p.calculatedClicks >= 11
        const hasMinimumSales = (parseInt(p.salesLast28d) || 0) >= 3
        const hasImpressions = p.calculatedImpressions > 0
        const hasValidRPI = p.calculatedRPI > 0

        return hasMinimumClicks && hasMinimumSales && hasImpressions && hasValidRPI
      })

      // console.log('🔍 베스트 상품 분석 필터링 결과:', {
      //   totalValidProducts: validProducts.length
      // })

      // 베스트 상품들은 analyzeSourcingStrategy()에서 설정됨

      // 전체 상품을 노출수 기준으로 정렬하여 현재노출순위 계산 (미리 계산된 값 사용)
      const allProductsByImpressions = validProducts
        .map(product => ({
          ...product,
          totalImpressions: product.calculatedImpressions
        }))
        .sort((a, b) => b.totalImpressions - a.totalImpressions)

      // 각 상품에 현재노출순위 부여
      allProductsByImpressions.forEach((product, index) => {
        product.currentRank = index + 1
      })

      // 전체 베스트 상품 목록 (RPI 기준 상위 20개, 현재노출순위 + 시선집중도 포함)
      this.bestProducts = allProductsByImpressions
        .sort((a, b) => b.calculatedRPI - a.calculatedRPI)
        .slice(0, 20)
        .map(product => ({
          ...product,
          // 시선집중도 컬럼 추가
          attentionScore: product.calculatedAttentionScore
        }))

      // console.log('베스트 상품 분석 완료:', {
      //   bestCTRProduct: this.bestCTRProduct?.productName,
      //   bestCVRProduct: this.bestCVRProduct?.productName,
      //   bestAttentionProduct: this.bestAttentionProduct?.productName,
      //   bestRPIProduct: this.bestRPIProduct?.productName,
      //   totalBestProducts: this.bestProducts.length
      // })
    },

    // 키워드 SEO 분석 (최적 카테고리 키워드만 사용)
    analyzeKeywordSEO() {
      // console.log('🔍 키워드 SEO 분석 시작:', {
      //   integratedKeywordsLength: this.integratedKeywords?.length || 0,
      //   integratedKeywordsExists: !!this.integratedKeywords,
      //   optimizedCategoryInfo: this.optimizedCategoryInfo,
      //   firstKeyword: this.integratedKeywords?.[0]
      // })

      if (!this.integratedKeywords || this.integratedKeywords.length === 0) {
        // console.log('❌ 통합 키워드 분석 결과가 없어서 SEO 분석을 건너뜁니다.')
        // 빈 배열로 초기화
        this.keywordSEOAnalysis.productNameKeywords = []
        this.keywordSEOAnalysis.tagKeywords = []
        return
      }

      // 최적 카테고리에 해당하는 키워드들만 필터링
      const optimalCategoryName = this.optimizedCategoryInfo?.categoryName
      let filteredKeywords = this.integratedKeywords

      if (optimalCategoryName) {
        filteredKeywords = this.integratedKeywords.filter(keyword =>
          keyword.mainCategory === optimalCategoryName
        )
        // console.log(`🎯 최적 카테고리 필터링: ${optimalCategoryName}`)
        // console.log(`📊 필터링 결과: 전체 ${this.integratedKeywords.length}개 → 최적 카테고리 ${filteredKeywords.length}개`)
      } else {
        // console.log('⚠️ 최적 카테고리 정보가 없어서 전체 키워드를 사용합니다.')
      }

      if (filteredKeywords.length === 0) {
        // console.log('❌ 최적 카테고리에 해당하는 키워드가 없어서 SEO 분석을 건너뜁니다.')
        this.keywordSEOAnalysis.productNameKeywords = []
        this.keywordSEOAnalysis.tagKeywords = []
        return
      }

      // 상품명 키워드: 최적 카테고리 키워드 상위 1~6번
      this.keywordSEOAnalysis.productNameKeywords = filteredKeywords
        .slice(0, 6) // 상위 1~6번
        .map(keyword => ({
          word: keyword.keyword,
          count: keyword.totalVolume, // 노출수를 count로 사용
          impressions: keyword.totalVolume
        }))

      // 태그 키워드: 최적 카테고리 우선 + 나머지 카테고리 노출수 순
      const optimalCategoryTagKeywords = filteredKeywords
        .slice(6, 30) // 최적 카테고리 7~30번 (인덱스 6~29)
        .map(keyword => ({
          word: keyword.keyword,
          count: keyword.totalVolume,
          impressions: keyword.totalVolume,
          category: keyword.mainCategory,
          isOptimalCategory: true
        }))

      // 나머지 카테고리 키워드 (최적 카테고리가 아닌 키워드들)
      const otherCategoryKeywords = this.integratedKeywords
        .filter(keyword => keyword.mainCategory !== optimalCategoryName)
        .sort((a, b) => b.totalVolume - a.totalVolume) // 노출수 기준 내림차순 정렬
        .slice(0, 30 - optimalCategoryTagKeywords.length) // 남은 슬롯만큼
        .map(keyword => ({
          word: keyword.keyword,
          count: keyword.totalVolume,
          impressions: keyword.totalVolume,
          category: keyword.mainCategory,
          isOptimalCategory: false
        }))

      // 최적 카테고리 키워드 + 나머지 카테고리 키워드 결합
      this.keywordSEOAnalysis.tagKeywords = [
        ...optimalCategoryTagKeywords,
        ...otherCategoryKeywords
      ]

      // 편집 가능한 태그 키워드 초기화 (원본 복사)
      this.editableTagKeywords = [...this.keywordSEOAnalysis.tagKeywords]

      // 상품명 자동 생성
      this.generateProductName()

      // console.log('✅ 키워드 SEO 분석 완료:', {
      //   optimalCategory: optimalCategoryName,
      //   filteredKeywordsCount: filteredKeywords.length,
      //   productNameKeywords: this.keywordSEOAnalysis.productNameKeywords.length,
      //   tagKeywords: this.keywordSEOAnalysis.tagKeywords.length,
      //   optimalCategoryTagKeywords: optimalCategoryTagKeywords.length,
      //   otherCategoryKeywords: otherCategoryKeywords.length,
      //   productNameSample: this.keywordSEOAnalysis.productNameKeywords.slice(0, 2),
      //   tagKeywordsSample: this.keywordSEOAnalysis.tagKeywords.slice(0, 5).map(k => `${k.word} (${k.category}${k.isOptimalCategory ? ' ⭐' : ''})`)
      // })

      // SEO 최적화 제안 생성
      this.keywordSEOAnalysis.suggestions = this.generateSEOSuggestions()
    },

    // SEO 최적화 제안 생성
    generateSEOSuggestions() {
      const suggestions = []
      const topProductKeywords = this.keywordSEOAnalysis.productNameKeywords.slice(0, 5)
      // const topTagKeywords = this.keywordSEOAnalysis.tagKeywords.slice(0, 5)
      const optimalCategoryName = this.optimizedCategoryInfo?.categoryName

      if (topProductKeywords.length > 0) {
        suggestions.push(`상품명에 "${topProductKeywords[0].word}" 키워드를 포함하면 검색 노출이 향상될 수 있습니다.`)
      }

      if (optimalCategoryName) {
        suggestions.push(`"${optimalCategoryName}" 카테고리에 최적화된 키워드를 우선적으로 사용하세요.`)
      }

      // if (topTagKeywords.length > 0) {
      //   suggestions.push(`"${topTagKeywords[0].word}" 키워드로 광고를 집행하면 효과적일 수 있습니다.`)
      // }

      if (this.targetKeyword) {
        const targetInProductNames = topProductKeywords.some(k => k.word.includes(this.targetKeyword.toLowerCase()))
        if (!targetInProductNames) {
          suggestions.push(`타겟 키워드 "${this.targetKeyword}"를 상품명에 포함하는 것을 고려해보세요.`)
        }
      }

      if (suggestions.length === 0) {
        suggestions.push('충분한 데이터가 수집되면 더 구체적인 SEO 제안을 제공할 수 있습니다.')
      }

      return suggestions
    },

    // 상품명 자동 생성 로직 (오른쪽부터 왼쪽으로 채우기)
    generateProductName() {
      if (this.keywordSEOAnalysis.productNameKeywords.length === 0) {
        this.generatedProductName = ''
        return
      }

      // 노출수 큰 순으로 키워드 정렬
      const sortedKeywords = this.keywordSEOAnalysis.productNameKeywords
        .sort((a, b) => b.impressions - a.impressions)

      // 결과 배열 (오른쪽부터 왼쪽으로 채워나감)
      let result = []

      // 노출수 높은 순으로 하나씩 처리
      sortedKeywords.forEach((keywordObj) => {
        const keyword = keywordObj.word
        // console.log(`${index + 1}. "${keyword}" 처리 시작`)

        // 키워드를 단어로 분해 (복합 키워드 처리)
        const words = keyword.split(/\s+/).filter(word => word.length > 0)

        if (words.length === 1) {
          // 단일 키워드 처리
          const singleWord = words[0]
          if (!result.includes(singleWord)) {
            if (result.length === 0) {
              // 첫 번째 키워드는 맨 오른쪽
              result.push(singleWord)
              // console.log(`   첫 키워드 "${singleWord}" 맨 오른쪽 추가:`, result.join(' '))
            } else {
              // 이후 키워드는 맨 왼쪽에 추가 (오른쪽부터 왼쪽으로 채우기)
              result.unshift(singleWord)
              // console.log(`   키워드 "${singleWord}" 맨 왼쪽 추가:`, result.join(' '))
            }
          }
        } else {
          // 복합 키워드 처리
          const mainWord = words[words.length - 1] // 마지막 단어가 메인

          // 메인 키워드가 이미 result에 있는지 확인
          const mainWordIndex = result.indexOf(mainWord)

          if (mainWordIndex !== -1) {
            // 메인 키워드가 이미 있는 경우
            // 수식어들을 메인 키워드 앞에 추가하고, 후속어들을 메인 키워드 뒤에 추가
            words.forEach((word, wordIndex) => {
              if (!result.includes(word)) {
                if (wordIndex < words.length - 1) {
                  // 메인 키워드가 아닌 경우
                  if (wordIndex < words.indexOf(mainWord)) {
                    // 수식어: 메인 키워드 앞에 추가
                    result.splice(mainWordIndex, 0, word)
                    // console.log(`   수식어 "${word}"를 "${mainWord}" 앞에 추가:`, result.join(' '))
                  } else {
                    // 후속어: 메인 키워드 뒤에 추가
                    const currentMainIndex = result.indexOf(mainWord)
                    result.splice(currentMainIndex + 1, 0, word)
                    // console.log(`   후속어 "${word}"를 "${mainWord}" 뒤에 추가:`, result.join(' '))
                  }
                }
              }
            })
          } else {
            // 메인 키워드가 없으면 전체 키워드를 하나로 처리
            if (result.length === 0) {
              // 첫 번째면 맨 오른쪽
              result.push(keyword)
              // console.log(`   첫 복합키워드 "${keyword}" 맨 오른쪽 추가:`, result.join(' '))
            } else {
              // 이후면 맨 왼쪽
              result.unshift(keyword)
              // console.log(`   복합키워드 "${keyword}" 맨 왼쪽 추가:`, result.join(' '))
            }
          }
        }

        // console.log(`   ${index + 1}번 처리 완료:`, result.join(' '))
      })

      const productNamePart = result.join(' ')
      this.generatedProductName = `브랜드명 ${productNamePart}`

      // console.log('🔍 상품명 생성 로직 (오른쪽부터 왼쪽으로 채우기):', {
      //   originalKeywords: sortedKeywords.map(k => `${k.word} (${k.impressions.toLocaleString()})`),
      //   stepByStep: '위의 단계별 로그 참조',
      //   finalResult: result,
      //   finalProductName: this.generatedProductName
      // })
    },

    // 상품명 입력창 업데이트 시 호출
    updateGeneratedProductName() {
      // 사용자가 직접 수정한 경우 별도 처리 없음 (수정 허용)
    },

    // 상품명 키워드 복사
    async copyProductNameKeywords() {
      try {
        await navigator.clipboard.writeText(this.generatedProductName)
        this.$toast.success('상품명이 클립보드에 복사되었습니다!')
      } catch (err) {
        console.error('복사 실패:', err)
        // 폴백: 텍스트 선택 방식
        const textArea = document.createElement('textarea')
        textArea.value = this.generatedProductName
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$toast.success('상품명이 클립보드에 복사되었습니다!')
      }
    },

    // 상세보기 스위치 토글
    toggleDetailView() {
      this.showDetailView = !this.showDetailView
      this.updateHeaders()
      // console.log('🔄 상세보기 토글:', {
      //   showDetailView: this.showDetailView,
      //   headerType: this.showDetailView ? '상세보기' : '기본보기',
      //   headerCount: this.integratedKeywordHeaders.length
      // })
    },

    // 헤더 업데이트
    updateHeaders() {
      // console.log('🔄 헤더 업데이트 시작:', {
      //   showDetailView: this.showDetailView,
      //   기본헤더수: this.basicIntegratedKeywordHeaders.length,
      //   상세헤더수: this.detailIntegratedKeywordHeaders.length
      // })

      const newHeaders = this.showDetailView ?
        [...this.detailIntegratedKeywordHeaders] :
        [...this.basicIntegratedKeywordHeaders]

      // Vue의 반응성을 보장하기 위해 명시적으로 할당
      this.$set(this, 'integratedKeywordHeaders', newHeaders)

      // console.log('✅ 헤더 업데이트 완료:', {
      //   새헤더수: this.integratedKeywordHeaders.length,
      //   헤더타입: this.showDetailView ? '상세보기' : '기본보기'
      // })

      // 다음 틱에서 강제 업데이트
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    // 태그 키워드 뱃지 클래스 결정
    getTagKeywordBadgeClass(keyword) {
      if (keyword.isOptimalCategory) {
        return 'badge bg-success' // 최적 카테고리: 초록색
      } else {
        return 'badge bg-info' // 다른 카테고리: 파란색
      }
    },

    // 태그 키워드 삭제
    removeTagKeyword(index) {
      this.editableTagKeywords.splice(index, 1)
    },

    // 태그 키워드 복사 (앞에 20개만)
    async copyTagKeywords() {
      if (this.editableTagKeywords.length === 0) return

      const first20Keywords = this.editableTagKeywords.slice(0, 20)
      const tagString = first20Keywords.map(k => k.word).join(', ')

      try {
        await navigator.clipboard.writeText(tagString)
        this.$toast.success(`태그 키워드 ${first20Keywords.length}개가 클립보드에 복사되었습니다!`)
      } catch (err) {
        console.error('복사 실패:', err)
        // 폴백: 텍스트 선택 방식
        const textArea = document.createElement('textarea')
        textArea.value = tagString
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$toast.success(`태그 키워드 ${first20Keywords.length}개가 클립보드에 복사되었습니다!`)
      }
    },





    // 다국어 검색어 생성 - 주석처리
    /*
    async generateMultilingualKeywords() {
      if (!this.sourcingChecklist) {
        this.$toast.info('AI 소싱 분석을 먼저 실행해주세요')
        return
      }
      try {
        this.isGeneratingMultilingualKeywords = true
        const sellingPoints = this.buildSellingPointsSummary()
        const prompt = `You are an e-commerce keyword expert. Generate 5 sets of multilingual search queries where each set contains the SAME MEANING keywords in Korean, Chinese, and English.

Context (Korean analysis summary):
${sellingPoints}

Rules:
- Return VALID JSON only. No prose.
- Generate exactly 5 keyword sets
- Each set must have the SAME MEANING across all 3 languages
- JSON structure: {"keywordSets": [{"ko": {"keyword": string, "rationale": string}, "zh": {"keyword": string, "rationale": string}, "en": {"keyword": string, "rationale": string}}, ...]}
- Korean: Natural search phrases that strongly reflect selling points
- Chinese: Simplified Chinese (简体中文) typical 1688 search style, same meaning as Korean
- English: Amazon/Alibaba style queries, same meaning as Korean
- Prioritize queries that best express the USPs and improvements
- Keep 2-5 words if possible; avoid punctuation/special characters

Example JSON:
{
  "keywordSets": [
    {
      "ko": {"keyword": "미끄럼방지 옷걸이", "rationale": "논슬립 USP 반영"},
      "zh": {"keyword": "防滑衣架", "rationale": "防滑卖点"},
      "en": {"keyword": "non slip hanger", "rationale": "non-slip USP"}
    }
  ]
}`

        const result = await workflowService.requestExtensionWithPromise({
          workType: 'SOURCING_CHECKLIST',
          task: 'generate_multilingual_keywords',
          requestArr: [{ prompt }]
        }, { timeout: 60000, maxRetries: 2 })

        this.multilingualKeywords = this.parseMultilingualKeywords(result)
        this.$toast.success('다국어 검색어 생성 완료')
      } catch (err) {
        console.error('다국어 검색어 생성 실패:', err)
        this.$toast.error('다국어 검색어 생성 중 오류가 발생했습니다')
      } finally {
        this.isGeneratingMultilingualKeywords = false
      }
    },
    */

    // 응답 파싱 및 정규화 - 주석처리
    /*
    parseMultilingualKeywords(result) {
      try {
        let parsed = result
        if (parsed && typeof parsed === 'object' && parsed.batchResults && Array.isArray(parsed.batchResults)) {
          parsed = parsed.batchResults[0]?.result || parsed
        }
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed)
        }

        const keywordSets = Array.isArray(parsed?.keywordSets) ? parsed.keywordSets : []
        const normalizedSets = []

        for (let i = 0; i < Math.min(5, keywordSets.length); i++) {
          const set = keywordSets[i]
          if (set && typeof set === 'object') {
            const normalizedSet = {}

            // 한국어
            if (set.ko && set.ko.keyword) {
              normalizedSet.ko = {
                keyword: set.ko.keyword.trim(),
                rationale: (set.ko.rationale || '').trim()
              }
            }

            // 중국어
            if (set.zh && set.zh.keyword) {
              normalizedSet.zh = {
                keyword: set.zh.keyword.trim(),
                rationale: (set.zh.rationale || '').trim()
              }
            }

            // 영어
            if (set.en && set.en.keyword) {
              normalizedSet.en = {
                keyword: set.en.keyword.trim(),
                rationale: (set.en.rationale || '').trim()
              }
            }

            // 최소 하나의 언어라도 있으면 추가
            if (normalizedSet.ko || normalizedSet.zh || normalizedSet.en) {
              normalizedSets.push(normalizedSet)
            }
          }
        }

        return normalizedSets
      } catch (e) {
        console.error('다국어 키워드 파싱 실패:', e, result)
        return []
      }
    },

    // 단일 키워드 복사
    async copyKeyword(keyword) {
      try {
        await navigator.clipboard.writeText(keyword)
        this.$toast.success('키워드를 복사했습니다')
      } catch (err) {
        console.error('복사 실패:', err)
        this.$toast.error('복사 중 오류가 발생했습니다')
      }
    },
    */

    // 소싱 전략 분석 (미리 계산된 데이터 사용)
    analyzeSourcingStrategy() {
      // 미리 계산된 이상치제외 유효 상품들 사용
      const validProducts = this.validProductsWithoutOutliers

      // console.log('🔍 베스트 상품 분석 결과:', {
      //   validProducts: validProducts.length
      // })

      // 베스트 CTR 상품 (이상치제외 전체데이터 중 CTR 최고)
      this.bestCTRProduct = validProducts
        .filter(p => p.calculatedCTR > 0 && (p.price || 0) > 0)
        .sort((a, b) => b.calculatedCTR - a.calculatedCTR)[0] || null

      // 베스트 시선집중도 상품 (이상치제외 전체데이터 중 시선집중도 최고)
      this.bestAttentionProduct = validProducts
        .filter(p => p.calculatedAttentionScore > 0)
        .sort((a, b) => b.calculatedAttentionScore - a.calculatedAttentionScore)[0] || null

      // 베스트 CVR 상품 (이상치제외 전체데이터 중 CVR 최고)
      this.bestCVRProduct = validProducts
        .filter(p => p.calculatedCVR > 0 && (p.price || 0) > 0)
        .sort((a, b) => b.calculatedCVR - a.calculatedCVR)[0] || null

      // 베스트 판매가×CVR 상품 (이상치제외 전체데이터 중 판매가×CVR 최고)
      this.bestPriceCvrProduct = validProducts
        .filter(p => p.calculatedCVR > 0 && (p.price || 0) > 0)
        .sort((a, b) => (b.price * b.calculatedCVR) - (a.price * a.calculatedCVR))[0] || null

      // 베스트 RPI 상품 (이상치제외 전체데이터 중 RPI 최고)
      this.bestRPIProduct = validProducts
        .filter(p => p.calculatedRPI > 0 && (p.price || 0) > 0)
        .sort((a, b) => b.calculatedRPI - a.calculatedRPI)[0] || null



      // TOP3 소싱 상품 (순수 RPI 기준)
      this.topSourcingProducts = validProducts
        .sort((a, b) => b.calculatedRPI - a.calculatedRPI)
        .slice(0, 3)

      // 가격대별 분석
      this.analyzePriceRanges()

      // 카테고리 분석
      this.analyzeCategoryOptimization()

      // console.log('소싱 전략 분석 완료:', {
      //   bestCTRProduct: this.bestCTRProduct?.productName,
      //   bestAttentionProduct: this.bestAttentionProduct?.productName,
      //   bestSourcingProduct: this.bestSourcingProduct?.productName
      // })
    },

    // 가격대별 분석 (popup.js의 updateBestPriceRange와 동일)
    analyzePriceRanges() {
      const validPriceProducts = this.analysisResults.filter(product =>
        product.price > 0 && product.salesLast28d > 0
      )

      if (validPriceProducts.length > 0) {
        const priceRangeData = new Map()

        validPriceProducts.forEach(product => {
          // popup.js와 동일: 1000원 단위로 그룹화
          const priceRange = Math.floor(product.price / 1000) * 1000
          const sales = product.salesLast28d * product.price
          priceRangeData.set(priceRange, (priceRangeData.get(priceRange) || 0) + sales)
        })

        const sortedRanges = Array.from(priceRangeData.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3) // 상위 3개만 가져오기

        if (sortedRanges.length > 0) {
          this.bestPriceRanges = sortedRanges.map(range => ({
            priceRangeText: `${range[0].toLocaleString()}원~${(range[0] + 999).toLocaleString()}원`,
            salesAmount: range[1]
          }))

          // 기존 bestPriceRange도 유지 (1등만)
          this.bestPriceRange = this.bestPriceRanges[0].priceRangeText
        } else {
          this.bestPriceRanges = []
          this.bestPriceRange = '데이터 없음'
        }
      } else {
        this.bestPriceRanges = []
        this.bestPriceRange = '데이터 없음'
      }

      // console.log('가격대별 분석 완료:', this.bestPriceRanges)
    },

    getTotalItemsInCategory(category) {
      return category.reduce((total, section) => total + section.items.length, 0)
    },

    getPriorityBorderClass(priority) {
      const classes = {
        high: 'border-danger',
        medium: 'border-warning',
        low: 'border-success'
      }
      return classes[priority] || 'border-secondary'
    },

    getPriorityBadgeClass(priority) {
      const classes = {
        high: 'bg-danger',
        medium: 'bg-warning text-dark',
        low: 'bg-success'
      }
      return classes[priority] || 'bg-secondary'
    },

    getPriorityText(priority) {
      const texts = {
        high: '높음',
        medium: '보통',
        low: '낮음'
      }
      return texts[priority] || priority
    },

    // 기본 summaryMetrics 반환
    getDefaultSummaryMetrics() {
      return {
        medianCTR: '0.00',
        medianCVR: '0.00',
        totalProducts: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalSales: 0,
        totalAdImpressions: 0,
        totalOrganicImpressions: 0
      }
    },



    // 차트용 최적화된 데이터 추출 (필요한 필드만)
    extractChartData(products, fields) {
      return products.map(product => {
        const extracted = {}
        fields.forEach(field => {
          if (field === 'calculatedRPI') {
            extracted[field] = parseFloat(this.calculateRPI(product))
          } else if (field === 'calculatedCTR') {
            extracted[field] = parseFloat(this.calculateCTR(product))
          } else if (field === 'calculatedCVR') {
            extracted[field] = parseFloat(this.calculateCVR(product))
          } else if (field === 'calculatedImpressions') {
            extracted[field] = this.calculateImpressions(product)
          } else {
            extracted[field] = product[field]
          }
        })
        return extracted
      })
    },

    // 메모리 정리 (사용하지 않는 데이터 해제)
    cleanupMemory() {
      try {
        // 차트 옵션 초기화 (메모리 해제)
        this.impressionChartOptions = {}
        this.priceVsCvrChartOptions = {}
        this.adCompetitivenessChartOptions = {}
        this.priceRangeSalesChartOptions = {}
        this.dailyTrafficChartOptions = {}

        // 차트 표시 상태 초기화
        this.showImpressionChart = false
        this.showPriceVsCvrChart = false
        this.showAdCompetitivenessChart = false
        this.showPriceRangeSalesChart = false
        this.showDailyTrafficChart = false

        // 임시 데이터 정리
        this.adCompetitivenessData = []
        this.priceVsCvrData = []

        // RPI Top20 데이터 초기화 (메모리 사용량 문제 해결)
        this.rpiTop20Products = []

        // 가비지 컬렉션 힌트
        if (window.gc) {
          window.gc()
        }

        // console.log('🧹 메모리 정리 완료')
      } catch (error) {
        console.error('메모리 정리 실패:', error)
      }
    },


    // 차트 업데이트 (정렬 변경 시)
    updateAdCompetitivenessChart() {
      if (this.adCompetitivenessData.length === 0) return

      // 정렬 다시 적용
      if (this.sortByImpression) {
        this.adCompetitivenessData.sort((a, b) => b.impressions - a.impressions)
      } else {
        this.adCompetitivenessData.sort((a, b) => b.competitiveness - a.competitiveness)
      }

      this.updateAdCompetitivenessChartOptions()
    },

    // ===== 키워드그룹 관리 메서드 =====

    // 현재 그룹의 키워드 설정 저장 (상품경쟁력분석 전용 DB에 저장)
    async saveCurrentGroupKeywords() {
      try {
        this.cleanAutoListFields()

        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'
        const request = indexedDB.open(scopedDbName(dbName), 1)
        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'groupName' })
          }
        }
        const db = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })

        const tx = db.transaction([storeName], 'readwrite')
        const store = tx.objectStore(storeName)
        const prev = await new Promise((resolve, reject) => {
          const getReq = store.get(this.activeProductGroup)
          getReq.onsuccess = () => resolve(getReq.result || { groupName: this.activeProductGroup })
          getReq.onerror = () => reject(getReq.error)
        })

        const updated = {
          ...prev,
          groupName: this.activeProductGroup,
          autoList: this.autoList || [],
          maxProducts: this.maxProducts,
          updatedAt: new Date()
        }

        await new Promise((resolve, reject) => {
          const putReq = store.put(updated)
          putReq.onsuccess = () => resolve()
          putReq.onerror = () => reject(putReq.error)
        })

        tx.oncomplete = () => db.close()
        console.log('💾 [PCA] 그룹 설정 저장', { db: dbName, store: storeName, group: this.activeProductGroup, autoListLen: this.autoList?.length || 0 })
      } catch (error) {
        console.error('❌ 현재 그룹 키워드 설정 저장 실패:', error)
      }
    },


    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('ko-KR')
    },


    // 가격 경쟁력 분석 결과 표시 메서드들
    getAverageFullRangeScore() {
      if (this.competitivenessResults.length === 0) return 0
      const vals = this.competitivenessResults
        .map(r => r.fullRange)
        .filter(rd => rd && rd.myPriceRank && rd.totalProducts)
        .map(rd => Math.max(1, Math.min(100, Math.round((rd.myPriceRank / rd.totalProducts) * 100))))
      if (vals.length === 0) return 0
      const sum = vals.reduce((a, b) => a + b, 0)
      return Math.round(sum / vals.length)
    },

    getAverageNearbyRangeScore() {
      if (this.competitivenessResults.length === 0) return 0
      const vals = this.competitivenessResults
        .map(r => r.nearbyRange)
        .filter(rd => rd && rd.myPriceRank && rd.totalProducts)
        .map(rd => Math.max(1, Math.min(100, Math.round((rd.myPriceRank / rd.totalProducts) * 100))))
      if (vals.length === 0) return 0
      const sum = vals.reduce((a, b) => a + b, 0)
      return Math.round(sum / vals.length)
    },

    // 가격 순위에 따른 배지 클래스
    getPriceRankBadgeClass(rank, total) {
      const percentage = (rank / total) * 100
      if (percentage <= 20) return 'bg-success'
      if (percentage <= 40) return 'bg-info'
      if (percentage <= 60) return 'bg-warning'
      return 'bg-danger'
    },

    // 경쟁력 점수에 따른 그라데이션 색상 (빨간색 → 초록색)
    getCompetitivenessColor(score) {
      // 0점 = 빨간색 (255, 0, 0), 100점 = 초록색 (0, 255, 0)
      const red = Math.round(255 * (1 - score / 100))
      const green = Math.round(255 * (score / 100))
      const blue = 0

      return `rgb(${red}, ${green}, ${blue})`
    },

    // 경쟁력 점수에 따른 프로그레스 바 스타일
    getCompetitivenessBarStyle(score) {
      return {
        backgroundColor: this.getCompetitivenessColor(score)
      }
    },

    // 경쟁력 점수에 따른 텍스트 색상

    // 가격 경쟁력 백분위(1%~100%) 계산: 1위=1%, 꼴찌=100%
    getPricePercent(rangeData) {
      if (!rangeData || !rangeData.myPriceRank || !rangeData.totalProducts) return null
      return Math.max(1, Math.min(100, Math.round((rangeData.myPriceRank / rangeData.totalProducts) * 100)))
    },

    // 백분위는 낮을수록 좋음 → 색상은 (100 - percent)를 점수로 환산하여 기존 그라데이션 재사용
    getPercentBarStyle(percent) {
      const score = 100 - (percent || 0)
      return this.getCompetitivenessBarStyle(score)
    },
    getPercentTextStyle(percent) {
      const score = 100 - (percent || 0)
      return this.getCompetitivenessTextStyle(score)
    },

    getCompetitivenessTextStyle(score) {
      return {
        color: this.getCompetitivenessColor(score)
      }
    },


    // ===== RocketSettingsPanel 스타일 참고: Bar 드래그 핸들러 =====
    startDragSuggest(type, evt) {
      try {
        const barEl = evt.currentTarget.parentElement // .pos-bar
        const rect = barEl.getBoundingClientRect()
        const scope = (type === 'full') ? 'fullRange' : 'nearbyRange'
        const prices = (this.collectPriceList(scope) || [])
          .filter(p => Number.isFinite(p))
          .sort((a, b) => a - b)
        const data = (type === 'full') ? this.barFull : this.barNear
        this.dragging = { active: true, type, rect, prices, min: data.min, max: data.max }
        window.addEventListener('mousemove', this.onDrag)
        window.addEventListener('mouseup', this.endDrag)
        this.onDrag(evt)
      } catch (e) {
        console.error('❌ startDragSuggest 오류:', e)
      }
    },
    onDrag(evt) {
      try {
        const d = this.dragging
        if (!d || !d.active) return
        const x = Math.min(Math.max(evt.clientX - d.rect.left, 0), d.rect.width)
        const pct = d.rect.width > 0 ? (x / d.rect.width) : 0
        const N = (d.prices && d.prices.length) ? d.prices.length : 0
        if (!N) return
        const idx = Math.max(0, Math.min(N - 1, Math.round(pct * (N - 1))))
        const value = Math.round(d.prices[idx])
        if (d.type === 'full') this.manualSuggestion.full = value
        else this.manualSuggestion.near = value
      } catch (e) {
        console.error('❌ onDrag 오류:', e)
      }
    },
    
    // 가격값의 순위 백분위(1~100) 계산: 주어진 rangeType의 가격 리스트에서 price의 위치
    getPricePercentileInRange(rangeType, price) {
      try {
        const prices = this.collectPriceList(rangeType)
        const list = (prices || []).filter(p => Number.isFinite(p)).sort((a, b) => a - b)
        const N = list.length
        if (!N || !Number.isFinite(price)) return null
        let idx = list.findIndex(p => p >= price)
        if (idx < 0) idx = N - 1
        const percent = Math.max(1, Math.min(100, Math.round(((idx + 1) / N) * 100)))
        return percent
      } catch (e) {
        console.warn('⚠️ [getPricePercentileInRange] 실패:', e)
        return null
      }
    },
    endDrag() {
      try {
        if (!this.dragging || !this.dragging.active) return
        window.removeEventListener('mousemove', this.onDrag)
        window.removeEventListener('mouseup', this.endDrag)
        this.dragging.active = false
      } catch (e) {
        console.error('❌ endDrag 오류:', e)
      }
    },

    // 가격 제안 계산
    calculatePriceSuggestions() {
      console.log('🚀 [가격 제안 계산] 버튼 클릭됨')
      console.log('🔍 [가격 제안 계산] competitivenessResults.length:', this.competitivenessResults.length)
      console.log('🔍 [가격 제안 계산] targetCompetitivenessPercent:', this.targetCompetitivenessPercent)

      if (this.competitivenessResults.length === 0) {
        console.log('❌ [가격 제안 계산] 분석 결과 없음')
        this.$toast.warning('분석 결과가 없습니다. 먼저 경쟁력 분석을 실행해주세요.')
        return
      }

      const targetPercent = this.targetCompetitivenessPercent
      if (!targetPercent || targetPercent < 1 || targetPercent > 100) {
        console.log('❌ [가격 제안 계산] 목표 경쟁력 % 오류:', targetPercent)
        this.$toast.warning('목표 경쟁력 순위는 1~100% 사이의 값을 입력해주세요.')
        return
      }

      console.log('✅ [가격 제안 계산] 유효성 검사 통과, 계산 시작...')

      // 🔍 가격 경쟁력 분석 Raw Data 콘솔 출력
      console.log('🔍 ===== 가격 경쟁력 분석 Raw Data =====')
      console.log('📊 목표 경쟁력 순위:', targetPercent + '%')
      console.log('📊 분석 대상 키워드 수:', this.competitivenessResults.length)
      console.log('📊 전체 Raw Data:', JSON.stringify(this.competitivenessResults, null, 2))

      // 키워드별 상세 분석 데이터
      this.competitivenessResults.forEach((result, index) => {
        console.log(`\n🔍 [키워드 ${index + 1}/${this.competitivenessResults.length}] "${result.keyword}" 상세 분석:`)
        console.log('  📍 검색순위:', result.searchRank + '위')
        console.log('  💰 현재가격:', result.price + '원')
        console.log('  📊 전체범위:', {
          myPriceRank: result.fullRange?.myPriceRank,
          totalProducts: result.fullRange?.totalProducts,
          competitivenessScore: result.fullRange?.competitivenessScore,
          avgPrice: result.fullRange?.avgPrice
        })
        console.log('  📊 근접범위:', {
          myPriceRank: result.nearbyRange?.myPriceRank,
          totalProducts: result.nearbyRange?.totalProducts,
          competitivenessScore: result.nearbyRange?.competitivenessScore,
          avgPrice: result.nearbyRange?.avgPrice
        })

        // 해당 키워드의 검색 결과 데이터도 출력
        const searchResults = this.keywordSearchResults[result.keyword]
        if (searchResults && searchResults.products) {
          console.log('  🔍 검색결과 상품수:', searchResults.products.length + '개')
          console.log('  💰 검색결과 가격분포:', searchResults.products
            .filter(p => p.price && !isNaN(p.price))
            .map(p => p.price)
            .sort((a, b) => a - b)
          )
        }
      })
      console.log('🔍 ===== Raw Data 출력 완료 =====\n')

      this.priceSuggestions = []

      this.competitivenessResults.forEach((result, index) => {
        const keyword = result.keyword
        const currentPrice = result.price // myPrice → price로 수정

        console.log(`\n🔍 [가격 제안 ${index + 1}/${this.competitivenessResults.length}] "${keyword}" 처리 시작:`)
        console.log(`  - currentPrice: ${currentPrice}`)
        console.log(`  - result.searchRank: ${result.searchRank}`)
        console.log(`  - result.fullRange: ${result.fullRange ? 'O' : 'X'}`)
        console.log(`  - result.nearbyRange: ${result.nearbyRange ? 'O' : 'X'}`)

        // 내 상품이 없는 키워드는 가격 제안 불가 (가격이 있으면 내 상품이 있다고 판단)
        if (!currentPrice || currentPrice <= 0) {
          console.log(`⚠️ [가격 제안] "${keyword}": 내 상품 없음 (currentPrice: ${currentPrice}) - 건너뜀`)
          return
        }

        // 🔧 keywordSearchResults 없이도 competitivenessResults만으로 가격 제안 계산
        console.log(`✅ [가격 제안] "${keyword}": 조건 통과, 가격 제안 계산 시작...`)

        // 🔧 competitivenessResults의 데이터만으로 가격 제안 계산
        // fullRange 데이터에서 가격 제안 계산
        const fullRangeSuggestion = this.calculatePriceSuggestionFromCompetitivenessData(
          result.fullRange,
          targetPercent,
          currentPrice
        )

        // 근접범위 가격 제안 (근접범위가 있는 경우만)
        let nearbyRangeSuggestion = null
        if (result.nearbyRange && result.nearbyRange.totalProducts > 0) {
          nearbyRangeSuggestion = this.calculatePriceSuggestionFromCompetitivenessData(
            result.nearbyRange,
            targetPercent,
            currentPrice
          )
        }

        this.priceSuggestions.push({
          keyword: keyword,
          currentPrice: currentPrice,
          fullRangeSuggestedPrice: fullRangeSuggestion.suggestedPrice,
          fullRangeTargetRank: fullRangeSuggestion.targetRank,
          fullRangePriceChange: fullRangeSuggestion.suggestedPrice - currentPrice,
          nearbyRangeSuggestedPrice: nearbyRangeSuggestion ? nearbyRangeSuggestion.suggestedPrice : null,
          nearbyRangeTargetRank: nearbyRangeSuggestion ? nearbyRangeSuggestion.targetRank : null,
          nearbyRangePriceChange: nearbyRangeSuggestion ? nearbyRangeSuggestion.suggestedPrice - currentPrice : null
        })
      })

      console.log('💡 [가격 제안] 계산 완료:', this.priceSuggestions.length, '개 키워드')
      try {
        const summary = this.priceSuggestions.slice(0, 10).map(s => ({
          keyword: s.keyword,
          current: s.currentPrice,
          full: s.fullRangeSuggestedPrice,
          near: s.nearbyRangeSuggestedPrice
        }))
        console.log('📦 [가격 제안 요약](상위 10):', JSON.stringify(summary))
      } catch (e) {
        console.warn('⚠️ [가격 제안 요약 로깅 오류]', e?.message || e)
      }
    },

    // 🔧 competitivenessResults 데이터만으로 가격 제안 계산 (keywordSearchResults 불필요)
    calculatePriceSuggestionFromCompetitivenessData(rangeData, targetPercent, currentPrice) {
      // 목표 순위 계산 (30% = 30순위)
      const targetRank = Math.ceil(rangeData.totalProducts * targetPercent / 100)

      // 현재 가격 순위와 목표 순위 비교
      const currentPriceRank = rangeData.myPriceRank
      const avgPrice = rangeData.avgPrice

      let suggestedPrice = currentPrice

      // 목표 순위보다 현재 순위가 낮으면 (순위가 높은 숫자면) 가격을 낮춰야 함
      if (currentPriceRank > targetRank) {
        // 평균 가격보다 낮게 설정하되, 너무 극단적이지 않게 조정
        const priceReduction = (currentPrice - avgPrice) * 0.3 // 30% 정도 조정
        suggestedPrice = Math.max(currentPrice - priceReduction, avgPrice * 0.8)
      }
      // 목표 순위보다 현재 순위가 높으면 (순위가 낮은 숫자면) 가격을 올릴 수 있음
      else if (currentPriceRank < targetRank) {
        // 평균 가격에 가깝게 올리되, 너무 극단적이지 않게 조정
        const priceIncrease = (avgPrice - currentPrice) * 0.2 // 20% 정도 조정
        suggestedPrice = Math.min(currentPrice + priceIncrease, avgPrice * 1.2)
      }

      // 최소 가격 보장 (현재 가격의 50% 이상)
      suggestedPrice = Math.max(suggestedPrice, currentPrice * 0.5)

      // 정수로 반올림
      suggestedPrice = Math.round(suggestedPrice)

      console.log(`💡 [가격 제안 계산] 목표순위: ${targetRank}위, 현재순위: ${currentPriceRank}위, 현재가격: ${currentPrice}원 → 제안가격: ${suggestedPrice}원`)

      return {
        suggestedPrice: suggestedPrice,
        targetRank: targetRank
      }
    },

    // 특정 범위에서 목표 순위에 맞는 가격 제안 계산
    calculatePriceSuggestionForRange(products, totalProducts, targetPercent, currentPrice) {
      // 목표 순위 계산 (30% = 30순위)
      const targetRank = Math.ceil(totalProducts * targetPercent / 100)

      // 가격 순으로 정렬
      const sortedByPrice = products
        .filter(p => p.price && !isNaN(p.price))
        .sort((a, b) => a.price - b.price)

      if (sortedByPrice.length === 0) {
        return {
          suggestedPrice: currentPrice,
          targetRank: 1
        }
      }

      // 목표 순위에 해당하는 가격 찾기
      const targetIndex = Math.min(targetRank - 1, sortedByPrice.length - 1)
      const targetPrice = sortedByPrice[targetIndex].price

      // 100원 단위로 반올림
      const suggestedPrice = Math.round(targetPrice / 100) * 100

      return {
        suggestedPrice: suggestedPrice,
        targetRank: targetRank
      }
    },

    // 🔧 범위 데이터 통합 (전체범위 또는 근접범위)
    aggregateRangeData(rangeType) {
      const validResults = this.competitivenessResults.filter(result =>
        result[rangeType] && result[rangeType].totalProducts > 0
      )

      if (validResults.length === 0) {
        return { totalProducts: 0, avgPrice: 0, myPriceRank: 0 }
      }

      // 가중평균 계산 (상품 수가 많은 키워드에 더 큰 가중치)
      let totalWeightedAvgPrice = 0
      let totalWeightedPriceRank = 0
      let totalWeight = 0
      let totalProducts = 0

      validResults.forEach(result => {
        const rangeData = result[rangeType]
        const weight = rangeData.totalProducts

        totalWeightedAvgPrice += rangeData.avgPrice * weight
        totalWeightedPriceRank += rangeData.myPriceRank * weight
        totalWeight += weight
        totalProducts += rangeData.totalProducts
      })

      return {
        totalProducts: Math.round(totalProducts / validResults.length), // 평균 상품 수
        avgPrice: Math.round(totalWeightedAvgPrice / totalWeight), // 가중평균 가격
        myPriceRank: Math.round(totalWeightedPriceRank / totalWeight) // 가중평균 순위
      }
    },
    // 📦 범위별 가격 리스트 수집 후 정렬 병합
    collectPriceList(rangeType) {
      const all = []
      try {
        (this.competitivenessResults || []).forEach(r => {
          const rd = r && r[rangeType]
          if (rd && Array.isArray(rd.priceList) && rd.priceList.length) {
            all.push(...rd.priceList)
          }
        })
      } catch (e) {
        console.warn('⚠️ [collectPriceList] 실패:', e)
      }
      return all.sort((a, b) => (a || 0) - (b || 0))
    },

    // 🎯 백분위 기반 제안가: 1%→index 0(1등), 100%→index N-1(꼴찌)
    percentilePriceSuggest(prices, percent) {
      const list = (prices || []).filter(p => Number.isFinite(p)).sort((a, b) => a - b)
      const N = list.length
      if (!N) return null
      const idx = Math.max(0, Math.ceil((percent / 100) * N) - 1)
      const base = list[Math.min(idx, N - 1)]
      // 경쟁사보다 소폭 낮춤(10원). 하한 10원 보호
      const suggested = Math.max(10, base - 10)
      console.log('📐 [백분위 제안] N:', N, 'percent:', percent, 'idx:', idx, 'base:', base, 'suggested:', suggested)
      return suggested
    },


    // 🔧 통합된 데이터로 가격 제안 계산
    calculateIntegratedPrice(aggregatedData, currentPrice) {
      if (aggregatedData.totalProducts === 0) return null

      const targetRank = Math.ceil(aggregatedData.totalProducts * this.targetCompetitivenessPercent / 100)
      const currentPriceRank = aggregatedData.myPriceRank
      const avgPrice = aggregatedData.avgPrice

      // 목표-현재 순위 차이를 이용해 조정 강도를 연속적으로 반영
      const rankGap = currentPriceRank - targetRank // >0 이면 상향(순위 올리기) 필요
      const intensity = Math.min(Math.abs(rankGap) / Math.max(1, aggregatedData.totalProducts), 1)

      let suggestedPrice = currentPrice

      if (rankGap > 0) {
        // 순위를 올리기 위해 가격을 낮추는 방향
        const baseTarget = Math.min(avgPrice * 0.9, currentPrice * 0.95)
        const delta = Math.max((currentPrice - baseTarget) * (0.3 + 0.7 * intensity), currentPrice * 0.01)
        suggestedPrice = Math.max(currentPrice - delta, Math.max(avgPrice * 0.7, currentPrice * 0.5))
      } else if (rankGap < 0) {
        // 순위를 낮춰도 되는 상황(경쟁력 완화) → 가격을 올리는 방향
        const baseTarget = Math.max(avgPrice * 1.1, currentPrice * 1.05)
        const delta = Math.max((baseTarget - currentPrice) * (0.3 + 0.7 * intensity), currentPrice * 0.01)
        suggestedPrice = Math.min(currentPrice + delta, Math.min(avgPrice * 1.5, currentPrice * 1.5))
      } else {
        suggestedPrice = currentPrice
      }

      // 가격을 10원 단위로 반올림
      const roundUnit = 10
      suggestedPrice = Math.round(suggestedPrice / roundUnit) * roundUnit

      return suggestedPrice
    },

    // 🔎 통합 가격 제안 상세 로깅
    logIntegratedSuggestionDetails(contextLabel) {
      try {
        if (!this.competitivenessResults || this.competitivenessResults.length === 0) {
          console.log('ℹ️ [가격제안][%s] 경쟁력 결과 없음', contextLabel)
          return
        }
        const currentPrice = this.competitivenessResults[0]?.price || 0
        const tp = this.targetCompetitivenessPercent

        const fullAgg = this.aggregateRangeData('fullRange')
        const nearAgg = this.aggregateRangeData('nearbyRange')

        const summary = {
          context: contextLabel,
          targetPercent: tp,
          currentPrice,
          fullRange: {
            totalProducts: fullAgg.totalProducts || 0,
            avgPrice: Math.round(fullAgg.avgPrice || 0),
            myPriceRank: fullAgg.myPriceRank || null,
            targetRank: fullAgg.totalProducts ? Math.ceil(fullAgg.totalProducts * tp / 100) : null,
            suggestedPrice: this.calculateIntegratedPrice(fullAgg, currentPrice)
          },
          nearbyRange: {
            totalProducts: nearAgg.totalProducts || 0,
            avgPrice: Math.round(nearAgg.avgPrice || 0),
            myPriceRank: nearAgg.myPriceRank || null,
            targetRank: nearAgg.totalProducts ? Math.ceil(nearAgg.totalProducts * tp / 100) : null,
            suggestedPrice: this.calculateIntegratedPrice(nearAgg, currentPrice)
          }
        }
        console.log('🔎 [가격제안 상세]', JSON.stringify(summary))
      } catch (e) {
        console.warn('⚠️ [가격제안 로깅 오류]', e?.message || e)
      }
    },

    // 🔧 목표 경쟁력 % 변경 시 호출 (실시간 업데이트)
    onTargetPercentChange() {
      console.log('🔄 [목표 경쟁력 변경]', this.targetCompetitivenessPercent + '%')
      this.logIntegratedSuggestionDetails('onTargetChange')
    },

    // 전체범위만 분석 (내 상품이 없는 경우)
    performFullRangeAnalysis(keyword, products) {
      if (!products || products.length === 0) {
        return null
      }

      // 가격 데이터 추출
      const prices = products
        .filter(p => p.price && !isNaN(p.price))
        .map(p => parseInt(p.price))

      if (prices.length === 0) {
        return null
      }

      // 가격 통계 계산
      const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length
      const minPrice = Math.min(...prices)
      const maxPrice = Math.max(...prices)

      // 내 상품 가격 정보 가져오기 (competitivenessResults의 첫 번째 항목에서)
      let myPrice = null
      if (this.competitivenessResults.length > 0) {
        myPrice = this.competitivenessResults[0].price
      }

      // 내 상품 가격이 있으면 순위와 경쟁력 계산
      let myPriceRank = null
      let competitivenessScore = null
      let priceList = [...prices].sort((a, b) => a - b)

      if (myPrice && !isNaN(myPrice)) {
        // 내 가격보다 저렴한 상품 수 + 1 = 내 순위
        const cheaperCount = prices.filter(price => price < myPrice).length
        myPriceRank = cheaperCount + 1

        // 경쟁력 점수 계산 (1-100점)
        competitivenessScore = 50
        if (avgPrice > 0) {
          if (myPrice < avgPrice) {
            competitivenessScore = Math.min(100, 50 + ((avgPrice - myPrice) / avgPrice) * 50)
          } else {
            competitivenessScore = Math.max(0, 50 - ((myPrice - avgPrice) / avgPrice) * 50)
          }
        }
        competitivenessScore = Math.round(competitivenessScore)

        console.log(`💰 [전체범위 가격분석] "${keyword}": 내가격 ${myPrice}원, 순위 ${myPriceRank}/${products.length}위, 경쟁력 ${competitivenessScore}점`)
      }

      return {
        keyword: keyword,
        totalProducts: products.length,
        avgPrice: avgPrice,
        minPrice: minPrice,
        maxPrice: maxPrice,
        myPriceRank: myPriceRank,
        competitivenessScore: competitivenessScore,
        priceList: priceList,
        hasMyProduct: false
      }
    },

    // 내 상품이 없는 경우 전체범위만 저장
    savePriceAnalysisResultWithoutNearby(keyword, fullRangeAnalysis) {
      console.log(`💰 [전체범위만 분석] "${keyword}" - 총 ${fullRangeAnalysis.totalProducts}개 상품`)
      console.log(`📊 [가격 정보] 평균: ${Math.round(fullRangeAnalysis.avgPrice)}원, 최저: ${fullRangeAnalysis.minPrice}원, 최고: ${fullRangeAnalysis.maxPrice}원`)

      // 내 상품 가격 정보 가져오기
      let myPrice = null
      if (this.competitivenessResults.length > 0) {
        myPrice = this.competitivenessResults[0].price
      }

      // 경쟁력 분석 결과에 추가
      this.competitivenessResults.push({
        keyword: keyword,
        price: myPrice, // 내 상품 가격 (검색결과에는 없지만 알고 있는 가격)
        searchRank: null, // 검색순위는 없음 (검색결과에 없으므로)
        fullRange: {
          totalProducts: fullRangeAnalysis.totalProducts,
          avgPrice: fullRangeAnalysis.avgPrice,
          minPrice: fullRangeAnalysis.minPrice,
          maxPrice: fullRangeAnalysis.maxPrice,
          myPriceRank: fullRangeAnalysis.myPriceRank, // 계산된 가격순위
          competitivenessScore: fullRangeAnalysis.competitivenessScore, // 계산된 경쟁력
          priceList: fullRangeAnalysis.priceList // 가격 리스트
        },
        nearbyRange: null, // 근접범위 없음 (검색결과에 없으므로)
        hasMyProduct: false
      })

      if (fullRangeAnalysis.myPriceRank) {
        console.log(`📊 [전체범위 순위] "${keyword}": ${fullRangeAnalysis.myPriceRank}/${fullRangeAnalysis.totalProducts}위, 경쟁력 ${fullRangeAnalysis.competitivenessScore}점`)
      }
    },



    getOverallCompetitivenessScore() {
      const enabledResults = this.enabledCompetitivenessResults
      if (enabledResults.length === 0) return '-'

      const sum = enabledResults.reduce((acc, result) => acc + result.overallScore, 0)
      return Math.round(sum / enabledResults.length)
    },

    getCompetitivenessLevel() {
      const score = this.getOverallCompetitivenessScore()
      if (score === '-') return '데이터 없음'

      if (score >= 16) return '매우 우수'
      if (score >= 12) return '우수'
      if (score >= 8) return '보통'
      if (score >= 4) return '개선 필요'
      return '경쟁력 부족'
    },

    getCompetitivenessClass() {
      const score = this.getOverallCompetitivenessScore()
      if (score === '-') return 'text-muted'

      if (score >= 16) return 'text-success'
      if (score >= 12) return 'text-info'
      if (score >= 8) return 'text-warning'
      if (score >= 4) return 'text-danger'
      return 'text-danger'
    },

    getRankBadgeClass(rank) {
      if (rank <= 3) return 'bg-success'
      if (rank <= 5) return 'bg-info'
      if (rank <= 8) return 'bg-warning'
      return 'bg-danger'
    },

    getScoreClass(score) {
      if (score >= 16) return 'text-success'
      if (score >= 12) return 'text-info'
      if (score >= 8) return 'text-warning'
      if (score >= 4) return 'text-danger'
      return 'text-danger'
    },

    updateCompetitivenessDisplay() {
      // 키워드별 ON/OFF 토글 시 호출되는 메서드
      console.log('🔄 [경쟁력 표시 업데이트] 활성화된 키워드:', this.enabledCompetitivenessResults.length, '개')
    },

    // 새로운 경쟁력 순위 계산 메서드들 (단순 순위 반환)
    calculateRPIRanking(products) {
      const productsWithRPI = products.map(p => ({
        ...p,
        rpi: this.calculateRPI(p)
      })).filter(p => p.rpi !== null && p.rpi > 0)

      // RPI 기준 내림차순 정렬 (높을수록 좋음)
      productsWithRPI.sort((a, b) => b.rpi - a.rpi)

      const myRank = productsWithRPI.findIndex(p => p.isMyProduct) + 1
      return myRank > 0 ? myRank : productsWithRPI.length
    },

    calculateAdRanking(products) {
      const productsWithAd = products.map(p => ({
        ...p,
        adScore: this.calculateAdScore(p)
      })).filter(p => p.adScore !== null && p.adScore > 0)

      // 광고 점수 기준 내림차순 정렬 (높을수록 좋음)
      productsWithAd.sort((a, b) => b.adScore - a.adScore)

      const myRank = productsWithAd.findIndex(p => p.isMyProduct) + 1
      return myRank > 0 ? myRank : productsWithAd.length
    },

    calculateCTRRanking(products) {
      const productsWithCTR = products.map(p => ({
        ...p,
        ctr: this.calculateCTR(p)
      })).filter(p => p.ctr !== null && p.ctr > 0)

      // CTR 기준 내림차순 정렬 (높을수록 좋음)
      productsWithCTR.sort((a, b) => b.ctr - a.ctr)

      const myRank = productsWithCTR.findIndex(p => p.isMyProduct) + 1
      return myRank > 0 ? myRank : productsWithCTR.length
    },

    calculateCVRRanking(products) {
      const productsWithCVR = products.map(p => ({
        ...p,
        cvr: this.calculateCVR(p)
      })).filter(p => p.cvr !== null && p.cvr > 0)

      // CVR 기준 내림차순 정렬 (높을수록 좋음)
      productsWithCVR.sort((a, b) => b.cvr - a.cvr)

      const myRank = productsWithCVR.findIndex(p => p.isMyProduct) + 1
      return myRank > 0 ? myRank : productsWithCVR.length
    },











    // 상품그룹 전환
    async switchProductGroup(groupName) {
      if (this.isAnalyzing) {
        alert('분석 중에는 상품그룹을 변경할 수 없습니다.')
        return
      }

      if (groupName === this.activeProductGroup) return

      console.log(`🔄 [상품그룹 전환] ${this.activeProductGroup} → ${groupName}`)

      // 현재 데이터 저장
      await this.saveToIndexedDB()

      // 새 그룹으로 전환
      this.activeProductGroup = groupName

      // 데이터 초기화
      this.productList = []
      this.competitivenessResults = []
      this.targetProductLink = ''
      this.myVendorItemId = null
      this.targetCompetitivenessPercent = 30 // 목표 경쟁력 % 초기화

      // 새 그룹 데이터 로드
      await this.loadFromIndexedDB()

      console.log(`✅ [상품그룹 전환] "${groupName}" 로드 완료`)
    },

    // IndexedDB에서 데이터 로드
    async loadFromIndexedDB() {
      try {
        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'

        // IndexedDB 열기
        const request = indexedDB.open(scopedDbName(dbName), 1)

        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'groupName' })
          }
        }

        const db = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })

        // 데이터 조회
        const transaction = db.transaction([storeName], 'readonly')
        const store = transaction.objectStore(storeName)
        const getRequest = store.get(this.activeProductGroup)

        const groupData = await new Promise((resolve, reject) => {
          getRequest.onsuccess = () => resolve(getRequest.result)
          getRequest.onerror = () => reject(getRequest.error)
        })

        if (groupData) {
          // 데이터 복원
          this.productList = groupData.productList || []
          this.competitivenessResults = groupData.competitivenessResults || []
          this.keywordSearchResults = groupData.keywordSearchResults || {} // 🔧 키워드 검색 결과 복원 추가
          this.compareRange = groupData.compareRange || 5
          this.myVendorItemId = groupData.myVendorItemId || null
          this.targetCompetitivenessPercent = groupData.targetCompetitivenessPercent || 30 // 목표 경쟁력 % 복원
          // 그룹 설정 복원(격리 저장)
          if (groupData.autoList) this.autoList = groupData.autoList
          if (groupData.maxProducts) this.maxProducts = groupData.maxProducts

          console.log(`✅ [데이터 로드] "${this.activeProductGroup}": 키워드 ${this.productList.length}개, 분석결과 ${this.competitivenessResults.length}개, 검색결과 ${Object.keys(this.keywordSearchResults).length}개, 목표경쟁력 ${this.targetCompetitivenessPercent}%`)

          // 🔍 저장된 데이터 구조 상세 확인
          console.log('🔍 [데이터 구조] competitivenessResults 샘플:', this.competitivenessResults.slice(0, 2))
          console.log('🔍 [데이터 구조] keywordSearchResults 키목록:', Object.keys(this.keywordSearchResults))
          console.log('🔍 [데이터 구조] 전체 groupData 키목록:', Object.keys(groupData))
          console.log('🔍 [데이터 구조] groupData.keywordSearchResults 존재여부:', !!groupData.keywordSearchResults)
          if (groupData.keywordSearchResults) {
            console.log('🔍 [데이터 구조] groupData.keywordSearchResults 키목록:', Object.keys(groupData.keywordSearchResults))
          }
          console.log('🔍 [데이터 구조] 전체 groupData:', groupData)

          // 🔍 keywordSearchResults 상품 데이터 상세 로깅
          console.log('\n🔍 ===== keywordSearchResults 상품 데이터 상세 분석 =====')
          Object.keys(this.keywordSearchResults).forEach((keyword, index) => {
            const searchResult = this.keywordSearchResults[keyword]
            console.log(`\n🔍 [키워드 ${index + 1}] "${keyword}":`)
            console.log('  - searchResult 타입:', typeof searchResult)
            console.log('  - searchResult 키목록:', Object.keys(searchResult || {}))

            if (searchResult && searchResult.products) {
              console.log('  - products 배열 길이:', searchResult.products.length)
              console.log('  - products 첫 번째 상품 샘플:', searchResult.products[0])
              console.log('  - products 첫 번째 상품 키목록:', Object.keys(searchResult.products[0] || {}))

              // 처음 3개 상품의 주요 필드만 로깅
              console.log('  - 처음 3개 상품 요약:')
              searchResult.products.slice(0, 3).forEach((product, pIndex) => {
                console.log(`    [${pIndex + 1}] vendorItemId: ${product.vendorItemId}, productName: ${product.productName || product.name}, price: ${product.price}, imageUrl: ${product.imageUrl || product.image}`)
              })
            } else if (Array.isArray(searchResult)) {
              console.log('  - searchResult는 배열 형태, 길이:', searchResult.length)
              if (searchResult.length > 0) {
                console.log('  - 첫 번째 요소 샘플:', searchResult[0])
                console.log('  - 첫 번째 요소 키목록:', Object.keys(searchResult[0] || {}))
              }
            } else {
              console.log('  - searchResult 전체 데이터:', searchResult)
            }
          })
          console.log('🔍 ===== keywordSearchResults 분석 완료 =====\n')

          // 🔎 통합 가격 제안 요약 로깅
          this.logIntegratedSuggestionDetails('afterLoad')

          // 🔄 원본 데이터가 있으면 최신 로직으로 다시 계산
          if (Object.keys(this.keywordSearchResults).length > 0 && this.productList.length > 0) {
            console.log('🔄 [데이터 재계산] 원본 데이터로 최신 로직 적용 시작...')
            await this.recalculateCompetitivenessResults()
          }

          this.competitivenessResults.forEach((result, index) => {
            console.log(`🔍 [키워드 ${index + 1}] "${result.keyword}":`)
            console.log(`  - searchRank: ${result.searchRank}`)
            console.log(`  - price: ${result.price}`)
            console.log(`  - hasMyProduct: ${result.hasMyProduct}`)
            console.log(`  - myPrice: ${result.myPrice}`)
            console.log(`  - fullRange: ${result.fullRange ? 'O' : 'X'}`)
            console.log(`  - nearbyRange: ${result.nearbyRange ? 'O' : 'X'}`)


            // keywordSearchResults에서 해당 키워드 확인
            const searchResult = this.keywordSearchResults[result.keyword]
            console.log(`  - keywordSearchResults["${result.keyword}"]: ${searchResult ? `${searchResult.products?.length}개 상품` : 'X'}`)
          })
        } else {
          console.log(`ℹ️ [데이터 로드] "${this.activeProductGroup}": 새 그룹`)
        }

        db.close()
      } catch (error) {
        console.error('❌ [데이터 로드] 실패:', error)
      }
    },

    // 원본 데이터를 기반으로 경쟁력 분석 결과 재계산
    async recalculateCompetitivenessResults() {
      try {
        console.log('🔄 [재계산] 시작 - keywordSearchResults:', Object.keys(this.keywordSearchResults).length, '개')

        // 기존 분석 결과 초기화
        this.competitivenessResults = []

        // 각 키워드별로 다시 분석
        for (const productKeyword of this.productList) {
          const keyword = productKeyword.keyword
          const searchResults = this.keywordSearchResults[keyword]

          if (!searchResults || !Array.isArray(searchResults) || searchResults.length === 0) {
            console.log(`⚠️ [재계산] "${keyword}": 검색 결과 없음`)
            continue
          }

          console.log(`🔄 [재계산] "${keyword}": ${searchResults.length}개 상품으로 분석 시작`)

          // 내 상품 찾기
          const myProductIndex = this.findMyProductInResults(searchResults)

          if (myProductIndex === -1) {
            // 내 상품이 없는 경우 - 전체범위만 분석
            console.log(`⚠️ [재계산] "${keyword}": 내 상품 없음 - 전체범위만 분석`)
            const fullRangeAnalysis = this.performFullRangeAnalysis(keyword, searchResults)
            if (fullRangeAnalysis) {
              this.savePriceAnalysisResultWithoutNearby(keyword, fullRangeAnalysis)
            }
          } else {
            // 내 상품이 있는 경우 - 전체범위 + 근접범위 분석
            console.log(`✅ [재계산] "${keyword}": 내 상품 ${myProductIndex + 1}위 발견`)

            // 비교 대상 상품 선정 (기존 로직과 동일)
            const competitorProducts = this.selectCompetitorProducts(searchResults, myProductIndex)
            console.log(`🎯 [재계산 비교 대상] ${competitorProducts.length}개 상품 선정`)

            // 가격 경쟁력 분석
            const priceAnalysis = await this.performPriceCompetitivenessAnalysis(keyword, competitorProducts, myProductIndex)
            if (priceAnalysis) {
              this.savePriceAnalysisResult(keyword, priceAnalysis)
            } else {
              console.warn(`⚠️ [재계산] "${keyword}" 가격 분석 실패`)
            }
          }
        }

        console.log(`✅ [재계산] 완료 - ${this.competitivenessResults.length}개 결과 생성`)

        // 재계산 후 저장
        await this.saveToIndexedDB()

      } catch (error) {
        console.error('❌ [재계산] 실패:', error)
      }
    },

    // 새 상품그룹 생성
    createProductGroup() {
      const groupName = prompt('새 상품그룹 이름을 입력하세요:')
      if (!groupName || !groupName.trim()) return

      const trimmedName = groupName.trim()
      if (this.productGroups.includes(trimmedName)) {
        alert('이미 존재하는 상품그룹 이름입니다.')
        return
      }

      this.productGroups.push(trimmedName)
      this.switchProductGroup(trimmedName)
    },

    // 상품그룹 삭제
    async deleteProductGroup(groupName) {
      if (groupName === 'Default') {
        alert('기본 그룹은 삭제할 수 없습니다.')
        return
      }

      if (!confirm(`"${groupName}" 상품그룹을 삭제하시겠습니까?\n저장된 모든 데이터가 삭제됩니다.`)) {
        return
      }

      try {
        // IndexedDB에서 삭제
        const dbName = 'productCompetitiveAnalysis'
        const storeName = 'analysisResults'

        const request = indexedDB.open(scopedDbName(dbName), 1)
        const db = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })

        const transaction = db.transaction([storeName], 'readwrite')
        const store = transaction.objectStore(storeName)
        await store.delete(groupName)

        db.close()

        // 그룹 목록에서 제거
        this.productGroups = this.productGroups.filter(g => g !== groupName)

        // 현재 활성 그룹이 삭제된 그룹이면 기본 그룹으로 전환
        if (this.activeProductGroup === groupName) {
          this.switchProductGroup('Default')
        }

        console.log(`✅ [상품그룹 삭제] "${groupName}" 삭제 완료`)
      } catch (error) {
        console.error('❌ [상품그룹 삭제] 실패:', error)
        alert('상품그룹 삭제 중 오류가 발생했습니다.')
      }
    },





  }
}
</script>

<style scoped>
.progress {
  background-color: #e9ecef;
}

.progress-bar {
  background-color: #007bff;
  transition: width 0.3s ease;
}

.badge {
  font-size: 0.75em;
}

.table-responsive {
  border-radius: 0.375rem;
}

/* 목표 백분위 위치 Bar 스타일 */
/* RocketSettingsPanel.vue progress 스타일 참고 */
.pos-bar{position:relative;width:100%;height:30px;overflow:visible}
.pos-bar__track{position:absolute;inset:0;background:#e9ecef;border-radius:5px}
.pos-bar--empty .pos-bar__track{background:#f1f5f9}
.pos-bar__mark{position:absolute;top:-2px;width:8px;height:34px;border-radius:4px;transform:translateX(-50%);box-shadow:0 0 0 1px rgba(0,0,0,0.05)}
.pos-bar__mark--my{background:#3b82f6}
.pos-bar__mark--sug{background:#f59e0b;cursor:ew-resize}
.pos-bar__mark--tgt{background:#10b981}
.pos-bar__label{position:absolute;transform:translateX(-50%);font-size:12px;white-space:nowrap;color:#333;background:#fff;padding:2px 6px;border:1px solid #e5e7eb;border-radius:4px}
.pos-bar__label--sug{top:-26px}
.pos-bar__label--my{top:36px}


/* 바 그룹 간 여백 보강: 절대배치 라벨이 겹치지 않도록 */
/* .pos-bar-block{ margin-bottom:1px !important; } */

</style>